import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Select,
  Menu,
  MenuItem,
  Divider,
  Checkbox,
  FormControlLabel,
  Avatar,
  Fade,
  Slide,
  Grow,
  Zoom,
  keyframes,
  CircularProgress,
  Slider,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Pagination,
} from "@mui/material";
import api, { reportsAPI, callAPI, messagesAPI } from '../services/api';
import { useCallRecording } from '../contexts/CallRecordingContext';
import { useAgentStatus } from '../contexts/AgentStatusContext';
import WaveSurfer from 'wavesurfer.js';
import customerService from '../services/customerService';
import authService from '../services/authService';
import CallReports from '../components/reports/CallReports';
import whatsappTemplateService, { WhatsappTemplate } from '../services/whatsappTemplateService';
import emailTemplateService, { EmailTemplate } from '../services/emailTemplateService';
import emailAccountService, { EmailAccount } from '../services/emailAccountService';
import freePBXWebRTCService from '../services/freePBXWebRTCService';
import { API_URL, SOCKET_URL } from '../config';

import AgentReports from '../components/reports/AgentReports';
import QueueReports from '../components/reports/QueueReports';
import StatusReports from '../components/reports/StatusReports';
import CampaignDashboard from './CampaignDashboard';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import {
  IconUsers,
  IconHeadset,
  IconPercentage,
  IconDotsVertical,
  IconX,
  IconPhone,
  IconClock,
  IconTarget,
  IconTrendingUp,
  IconCircle,
  IconAlertCircle,
  IconPlayerPlay,
  IconDownload,
  IconUpload,
  IconPlus,
  IconArrowLeft,
  IconQuestionMark,
  IconPlayerPause,
  IconPlayerStop,
  IconVolume,
  IconHelp,
  IconSend,
  IconSpeakerphone,
  IconFileAnalytics,
  IconUser,
  IconBrandWhatsapp,
  IconPencil,
  IconMail,
  IconServer,
  IconWifi,
  IconChevronDown,
  IconChevronUp,
  IconTemplate,
  IconCalendarEvent,
} from "@tabler/icons-react";


// Animasyon tanımları
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.5); }
  50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 30px rgba(59, 130, 246, 0.6); }
`;

// ✨ Gerçek zamanlı çağrı süresi göstergesi komponenti
const CallDurationTimer: React.FC<{ startTime: string | Date | null | undefined; answerTime?: string | Date | null }> = ({ startTime, answerTime }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    // answerTime varsa onu kullan (çağrı cevaplanmış), yoksa startTime kullan
    const timeToUse = answerTime || startTime;

    if (!timeToUse) {
      setElapsed(0);
      return;
    }

    const start = new Date(timeToUse).getTime();

    // Geçersiz tarih kontrolü
    if (isNaN(start)) {
      setElapsed(0);
      return;
    }

    // İlk hesaplama
    const now = Date.now();
    const initialElapsed = Math.max(0, Math.floor((now - start) / 1000));
    setElapsed(initialElapsed);

    // Her saniye güncelle
    const interval = setInterval(() => {
      const currentElapsed = Math.max(0, Math.floor((Date.now() - start) / 1000));
      setElapsed(currentElapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, answerTime]);

  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;

  if (hours > 0) {
    return <>{`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}</>;
  }
  return <>{`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}</>;
};

const EmployeeBadgeCard = ({ user }: { user: any }) => {
  if (!user) return null;
  return (
    <Box sx={{
      width: 380,
      height: 240,
      borderRadius: '12px',
      overflow: 'hidden',
      position: 'relative',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 40%, #2563eb 100%)',
      boxShadow: '0 10px 40px rgba(30, 58, 138, 0.5), 0 2px 8px rgba(0,0,0,0.3)',
      border: '1px solid rgba(255,255,255,0.15)',
      fontFamily: '"Inter", "Roboto", sans-serif',
    }}>
      {/* Decorative diagonal stripe */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '120px',
        height: '100%',
        background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.06) 100%)',
        clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0 100%)',
      }} />

      {/* Top bar - Logo + Title */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        borderBottom: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(0,0,0,0.15)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src="/percrm-logo.svg" alt="PerCRM" style={{ height: '18px', width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
        </Box>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#f59e0b', letterSpacing: 2.5, textTransform: 'uppercase' }}>
          Personel Kimlik Kartı
        </Typography>
      </Box>

      {/* Main content - Photo left, Info right */}
      <Box sx={{ display: 'flex', px: 2, py: 1.5, gap: 2, flex: 1 }}>

        {/* Left - Photo */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Box sx={{
            width: 90,
            height: 110,
            borderRadius: '6px',
            border: '2px solid rgba(255,255,255,0.3)',
            overflow: 'hidden',
            bgcolor: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {user.profilePicture ? (
              <Box component="img" src={user.profilePicture} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Typography sx={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'rgba(255,255,255,0.5)' }}>
                {user.firstName?.[0]}{user.lastName?.[0]}
              </Typography>
            )}
          </Box>
          <Box sx={{
            bgcolor: user.bloodType ? '#dc2626' : 'rgba(255,255,255,0.15)',
            px: 1.2,
            py: 0.2,
            borderRadius: '4px',
          }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', letterSpacing: 0.5 }}>
              {user.bloodType || '—'}
            </Typography>
          </Box>
        </Box>

        {/* Right - Info */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
          {/* Name */}
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#ffffff', lineHeight: 1.2, mb: 0.3 }}>
            {user.firstName} {user.lastName}
          </Typography>
          <Typography sx={{
            fontSize: '0.6rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: 1.5, mb: 1.2,
            display: 'inline-block',
            bgcolor: 'rgba(245, 158, 11, 0.15)',
            px: 1,
            py: 0.2,
            borderRadius: '3px',
            width: 'fit-content'
          }}>
            {user.role === 'admin' ? 'Yönetici' : user.role === 'supervisor' ? 'Süpervizör' : 'Müşteri Temsilcisi'}
          </Typography>

          {/* Detail rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', minWidth: 65, textTransform: 'uppercase' }}>Acil İrtibat</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.emergencyContactName || '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', minWidth: 65, textTransform: 'uppercase' }}>Telefon</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#4ade80' }}>
                {user.emergencyContactPhone || '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', minWidth: 65, textTransform: 'uppercase' }}>Adres</Typography>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {user.address || '—'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Bottom bar */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '20px',
        background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color: '#1e3a8a', letterSpacing: 3, textTransform: 'uppercase' }}>
          PerCRM • Personel Yönetim Sistemi
        </Typography>
      </Box>
    </Box>
  );
};

// Navigation Item Component
interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
}

function NavItem(props: NavItemProps) {
  const { icon, label, active } = props;
  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.5,
      p: '11px 10px',
      borderRadius: 2,
      transition: "all 0.2s ease",
      cursor: "pointer",
      bgcolor: active ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)",
      border: active ? "1px solid rgba(255,255,255,0.4)" : "1px solid rgba(255,255,255,0.12)",
      boxShadow: active ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
      "&:hover": {
        bgcolor: "rgba(255, 255, 255, 0.18)",
        transform: "translateY(-1px)",
      }
    }}>
      <Box sx={{ color: "white", lineHeight: 1 }}>{icon}</Box>
      <Typography variant="caption" sx={{ color: "white", fontSize: "0.72rem", fontWeight: active ? 700 : 500, lineHeight: 1.3, textAlign: 'center' }}>
        {label}
      </Typography>
    </Box>
  );
}

const SupervisorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [statusTab, setStatusTab] = useState(0);
  const [agentsTab, setAgentsTab] = useState(0);
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [agentPerformanceData, setAgentPerformanceData] = useState<any>(null);
  const [agentCallHistory, setAgentCallHistory] = useState<any[]>([]);
  const [loadingAgentData, setLoadingAgentData] = useState(false);
  // MT Performansı modalı: Statü süreleri (AgentPanel'deki mantığın aynısı, tablo olarak)
  const [agentStatusDurations, setAgentStatusDurations] = useState<any[]>([]);
  const [agentQualityData, setAgentQualityData] = useState<any>({
    dailyAverage: 0,
    weeklyAverage: 0,
    monthlyAverage: 0,
    totalAnalyzed: 0
  });

  const [statusData, setStatusData] = useState<any[]>([]);
  const [agentsData, setAgentsData] = useState<any[]>([]);
  const [queueData, setQueueData] = useState<any[]>([]);
  const [activeCalls, setActiveCalls] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState<any>(null);
  const dashboardLoadInFlightRef = useRef(false);
  const analysesLoadInFlightRef = useRef(false);
  const [agentStatuses, setAgentStatuses] = useState<any[]>([]);
  // Üç nokta menüsü için state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAgent, setMenuAgent] = useState<any>(null);

  // Call Analyses states
  const [analysesData, setAnalysesData] = useState<any[]>([]);
  const [analysesTotal, setAnalysesTotal] = useState(0);
  const [analysesPage, setAnalysesPage] = useState(1);
  const [analysesLimit] = useState(10);
  const [analysesLoading, setAnalysesLoading] = useState(false);
  const [analysesSyncing, setAnalysesSyncing] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<any | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState(0);
  const [analysesStats, setAnalysesStats] = useState<any>({
    totalAnalyses: 0,
    completedAnalyses: 0,
    failedAnalyses: 0,
    averageScore: 0,
    positiveCount: 0,
    neutralCount: 0,
    negativeCount: 0
  });

  // Analyses filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [filterCallerNumber, setFilterCallerNumber] = useState('');
  const [filterAgentId, setFilterAgentId] = useState('all');
  const [filterQueueId, setFilterQueueId] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all'); // 'all', 'today', 'yesterday', 'week', 'month', 'custom'
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [analysisFilterAgents, setAnalysisFilterAgents] = useState<any[]>([]);
  const [analysisFilterQueues, setAnalysisFilterQueues] = useState<any[]>([]);

  const fetchAnalysisFilterOptions = async () => {
    try {
      const res = await api.get('/call-analyses/filter-options');
      if (res.data?.success && res.data.data) {
        if (Array.isArray(res.data.data.agents)) {
          setAnalysisFilterAgents(res.data.data.agents);
        }
        if (Array.isArray(res.data.data.queues)) {
          setAnalysisFilterQueues(res.data.data.queues);
        }
      }
    } catch (err) {
      console.error('Failed to fetch analysis filter options:', err);
    }
  };

  // Call analysis manual upload states
  const [manualUploadDialogOpen, setManualUploadDialogOpen] = useState(false);
  const [manualUploadFile, setManualUploadFile] = useState<File | null>(null);
  const [manualUploadAgentId, setManualUploadAgentId] = useState('');
  const [manualUploadCustomerPhone, setManualUploadCustomerPhone] = useState('');
  const [manualUploadDirection, setManualUploadDirection] = useState<'inbound' | 'outbound'>('inbound');
  const [manualUploadLoading, setManualUploadLoading] = useState(false);

  // Statü değiştirme dialog'u için state
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState('');
  const [statusChangeAgent, setStatusChangeAgent] = useState<any>(null);

  // Call monitoring state
  const [monitoringAgent, setMonitoringAgent] = useState<any>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);

  // AgentStatusContext
  const agentStatusContext = useAgentStatus();

  // Agent Stats Dialog States
  const [agentStatsDialogOpen, setAgentStatsDialogOpen] = useState(false);
  const [selectedAgentForStats, setSelectedAgentForStats] = useState<any | null>(null);
  const [agentStatsLoading, setAgentStatsLoading] = useState(false);
  const [agentStatsData, setAgentStatsData] = useState<any>({
    dailyAverage: 0,
    weeklyAverage: 0,
    monthlyAverage: 0,
    totalAnalyzed: 0,
    summaryText: '',
    strengths: [],
    weaknesses: []
  });

  const handleCloseAgentStatsDialog = () => {
    setAgentStatsDialogOpen(false);
  };

  const handleOpenAgentStats = async (agentId: string, agentName: string, agentFromRow?: any) => {
    // Mevcut ajanlar listesinden ajanı bulup profil resmini ekleyelim
    const agent = agentsData.find((a: any) => String(a.id || a._id || a.agent?._id) === String(agentId))?.agent
      || agentsData.find((a: any) => String(a.id || a._id) === String(agentId))
      || agentFromRow
      || {};

    setSelectedAgentForStats({ 
      ...agent,
      id: agentId, 
      name: agentName,
      profilePicture: agent?.profilePicture
    });
    setAgentStatsDialogOpen(true);
    setAgentStatsLoading(true);
    try {
      const response = await api.get(`/call-analyses/agent-stats/${agentId}`);
      if (response.data && response.data.success) {
        setAgentStatsData(response.data.data);
      } else {
        showSnackbar('Temsilci istatistikleri yüklenemedi', 'error');
      }
    } catch (error) {
      console.error('Error loading agent stats:', error);
      showSnackbar('Temsilci istatistikleri yüklenirken hata oluştu', 'error');
    } finally {
      setAgentStatsLoading(false);
    }
  };

  // Sol menü: aktif bölüm (dashboard, calls, reports, support, messages, campaigns, analyses, whatsapp, email-accounts, appointments)
  const [activeSection, setActiveSection] = useState<'dashboard' | 'calls' | 'reports' | 'support' | 'messages' | 'campaigns' | 'analyses' | 'whatsapp' | 'email' | 'email-accounts' | 'appointments'>('dashboard');

  // Randevularım (Supervisor & Temsilciler) State'leri
  const [supervisorAppointments, setSupervisorAppointments] = useState<any[]>([]);
  const [loadingSupervisorAppointments, setLoadingSupervisorAppointments] = useState(false);
  const [supApptFilter, setSupApptFilter] = useState<'all' | 'today' | 'upcoming' | 'overdue' | 'completed'>('all');
  const [supApptAgentFilter, setSupApptAgentFilter] = useState<string>('all');
  const [supApptSearch, setSupApptSearch] = useState<string>('');

  const fetchSupervisorAppointments = useCallback(async () => {
    try {
      setLoadingSupervisorAppointments(true);
      const res = await api.get('/appointments', {
        params: {
          agentId: supApptAgentFilter,
          period: supApptFilter,
          search: supApptSearch
        }
      });
      if (res.data?.success) {
        setSupervisorAppointments(res.data.data || []);
      }
    } catch (err) {
      console.error('❌ Supervisor randevuları hatası:', err);
    } finally {
      setLoadingSupervisorAppointments(false);
    }
  }, [supApptAgentFilter, supApptFilter, supApptSearch]);

  useEffect(() => {
    if (activeSection === 'appointments') {
      fetchSupervisorAppointments();
    }
  }, [activeSection, fetchSupervisorAppointments]);

  // Destek talebi için state'ler
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportPriority, setSupportPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [sendingSupport, setSendingSupport] = useState(false);

  // Raporlar sekmesi için state
  const [activeReportTab, setActiveReportTab] = useState<'agents' | 'calls' | 'queues' | 'status'>('calls');
  const [reportDateRange, setReportDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState<any>(null);

  // WhatsApp Şablonları Eyaletleri
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsappTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [newTmplName, setNewTmplName] = useState('');
  const [newTmplContent, setNewTmplContent] = useState('');
  const [editingTmplId, setEditingTmplId] = useState<string | null>(null);
  const [editingTmplName, setEditingTmplName] = useState('');
  const [editingTmplContent, setEditingTmplContent] = useState('');
  const contentInputRef = useRef<HTMLTextAreaElement>(null);

  // Email Şablonları State'leri
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [loadingEmailTemplates, setLoadingEmailTemplates] = useState(false);
  const [newEmailTmplName, setNewEmailTmplName] = useState('');
  const [newEmailTmplSubject, setNewEmailTmplSubject] = useState('');
  const [newEmailTmplContent, setNewEmailTmplContent] = useState('');
  const [editingEmailTmplId, setEditingEmailTmplId] = useState<string | null>(null);
  const [editingEmailTmplName, setEditingEmailTmplName] = useState('');
  const [editingEmailTmplSubject, setEditingEmailTmplSubject] = useState('');
  const [editingEmailTmplContent, setEditingEmailTmplContent] = useState('');
  const [newEmailTmplAccountId, setNewEmailTmplAccountId] = useState<string>('');
  const [editingEmailTmplAccountId, setEditingEmailTmplAccountId] = useState<string>('');
  const emailContentInputRef = useRef<HTMLTextAreaElement>(null);

  // Sidebar şablonlar grubu
  const [templatesGroupOpen, setTemplatesGroupOpen] = useState(false);

  // Email Hesapları (SMTP) State'leri
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [loadingEmailAccounts, setLoadingEmailAccounts] = useState(false);
  const [newEmailAccountName, setNewEmailAccountName] = useState('');
  const [newEmailAccountEmail, setNewEmailAccountEmail] = useState('');
  const [newEmailAccountHost, setNewEmailAccountHost] = useState('');
  const [newEmailAccountPort, setNewEmailAccountPort] = useState(587);
  const [newEmailAccountSecure, setNewEmailAccountSecure] = useState(false);
  const [newEmailAccountUser, setNewEmailAccountUser] = useState('');
  const [newEmailAccountPass, setNewEmailAccountPass] = useState('');
  const [testingSmtp, setTestingSmtp] = useState(false);

  const [editingEmailAccountId, setEditingEmailAccountId] = useState<string | null>(null);
  const [editingEmailAccountName, setEditingEmailAccountName] = useState('');
  const [editingEmailAccountEmail, setEditingEmailAccountEmail] = useState('');
  const [editingEmailAccountHost, setEditingEmailAccountHost] = useState('');
  const [editingEmailAccountPort, setEditingEmailAccountPort] = useState(587);
  const [editingEmailAccountSecure, setEditingEmailAccountSecure] = useState(false);
  const [editingEmailAccountUser, setEditingEmailAccountUser] = useState('');
  const [editingEmailAccountPass, setEditingEmailAccountPass] = useState('');


  // "Çağrılar" sekmesi için durumlar
  const [calls, setCalls] = useState<any[]>([]);
  const [callsLoading, setCallsLoading] = useState<boolean>(false);
  const [callsPage, setCallsPage] = useState<number>(1);
  const [callsLimit, setCallsLimit] = useState<number>(10);
  const [callsTotalPages, setCallsTotalPages] = useState<number>(1);
  const [callsTotal, setCallsTotal] = useState<number>(0);

  // Filtreleme state'leri
  const [callsFilterDirection, setCallsFilterDirection] = useState<string>('all'); // all, incoming, outgoing
  const [callsFilterStatus, setCallsFilterStatus] = useState<string>('all'); // all, ended, failed, etc.
  const [callsFilterAgent, setCallsFilterAgent] = useState<string>(''); // agent name
  const [callsFilterQueue, setCallsFilterQueue] = useState<string>(''); // queue name
  const [callsFilterPhone, setCallsFilterPhone] = useState<string>(''); // phone number
  const [callsFilterStartDate, setCallsFilterStartDate] = useState<string>(''); // start date (YYYY-MM-DD)
  const [callsFilterEndDate, setCallsFilterEndDate] = useState<string>(''); // end date (YYYY-MM-DD)
  const [callsFilterFinishCode, setCallsFilterFinishCode] = useState<string>(''); // finish code

  // Call recording utilities (play/download)
  const callRecordingContext = useCallRecording();
  const { downloadRecording: crDownload, playRecording: crPlay } = callRecordingContext;
  const [playerDialog, setPlayerDialog] = useState<{ open: boolean; audioUrl: string; title: string }>({ open: false, audioUrl: '', title: '' });
  // WaveSurfer refs/state for popup waveform
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  // Snackbar for errors/warnings
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' | 'warning' | 'info' });

  // Profile menu state
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const currentUser = authService.getCurrentUser();
  const [userData, setUserData] = useState<any>(currentUser);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [badgeCardOpen, setBadgeCardOpen] = useState(false);
  const [profilePic, setProfilePic] = useState<string>('');
  const [bloodType, setBloodType] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [emergencyName, setEmergencyName] = useState<string>('');
  const [emergencyPhone, setEmergencyPhone] = useState<string>('');

  useEffect(() => {
    if (userData) {
      setBloodType(userData.bloodType || '');
      setAddress(userData.address || '');
      setEmergencyName(userData.emergencyContactName || '');
      setEmergencyPhone(userData.emergencyContactPhone || '');
      setProfilePic(userData.profilePicture || '');
    }
  }, [userData]);

  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showSnackbar('Resim boyutu 2MB\'tan küçük olmalıdır', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePic(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    if (!userData?._id) return;
    try {
      const response = await api.put(`/users/${userData._id}`, {
        bloodType,
        address,
        emergencyContactName: emergencyName,
        emergencyContactPhone: emergencyPhone,
        profilePicture: profilePic || userData.profilePicture
      });

      if (response.data && response.data.success) {
        const updatedUser = response.data.data;
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUserData(updatedUser);
        showSnackbar('Çalışan kartı başarıyla güncellendi', 'success');
        setProfileDialogOpen(false);
      } else {
        showSnackbar('Çalışan kartı güncellenemedi', 'error');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      showSnackbar('Çalışan kartı kaydedilirken hata oluştu', 'error');
    }
  };

  // ✨ Messages state
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [showUserSelectDialog, setShowUserSelectDialog] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);

  // Initialize WaveSurfer when supervisor player dialog opens (robust: retry until DOM mounted)
  useEffect(() => {
    console.debug('Supervisor player useEffect', { open: playerDialog.open, audioUrl: playerDialog.audioUrl, waveformMounted: !!waveformRef.current });

    let retryTimer: number | null = null;
    let retryCount = 0;

    const destroyWave = () => {
      try { wavesurferRef.current?.destroy(); } catch (e) { console.warn(e); }
      wavesurferRef.current = null;
    };

    const initWaveform = () => {
      // stop retrying if dialog closed
      if (!playerDialog.open || !playerDialog.audioUrl) return;

      if (!waveformRef.current) {
        if (retryCount < 20) {
          retryCount++;
          retryTimer = window.setTimeout(initWaveform, 100) as unknown as number;
          return;
        }
        console.warn('Waveform container not available after retries');
        return;
      }

      // destroy existing
      destroyWave();

      const ws = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#e6edf3',
        progressColor: '#2563eb',
        cursorColor: '#2563eb',
        barWidth: 2,
        barRadius: 2,
        cursorWidth: 1,
        height: 80,
        barGap: 2,
        normalize: true,
      });

      wavesurferRef.current = ws;

      ws.on('ready', () => {
        const dur = ws.getDuration();
        setDuration(dur);
      });

      ws.on('audioprocess', () => {
        setCurrentTime(ws.getCurrentTime());
      });

      ws.on('play', () => setIsPlaying(true));
      ws.on('pause', () => setIsPlaying(false));
      ws.on('finish', () => setIsPlaying(false));

      ws.on('error', (e: any) => console.error('WaveSurfer error', e));

      ws.setVolume(volume);
      try {
        console.debug('WaveSurfer: loading URL', playerDialog.audioUrl);
        ws.load(playerDialog.audioUrl);
      } catch (e) {
        console.error('WaveSurfer load threw', e);
        destroyWave();
      }
    };

    // Start initialization (may retry until DOM ready)
    initWaveform();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      destroyWave();
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    };
  }, [playerDialog.open, playerDialog.audioUrl, volume]);

  const togglePlayPause = () => {
    if (!wavesurferRef.current) return;
    wavesurferRef.current.playPause();
    setIsPlaying(!isPlaying);
  };

  const stopPlayback = () => {
    if (!wavesurferRef.current) return;
    wavesurferRef.current.stop();
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const changeVolume = (val: number) => {
    setVolume(val);
    if (wavesurferRef.current) wavesurferRef.current.setVolume(val);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const getCoachingActionPlan = () => {
    if (!agentStatsData.criteriaBreakdown || agentStatsData.criteriaBreakdown.length === 0) return [];
    
    const weakCriteria = agentStatsData.criteriaBreakdown.filter((c: any) => {
      const percentage = c.max > 0 ? (c.avgEarned / c.max) * 100 : 0;
      return percentage < 80;
    });

    const recommendations = weakCriteria.map((c: any) => {
      let recText = '';
      if (c.name.includes('Tanıtma') || c.name.includes('Açılış')) {
        recText = 'Görüşme başlangıcında kendini ve kurumu/projeyi eksiksiz, net bir ses tonuyla tanıtarak borçluya güven vermelidir.';
      } else if (c.name.includes('Güvenlik') || c.name.includes('Kayıt')) {
        recText = 'Kritik Güvenlik Teyidi ve Ses Kaydı uyarısını konuşmanın ilk 30 saniyesinde, borçluya borç detayı vermeden önce kesinlikle tamamlamalıdır. Bu kural ihlal edildiğinde görüşmenin doğrudan sıfırlandığı unutulmamalıdır.';
      } else if (c.name.includes('Bilgi')) {
        recText = 'Dosya bakiye, vekalet ücreti, masraf ve faiz bilgilerini sistem ekranındaki güncel tutarlara göre kuruşu kuruşuna aktarmalı, yuvarlama yapmamalıdır.';
      } else if (c.name.includes('Empati') || c.name.includes('Dinleme')) {
        recText = 'Borçlunun ödeme gücünü tespit etmek için etkin dinleme yapmalı, borçlunun sözünü kesmemeli ve empati cümleleri ("Anlıyorum, durumunuzu kolaylaştırmak için...") kullanmalıdır.';
      } else if (c.name.includes('Yasak') || c.name.includes('Olumsuz')) {
        recText = 'Konuşma sırasında negatif veya tahrik edici, kışkırtıcı ifadelerden uzak durmalı, profesyonel bir duruş sergilemelidir.';
      } else if (c.name.includes('Müzakere') || c.name.includes('Vade') || c.name.includes('İndirim')) {
        recText = 'Müzakere sırasında sistem yetkileri ve onaylanmış indirim oranlarının dışına kesinlikle çıkmamalıdır. Yetki dışı indirim taahhüdü verilmemelidir.';
      } else if (c.name.includes('Kapama') || c.name.includes('İstem') || c.name.includes('Netleştir')) {
        recText = 'Görüşme sonunda net taahhüt/ödeme sözü almaya odaklanmalı, ödeme tarihi ve saatini netleştirip teyit almalıdır.';
      } else {
        recText = `"${c.name}" kriterindeki performansını artırmak için çağrı simülasyonları yapmalı ve süpervizör geri bildirimlerini dikkate almalıdır.`;
      }
      return { name: c.name, text: recText };
    }).slice(0, 5);

    return recommendations;
  };

  // Load agent statuses from context on mount
  useEffect(() => {
    agentStatusContext.fetchStatuses();
  }, []);
  useEffect(() => {
    console.log('🔄 FreePBX useEffect running, currentUser._id:', currentUser?._id);

    const initializeFreePBX = async () => {
      try {
        console.log('🎯 Initializing FreePBX WebRTC...');

        // 🎤 Mikrofon iznini hemen iste (Sayfa yüklendiğinde)
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          console.log('✅ Microphone permission granted on load');
        } catch (err) {
          console.error('❌ Microphone permission denied:', err);

        }




        // Backend'den WebRTC credentials al
        const response = await fetch(`${API_URL}/agent/webrtc-credentials`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });

        const data = await response.json();

        if (!data.hasExtension || !data.extension) {
          console.warn('⚠️ Supervisor has no SIP extension assigned');
          return;
        }

        console.log('📞 Supervisor Connecting to WebRTC:', data.server);
        console.log('📋 Supervisor credentials:', { agentId: data.agentId, extension: data.extension });

        // 🆕 Global scope ve localStorage'a kaydet (Socket listener erişebilsin diye)
        (window as any)._agentExtension = data.extension;
        localStorage.setItem('agentExtension', data.extension);

        let serverUrl = data.server;
        let realm = data.realm || 'pbx.percrm.net';

        await freePBXWebRTCService.connect({
          extension: data.extension,
          password: data.password,
          server: serverUrl,
          realm: realm,
          displayName: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.extension
        });

        // Debug için global scope'a ekle
        (window as any).freePBXWebRTCService = freePBXWebRTCService;
        console.log('🔧 freePBXWebRTCService added to window for debugging');

        // Event listeners
        freePBXWebRTCService.onRegistered = () => {
          console.log('✅ FreePBX registered');

          // Backend'e agent bağlandı bildir

        };

        freePBXWebRTCService.onUnregistered = () => {
          console.log('❌ FreePBX unregistered');
        };

        freePBXWebRTCService.onReconnecting = (attempt: number, maxAttempts: number) => {
          console.log(`🔄 Reconnecting... (${attempt}/${maxAttempts})`);

        };

        freePBXWebRTCService.onRemoteStream = (stream: MediaStream) => {
          const remoteAudio = document.getElementById('remoteAudio') as HTMLAudioElement;
          if (remoteAudio) {
            remoteAudio.srcObject = stream;
            remoteAudio.play();
          }
        };

        freePBXWebRTCService.onIncomingCall = (callerNumber: string, calledNumber: string, invitation: any) => {
          console.log('📞 onIncomingCall triggered!');
          console.log('📞 Incoming call from:', callerNumber, 'to:', calledNumber);

          // 1. Display Name'i al (Genellikle "Queue Name" veya "CID:QueueName" formatındadır)
          let displayName = invitation.remoteIdentity?.displayName || '';
          console.log('👤 Caller Display Name:', displayName);

          // 2. Headerlardan Kuyruk Bilgisi Ara
          let queueName = '';
          let sipNumber = calledNumber;

          try {
            const headers = invitation.request.headers;
            console.log('📜 SIP Headers:', headers);

            // X-Queue veya X-Queue-Name headerlarını kontrol et
            if (headers['X-Queue']) queueName = headers['X-Queue'][0].raw;
            else if (headers['X-Queue-Name']) queueName = headers['X-Queue-Name'][0].raw;

            // Diversion header'dan DID bul
            if (headers['Diversion']) {
              const match = headers['Diversion'][0].raw.match(/sip:(\d+)@/);
              if (match) sipNumber = match[1];
            }
          } catch (e) {
            console.warn('⚠️ Error parsing headers:', e);
          }

          // 3. Kuyruk ismini belirle
          // Eğer headerlardan gelmediyse, Display Name'den çıkarmaya çalış
          if (!queueName && displayName) {
            // Örnek: "Satis <0555...>" -> "Satis"
            // Örnek: "CID:Satis" -> "Satis"
            // Sadece harf ve boşluk içeren kısmı al (numaraları temizle)
            const cleanName = displayName.replace(/<.*>/, '').replace('CID:', '').trim();
            if (cleanName && cleanName !== callerNumber) {
              queueName = cleanName;
            }
          }

          // Eğer hala yoksa ve DID farklıysa DID'yi kullan
          if (!queueName && sipNumber !== calledNumber) {
            queueName = `Hat: ${sipNumber}`;
          }

          // Eğer hiçbiri yoksa varsayılan
          if (!queueName) {
            queueName = 'Gelen Çağrı';
          }

          console.log('🏷️ Final Queue Name:', queueName);

          // 🎯 Hemen geçici ID ile başla - backend ID'sini arka planda al
          const tempCallId = `incoming-${Date.now()}`;

          // Eğer zaten aktif bir ses oturumu varsa çift aramayı reddet
          const activeSession = freePBXWebRTCService.getCurrentSession();
          if (activeSession && 
              activeSession !== invitation &&
              (activeSession.state === 'Establishing' || activeSession.state === 'Established')) {
            console.warn('⚠️ Already in active call session, rejecting duplicate invitation');
            try { invitation.reject(); } catch (e) {}
            return;
          }

          setTimeout(() => {
            if (invitation.state === 'Initial' || invitation.state === 'Establishing') {
              freePBXWebRTCService.acceptCall(invitation);
            }
          }, 100);



          // Invitation'ı sakla (cevap vermek için)
          (window as any)._currentInvitation = invitation;

          // 🔄 Arka planda backend'den gerçek ID'yi al ve güncelle

        };

        freePBXWebRTCService.onCallEstablished = async (targetNumber?: string) => {
          console.log('✅ Call established', targetNumber);


          // Karşı taraf açtı bildirimi (kısa ses)
          try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const beepFrequency = 1000; // 1000 Hz
            const beepDuration = 0.1; // 100ms

            // Tek beep sesi
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = beepFrequency;
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + beepDuration);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + beepDuration);
          } catch (e) {
            console.log('Could not play beep:', e);
          }

          // Backend'e çağrı answered bildir

        };

        freePBXWebRTCService.onCallEnded = () => {
          console.log('📴 Call ended (remote hangup or network issue)');

          // Kapanma sesi çal (dit dit dit)
          console.log('🔊 Playing hangup beep...');
          try {
            const playBeep = () => {
              const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

              // Gerçek telefon meşgul sesi: 2 beep, pause, 2 beep, pause, 2 beep
              const beepPattern = [0, 0.25]; // Beep zamanları

              beepPattern.forEach(time => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);

                oscillator.frequency.value = 480; // Hz
                oscillator.type = 'sine';

                const startTime = audioContext.currentTime + time;
                gainNode.gain.setValueAtTime(0.25, startTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);

                oscillator.start(startTime);
                oscillator.stop(startTime + 0.2);
              });
            };

            playBeep();
            console.log('✅ Hangup beep played');
          } catch (e) {
            console.error('❌ Could not play beep:', e);
          }

          // State'leri temizle

          // ✨ Telefon numarasını SAKLIYORUZ - müşteri kartı açık kalsın
          // setCurrentCallPhoneNumber(''); // ❌ Bunu kaldırdık
          // ⚠️ currentCallId'yi KORUYORUZ - finish code için gerekli
          // setCurrentCallId(null); ❌ Bunu kaldırdık
          // currentCallIdRef.current = null; ❌ Bunu kaldırdık

          // Backend'e bildir - use captured callId

        };


        console.log('✅ FreePBX WebRTC initialized');

      } catch (error) {
        console.error('❌ FreePBX initialization error:', error);
      }
    };

    let isInitialized = false;

    if (currentUser?._id && !isInitialized) {
      isInitialized = true;
      console.log('🚀 Starting FreePBX initialization for user:', currentUser._id);
      initializeFreePBX();
    }

    // Cleanup
    return () => {
      console.log('🧹 Cleaning up FreePBX connection...');
      freePBXWebRTCService.disconnect();
    };
  }, [currentUser?._id]);


  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showProfileMenu) {
        const target = event.target as HTMLElement;
        // Check if click is outside profile menu
        if (!target.closest('.profile-menu-container')) {
          setShowProfileMenu(false);
        }
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileMenu]);

  const openAgentMenu = (event: React.MouseEvent<HTMLElement>, agent: any) => {
    event.stopPropagation();
    console.log('🔍 Opening agent menu for:', agent);
    setMenuAnchorEl(event.currentTarget);
    setMenuAgent(agent);
  };

  const closeAgentMenu = () => {
    setMenuAnchorEl(null);
    setMenuAgent(null);
  };

  const handleOpenStatusChangeDialog = (agent: any) => {
    setStatusChangeAgent(agent);
    setSelectedStatusId(agent.currentStatusId || '');
    setStatusChangeDialogOpen(true);
    closeAgentMenu();
  };

  const handleCloseStatusChangeDialog = () => {
    setStatusChangeDialogOpen(false);
    setStatusChangeAgent(null);
    setSelectedStatusId('');
  };

  const handleChangeAgentStatus = async () => {
    if (!statusChangeAgent || !selectedStatusId) {
      setSnackbar({ open: true, message: 'Lütfen bir statü seçin', severity: 'warning' });
      return;
    }

    // Agent ID'yi al - önce id'ye bak, yoksa agent._id'yi kullan
    const agentId = statusChangeAgent.id || statusChangeAgent.agent?._id;

    if (!agentId) {
      setSnackbar({ open: true, message: 'Agent ID bulunamadı', severity: 'error' });
      console.error('Agent ID not found in:', statusChangeAgent);
      return;
    }

    console.log('🔄 Changing agent status:', {
      agent: statusChangeAgent,
      agentId: agentId,
      statusId: selectedStatusId
    });

    try {
      const success = await agentStatusContext.setAgentStatus(agentId, selectedStatusId);

      if (success) {
        setSnackbar({ open: true, message: 'Agent statüsü başarıyla değiştirildi', severity: 'success' });
        handleCloseStatusChangeDialog();
        // Agent listesi socket üzerinden otomatik güncellenecek
      } else {
        setSnackbar({ open: true, message: 'Statü değiştirilemedi', severity: 'error' });
      }
    } catch (error: any) {
      console.error('Error changing agent status:', error);
      const errorMsg = error?.response?.data?.message || 'Statü değiştirme hatası';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const getAgentMonitoringParams = (agent: any) => {
    const agentId = agent.id || agent.agent?._id || agent._id;
    const agentExtension = agent.extension || agent.sipExtension || agent.agent?.extension || agent.agent?.sipExtension;

    const agentCall = activeCalls.find((call: any) => {
      return (
        (agentId && (call.agentId === agentId || call.agent?._id === agentId)) ||
        (agentExtension && (call.agentExtension === agentExtension || call.extension === agentExtension || call.callerNumber === agentExtension || call.calledNumber === agentExtension))
      );
    });

    const targetChannel = agentCall?.agentChannel || agentCall?.channel || agentCall?.uuid || agentExtension || agentId;
    const supervisorExtension = currentUser?.sipExtension || currentUser?.extension || localStorage.getItem('agentExtension') || (window as any)._agentExtension;

    return { agentId, agentExtension, agentCall, targetChannel, supervisorExtension };
  };

  // Whispering (Fısıldama)
  const handleStartWhisper = async (agent: any) => {
    handleStopMonitoring();
    try {
      const { agentId, targetChannel, supervisorExtension } = getAgentMonitoringParams(agent);

      if (!targetChannel) {
        showSnackbar('Agent dahili numarası veya aktif çağrısı bulunamadı', 'warning');
        closeAgentMenu();
        return;
      }

      console.log('🎧 Starting whisper mode:', { agent, targetChannel, supervisorExtension });

      const response = await api.post('/agent-statuses/monitor-call', {
        agentChannel: targetChannel,
        agentId: agentId,
        supervisorExtension: supervisorExtension,
        mode: 'whisper'
      });

      if (response.data.success) {
        (window as any)._activeMonitorChannelUuid = response.data.data?.supervisorChannel || response.data.data?.targetChannel;
        setMonitoringAgent(agent);
        setIsMonitoring(true);
        showSnackbar('Fısıldama başlatıldı', 'success');
      }

      closeAgentMenu();
    } catch (error: any) {
      console.error('❌ Error starting whisper:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Fısıldama başlatılamadı';
      showSnackbar(errorMsg, 'error');
      closeAgentMenu();
    }
  };

  // Konferans (Barge)
  const handleStartBarge = async (agent: any) => {
    handleStopMonitoring();
    try {
      const { agentId, targetChannel, supervisorExtension } = getAgentMonitoringParams(agent);

      if (!targetChannel) {
        showSnackbar('Agent dahili numarası veya aktif çağrısı bulunamadı', 'warning');
        closeAgentMenu();
        return;
      }

      console.log('🎧 Starting barge mode (conference):', { agent, targetChannel, supervisorExtension });

      const response = await api.post('/agent-statuses/monitor-call', {
        agentChannel: targetChannel,
        agentId: agentId,
        supervisorExtension: supervisorExtension,
        mode: 'barge'
      });

      if (response.data.success) {
        (window as any)._activeMonitorChannelUuid = response.data.data?.supervisorChannel || response.data.data?.targetChannel;
        setMonitoringAgent(agent);
        setIsMonitoring(true);
        showSnackbar('Konferans başlatıldı', 'success');
      }

      closeAgentMenu();
    } catch (error: any) {
      console.error('❌ Error starting barge:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Konferans başlatılamadı';
      showSnackbar(errorMsg, 'error');
      closeAgentMenu();
    }
  };

  // ✨ Messaging functions for supervisor
  const loadUsers = async () => {
    try {
      const response = await messagesAPI.getUsers();
      if (response.data.success) {
        setAllUsers(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
    }
  };

  const loadSupervisorConversations = async () => {
    try {
      setLoadingMessages(true);
      const response = await messagesAPI.getConversations();
      if (response.data.success) {
        setConversations(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error loading supervisor conversations:', error);
      showSnackbar('Konuşmalar yüklenirken hata oluştu', 'error');
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadConversationMessages = async (userId1: string, userId2: string) => {
    try {
      setLoadingMessages(true);
      // Supervisor can see any conversation - just use first userId
      const response = await messagesAPI.getConversation(userId1);
      if (response.data.success) {
        // Filter messages to show only those between these two users
        const filteredMessages = response.data.data.filter((msg: any) =>
          (msg.senderId?._id === userId1 && msg.receiverId?._id === userId2) ||
          (msg.senderId?._id === userId2 && msg.receiverId?._id === userId1)
        );
        setMessages(filteredMessages);
      }
    } catch (error) {
      console.error('❌ Error loading conversation messages:', error);
      showSnackbar('Mesajlar yüklenirken hata oluştu', 'error');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    try {
      const messageContent = newMessage.trim();
      const recipientId = selectedUser._id;
      setNewMessage('');

      // Send to backend
      const response = await messagesAPI.send(recipientId, messageContent);

      if (response.data.success) {
        const sentMessage = response.data.data;

        // Add message to local state if we have selectedUser
        if (selectedUser && !selectedConversation) {
          // New conversation - add the first message
          setMessages([sentMessage]);

          // Create a mock conversation object
          setSelectedConversation({
            _id: { user1: currentUser._id, user2: recipientId },
            lastMessage: sentMessage
          });
        } else if (selectedConversation) {
          // Existing conversation - reload messages
          loadConversationMessages(
            selectedConversation.lastMessage.senderId._id,
            selectedConversation.lastMessage.receiverId._id
          );
        }

        // Always reload conversations list
        loadSupervisorConversations();
        showSnackbar('Mesaj gönderildi', 'success');
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      showSnackbar('Mesaj gönderilemedi', 'error');
    }
  };

  // Call monitoring functions (Listen)
  const handleStartMonitoring = async (agent: any) => {
    try {
      const { agentId, targetChannel, supervisorExtension } = getAgentMonitoringParams(agent);

      if (!targetChannel) {
        showSnackbar('Agent dahili numarası veya aktif çağrısı bulunamadı', 'warning');
        closeAgentMenu();
        return;
      }

      console.log('🎧 Starting call monitoring (listen):', { agent, targetChannel, supervisorExtension });

      const response = await api.post('/agent-statuses/monitor-call', {
        agentChannel: targetChannel,
        agentId: agentId,
        supervisorExtension: supervisorExtension,
        mode: 'listen'
      });

      if (response.data.success) {
        (window as any)._activeMonitorChannelUuid = response.data.data?.supervisorChannel || response.data.data?.targetChannel;
        setMonitoringAgent(agent);
        setIsMonitoring(true);
        showSnackbar('Dinleme başlatıldı', 'success');
      }

      closeAgentMenu();
    } catch (error: any) {
      console.error('❌ Error starting monitoring:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Dinleme başlatılamadı';
      showSnackbar(errorMsg, 'error');
      closeAgentMenu();
    }
  };

  const handleStopMonitoring = async () => {
    try {
      // 1. Browser WebRTC tarafında aktif sesi/çağrıyı sonlandır
      try {
        await freePBXWebRTCService.hangup();
      } catch (webrtcErr) {
        console.warn('⚠️ WebRTC hangup notice:', webrtcErr);
      }

      // 2. FreeSWITCH backend tarafında dinleme kanalını sonlandır
      const supervisorExtension = localStorage.getItem('agentExtension') || (window as any)._agentExtension;
      const response = await api.post('/agent-statuses/stop-monitor', {
        supervisorExtension,
        supervisorChannel: (window as any)._activeMonitorChannelUuid || undefined
      });

      if (response.data.success) {
        showSnackbar('Dinleme durduruldu', 'success');
      }

      (window as any)._activeMonitorChannelUuid = null;
      setMonitoringAgent(null);
      setIsMonitoring(false);
    } catch (error: any) {
      console.error('❌ Error stopping monitoring:', error);
      const errorMsg = error?.response?.data?.message || 'Dinleme durdurulamadı';
      showSnackbar(errorMsg, 'error');
      (window as any)._activeMonitorChannelUuid = null;
      setMonitoringAgent(null);
      setIsMonitoring(false);
    }
  };

  // ✨ Get status color from database or fallback
  const getStatusColor = (statusName: string) => {
    if (!statusName) return "#6b7280";

    // First try to find in agentStatuses from database
    const status = agentStatuses.find((s: any) =>
      s.name?.toLowerCase() === statusName.toLowerCase() ||
      s.name === statusName
    );
    if (status?.color) {
      return status.color;
    }

    // Fallback to hardcoded colors for backward compatibility
    const statusLower = (statusName || '').toLowerCase();
    if (statusLower.includes('available') || statusLower.includes('hazır') || statusLower === 'available' || statusLower === 'hazır') {
      return "#10b981";
    } else if (statusLower.includes('backoffice') || statusLower.includes('ofis') || statusLower === 'backoffice') {
      return "#3b82f6";
    } else if (statusLower.includes('not answering') || statusLower.includes('cevap vermiyor') || statusLower.includes('offline') || statusLower === 'not answering') {
      return "#ef4444";
    } else {
      return "#6b7280";
    }
  };

  const handleAgentClick = async (agent: any) => {
    setSelectedAgent(agent);
    setIsPerformanceModalOpen(true);
    setLoadingAgentData(true);
    setAgentQualityData({
      dailyAverage: 0,
      weeklyAverage: 0,
      monthlyAverage: 0,
      totalAnalyzed: 0
    });

    try {
      // Get agent ID - agent listesinde "id" olarak geliyor, agent objesinde "_id" olabilir
      const agentId = agent.id || agent._id || agent.agent?._id;

      console.log('📊 handleAgentClick - agent:', agent);
      console.log('📊 handleAgentClick - agentId:', agentId);

      if (!agentId) {
        console.error('No agent ID found in:', agent);
        setLoadingAgentData(false);
        return;
      }

      setSelectedAgent({
        ...agent,
        profilePicture: agent.profilePicture || agent.agent?.profilePicture
      });

      // Format duration helper
      const formatDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      };

      // Load performance data - agent-stats'tan al (bugünün verisi)
      const statsResponse = await api.get(`/agent-stats/today/${agentId}`);
      const stats = statsResponse?.data?.data || {};

      try {
        const qualityResponse = await api.get(`/call-analyses/agent-stats/${agentId}`);
        if (qualityResponse.data?.success) {
          setAgentQualityData(qualityResponse.data.data);
        }
      } catch (qualityError) {
        console.error('Error loading agent quality stats:', qualityError);
        setAgentQualityData({
          dailyAverage: 0,
          weeklyAverage: 0,
          monthlyAverage: 0,
          totalAnalyzed: 0
        });
      }

      // Eski formata dönüştür
      const perfResponse = {
        data: {
          success: true,
          data: {
            totalCalls: stats.totalCalls || 0,
            answeredCalls: stats.answeredCalls || 0,
            missedCalls: stats.missedCalls || 0,
            averageCallDuration: stats.avgCallDuration || 0,
            averageWaitTime: 0, // TODO: waitTime hesaplansın
            successRate: stats.totalCalls > 0 ? Math.round((stats.answeredCalls / stats.totalCalls) * 100) : 0
          }
        }
      };

      const callStatsResponse = {
        data: {
          success: true,
          callStats: {
            incomingCalls: stats.incomingCalls || 0,
            outgoingCalls: stats.outgoingCalls || 0,
            totalCallDuration: formatDuration(stats.totalDuration || 0),
            incomingCallDuration: formatDuration(stats.incomingDuration || 0),
            outgoingCallDuration: formatDuration(stats.outgoingDuration || 0),
            avgTotalCallDuration: formatDuration(stats.avgCallDuration || 0),
            avgIncomingCallDuration: formatDuration(stats.avgIncomingDuration || 0),
            avgOutgoingCallDuration: formatDuration(stats.avgOutgoingDuration || 0)
          }
        }
      };

      // Load call history
      const historyResponse = await callAPI.getAgentCallHistory(agentId);

      // Load status durations (Bugün) — AgentPanel ile aynı mantık
      const todayDate = new Date();
      const startOfDay = new Date(todayDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(todayDate);
      endOfDay.setHours(23, 59, 59, 999);
      let statusDurationsResponse: any = null;
      try {
        // AgentPanel'de kullanılan endpoint
        // reportsAPI.getAgentStatusDurations(agentId, startISO, endISO)
        // Not: reportsAPI importu zaten mevcut.
        statusDurationsResponse = await reportsAPI.getAgentStatusDurations(
          agentId,
          startOfDay.toISOString(),
          endOfDay.toISOString()
        );
      } catch (e) {
        console.error('Error loading status durations:', e);
      }

      if (perfResponse.data?.success) {
        const perf = perfResponse.data.data || {};
        const callStats = callStatsResponse.data?.callStats || {};

        setAgentPerformanceData({
          totalCalls: perf.totalCalls || 0,
          answeredCalls: perf.answeredCalls || 0,
          missedCalls: perf.missedCalls || 0,
          avgCallDuration: typeof perf.averageCallDuration === 'number' ? formatDuration(perf.averageCallDuration) : '00:00:00',
          avgWaitTime: typeof perf.averageWaitTime === 'number' ? formatDuration(perf.averageWaitTime) : '00:00:00',
          satisfactionRate: perf.successRate || 0,
          incomingCalls: callStats.incomingCalls || 0,
          outgoingCalls: callStats.outgoingCalls || 0,
          totalCallDuration: callStats.totalCallDuration || '00:00:00',
          incomingCallDuration: callStats.incomingCallDuration || '00:00:00',
          outgoingCallDuration: callStats.outgoingCallDuration || '00:00:00',
          avgTotalCallDuration: callStats.avgTotalCallDuration || '00:00:00',
          avgIncomingCallDuration: callStats.avgIncomingCallDuration || '00:00:00',
          avgOutgoingCallDuration: callStats.avgOutgoingCallDuration || '00:00:00'
        });
      }

      if (historyResponse.data?.success) {
        // Backend response shape: { success: true, data: [...], pagination: {...} }
        // Previously we were reading historyResponse.data.calls which is undefined.
        const calls = Array.isArray(historyResponse.data?.data)
          ? historyResponse.data.data
          : [];
        setAgentCallHistory(calls);
      }

      // Set status durations
      if (statusDurationsResponse?.data?.success && Array.isArray(statusDurationsResponse.data.statusDurations)) {
        const raw = statusDurationsResponse.data.statusDurations;
        // Toplam süreyi hesapla (saniye)
        const totalSec = raw.reduce((acc: number, s: any) => {
          const sec = typeof s.durationSeconds === 'number'
            ? s.durationSeconds
            : (typeof s.totalDuration === 'number' ? s.totalDuration : 0);
          return acc + (sec > 0 ? sec : 0);
        }, 0);

        // Oranı yüzde olarak hesapla (toplam gün içi süreye göre)
        const withRatio = raw.map((s: any) => {
          const sec = typeof s.durationSeconds === 'number'
            ? s.durationSeconds
            : (typeof s.totalDuration === 'number' ? s.totalDuration : 0);
          const ratio = (typeof s.ratio === 'number' || typeof s.percentage === 'number')
            ? (s.ratio ?? s.percentage)
            : (totalSec > 0 ? (sec / totalSec) * 100 : 0);
          return { ...s, ratio };
        });

        setAgentStatusDurations(withRatio);
      } else {
        setAgentStatusDurations([]);
      }

    } catch (error) {
      console.error('Error loading agent data:', error);
    } finally {
      setLoadingAgentData(false);
    }
  };

  // Destek talebi gönderme fonksiyonu
  const handleSendSupport = async () => {
    if (!supportSubject.trim() || !supportMessage.trim()) {
      setSnackbar({ open: true, message: 'Lütfen konu ve mesaj alanlarını doldurun', severity: 'error' });
      return;
    }

    setSendingSupport(true);
    try {
      const res = await api.post('/support/send-ticket', {
        subject: supportSubject,
        message: supportMessage,
        priority: supportPriority,
        senderName: currentUser?.name || currentUser?.username || 'Bilinmeyen',
        senderEmail: currentUser?.email || '',
        senderRole: currentUser?.role || 'supervisor',
        locationId: currentUser?.locationId || null
      });

      if (res.data?.success) {
        setSnackbar({ open: true, message: 'Destek talebiniz başarıyla gönderildi', severity: 'success' });
        setSupportSubject('');
        setSupportMessage('');
        setSupportPriority('medium');
      } else {
        setSnackbar({ open: true, message: res.data?.message || 'Destek talebi gönderilemedi', severity: 'error' });
      }
    } catch (error: any) {
      console.error('Error sending support ticket:', error);
      setSnackbar({ open: true, message: error?.response?.data?.message || 'Destek talebi gönderilirken hata oluştu', severity: 'error' });
    } finally {
      setSendingSupport(false);
    }
  };

  // Yardımcılar
  const formatDateTR = (iso?: string) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
    } catch {
      return iso;
    }
  };

  const formatDurationHHMMSS = (totalSeconds?: number) => {
    const seconds = Math.max(0, Number(totalSeconds || 0));
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  // Tüm çağrıları yükle (filtreleme için)
  // Tüm agent kullanıcıları (filtreleme için)
  const [allAgents, setAllAgents] = useState<any[]>([]);

  // Tüm aktif kuyruklar (filtreleme için)
  const [allQueues, setAllQueues] = useState<any[]>([]);

  // Debounce ref'i - telefon filtresi için
  const callsPhoneDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [callsFilterPhoneInput, setCallsFilterPhoneInput] = useState('');

  const loadCalls = async (overrides?: {
    page?: number; direction?: string; status?: string;
    agent?: string; queue?: string; phone?: string;
    startDate?: string; endDate?: string; finishCode?: string;
  }) => {
    try {
      setCallsLoading(true);
      const params: any = {
        page: overrides?.page ?? callsPage,
        limit: callsLimit,
        sortBy: 'startTime',
        sortOrder: 'desc',
      };
      const dir = overrides?.direction ?? callsFilterDirection;
      const st = overrides?.status ?? callsFilterStatus;
      const ag = overrides?.agent ?? callsFilterAgent;
      const qu = overrides?.queue ?? callsFilterQueue;
      const ph = overrides?.phone ?? callsFilterPhone;
      const sd = overrides?.startDate ?? callsFilterStartDate;
      const ed = overrides?.endDate ?? callsFilterEndDate;
      const fc = overrides?.finishCode ?? callsFilterFinishCode;

      if (dir !== 'all') params.direction = dir;
      if (st !== 'all') params.status = st;
      if (ag) params.agentName = ag;
      if (qu) params.queueName = qu;
      if (ph) params.phoneNumber = ph;
      if (sd) params.startDate = sd;
      if (ed) params.endDate = ed;
      if (fc) params.finishCode = fc;

      const res = await callAPI.getAll(params);
      if (res.data?.success) {
        setCalls(Array.isArray(res.data.data) ? res.data.data : []);
        setCallsTotal(res.data.pagination?.total ?? 0);
        setCallsTotalPages(res.data.pagination?.pages ?? 1);
      } else {
        setCalls([]);
        setCallsTotal(0);
        setCallsTotalPages(1);
      }
    } catch (e) {
      console.error('❌ Calls load error:', e);
    } finally {
      setCallsLoading(false);
    }
  };

  const handleExportCallsExcel = async () => {
    try {
      const params: any = {
        limit: 5000,
        sortBy: 'startTime',
        sortOrder: 'desc',
      };

      if (callsFilterDirection !== 'all') params.direction = callsFilterDirection;
      if (callsFilterStatus !== 'all') params.status = callsFilterStatus;
      if (callsFilterAgent) params.agentName = callsFilterAgent;
      if (callsFilterQueue) params.queueName = callsFilterQueue;
      if (callsFilterPhone) params.phoneNumber = callsFilterPhone;
      if (callsFilterStartDate) params.startDate = callsFilterStartDate;
      if (callsFilterEndDate) params.endDate = callsFilterEndDate;
      if (callsFilterFinishCode) params.finishCode = callsFilterFinishCode;

      alert('Excel raporu hazırlanıyor, lütfen bekleyin...');

      const res = await callAPI.getAll(params);
      if (res.data?.success && Array.isArray(res.data.data)) {
        const exportData = res.data.data;
        if (exportData.length === 0) {
          alert('Dışa aktarılacak çağrı bulunamadı.');
          return;
        }

        const headers = ['Çağrı ID', 'Tarih/Saat', 'Yön', 'Telefon', 'Müşteri', 'Temsilci', 'Kuyruk', 'Süre (sn)', 'Durum', 'Kayıt', 'Finish Code'];
        const csvRows = [
          '\uFEFF' + headers.join(';')
        ];

        exportData.forEach((call: any) => {
          const row = [
            call._id || '',
            call.startTime ? new Date(call.startTime).toLocaleString('tr-TR') : '-',
            call.direction === 'incoming' ? 'Gelen' : 'Giden',
            call.calledNumber || call.callerNumber || '-',
            call.customerName || '-',
            call.agentName || call.agentId || '-',
            call.queueName || call.queueId || '-',
            call.duration ? String(call.duration) : '0',
            call.status || '-',
            call.recordingUrl ? 'Var' : 'Yok',
            call.finishCodeName || call.finishCode || '-'
          ];
          csvRows.push(row.map(val => `"${val.replace(/"/g, '""')}"`).join(';'));
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `Cagri_Raporu_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Çağrılar yüklenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Error exporting calls:', err);
      alert('Rapor indirilirken hata oluştu.');
    }
  };

  const handleExportAnalysesExcel = async () => {
    if (currentUser?.role !== 'admin' && currentUser?.role !== 'supervisor') return;
    try {
      const queryParams: any = {
        limit: 5000,
        page: 1
      };

      if (filterStatus && filterStatus !== 'all') queryParams.status = filterStatus;
      if (filterSentiment && filterSentiment !== 'all') queryParams.sentiment = filterSentiment;
      if (filterCallerNumber) queryParams.callerNumber = filterCallerNumber;
      if (filterAgentId && filterAgentId !== 'all') queryParams.agentId = filterAgentId;
      if (filterQueueId && filterQueueId !== 'all') queryParams.queueId = filterQueueId;
      if (filterDateRange && filterDateRange !== 'all') queryParams.dateRange = filterDateRange;
      if (filterStartDate) queryParams.startDate = filterStartDate;
      if (filterEndDate) queryParams.endDate = filterEndDate;

      alert('Analiz raporu hazırlanıyor, lütfen bekleyin...');

      const response = await api.get('/call-analyses', { params: queryParams });
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        const exportData = response.data.data;
        if (exportData.length === 0) {
          alert('Dışa aktarılacak analiz kaydı bulunamadı.');
          return;
        }

        const headers = ['Çağrı ID', 'Temsilci', 'Müşteri', 'Kuyruk', 'Sonuç Kodu', 'Çağrı Tarihi', 'Süre (sn)', 'Duygu', 'Kalite Skoru', '1. Analiz', '2. Analiz', 'Durum', 'Güçlü Yönler', 'Zayıf Yönler', 'Anahtar Kelimeler'];
        const csvRows = [
          '\uFEFF' + headers.join(';')
        ];

        exportData.forEach((analysis: any) => {
          const row = [
            analysis._id || '',
            analysis.agentName || '',
            analysis.callerNumber || '',
            analysis.queueName || analysis.callId?.queueId?.name || '-',
            analysis.finishCode || analysis.callId?.finishCode || '-',
            analysis.callStartTime ? new Date(analysis.callStartTime).toLocaleString('tr-TR') : '-',
            analysis.callDuration ? String(analysis.callDuration) : '0',
            analysis.sentiment || '-',
            analysis.status === 'completed' ? String(analysis.score) : '-',
            analysis.status === 'completed' ? String(analysis.firstAnalysis?.score ?? analysis.score) : '-',
            analysis.secondAnalysis?.status === 'completed' ? String(analysis.secondAnalysis.score) : (['pending', 'processing'].includes(analysis.secondAnalysis?.status) ? 'Analiz ediliyor' : '-'),
            analysis.status === 'completed' ? (['pending', 'processing'].includes(analysis.secondAnalysis?.status) ? '2. Analiz İşleniyor' : 'Tamamlandı') : (analysis.status === 'processing' ? 'İşleniyor' : (analysis.status === 'failed' ? 'Başarısız' : 'Bekliyor')),
            Array.isArray(analysis.strengths) ? analysis.strengths.join(', ') : (analysis.strengths || '-'),
            Array.isArray(analysis.weaknesses) ? analysis.weaknesses.join(', ') : (analysis.weaknesses || '-'),
            analysis.keywords || '-'
          ];
          csvRows.push(row.map(val => `"${val.replace(/"/g, '""')}"`).join(';'));
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `Analiz_Raporu_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Analiz verileri yüklenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Error exporting analyses:', err);
      alert('Rapor indirilirken hata oluştu.');
    }
  };

  // Call Analyses API Loaders
  const loadAnalyses = async (params: any = {}) => {
    if (currentUser?.role !== 'admin' && currentUser?.role !== 'supervisor') return;
    if (analysesLoadInFlightRef.current && !params.silent) return;
    analysesLoadInFlightRef.current = true;
    const silent = params.silent === true;
    if (!silent) setAnalysesLoading(true);
    try {
      const page = params.page !== undefined ? params.page : analysesPage;
      const status = params.status !== undefined ? params.status : filterStatus;
      const sentiment = params.sentiment !== undefined ? params.sentiment : filterSentiment;
      const callerNumber = params.callerNumber !== undefined ? params.callerNumber : filterCallerNumber;
      const agentId = params.agentId !== undefined ? params.agentId : filterAgentId;
      const queueId = params.queueId !== undefined ? params.queueId : filterQueueId;
      const dateRange = params.dateRange !== undefined ? params.dateRange : filterDateRange;
      const startDate = params.startDate !== undefined ? params.startDate : filterStartDate;
      const endDate = params.endDate !== undefined ? params.endDate : filterEndDate;

      const queryParams: any = {
        page,
        limit: analysesLimit
      };

      if (status && status !== 'all') queryParams.status = status;
      if (sentiment && sentiment !== 'all') queryParams.sentiment = sentiment;
      if (callerNumber) queryParams.callerNumber = callerNumber;
      if (agentId && agentId !== 'all') queryParams.agentId = agentId;
      if (queueId && queueId !== 'all') queryParams.queueId = queueId;
      if (dateRange && dateRange !== 'all') queryParams.dateRange = dateRange;
      if (startDate) queryParams.startDate = startDate;
      if (endDate) queryParams.endDate = endDate;

      const response = await api.get('/call-analyses', { params: queryParams });
      if (response.data && response.data.success) {
        setAnalysesData(response.data.data || []);
        setAnalysesTotal(response.data.pagination?.total || 0);
        if (response.data.stats) {
          setAnalysesStats(response.data.stats);
        }
      }
    } catch (error) {
      console.error('❌ Error loading call analyses:', error);
      if (!silent) showSnackbar('Analiz verileri yüklenirken hata oluştu', 'error');
    } finally {
      analysesLoadInFlightRef.current = false;
      if (!silent) setAnalysesLoading(false);
    }
  };

  const handleTriggerSync = async () => {
    if (currentUser?.role !== 'admin' && currentUser?.role !== 'supervisor') return;
    setAnalysesSyncing(true);
    try {
      const response = await api.post('/call-analyses/sync');
      if (response.data && response.data.success) {
        const result = response.data.data || {};
        showSnackbar(`Senkronizasyon tamamlandı: ${result.uploaded || 0} yeni ses yüklendi, ${result.updated || 0} sonuç güncellendi`, 'success');
        setAnalysesPage(1);
        loadAnalyses({ page: 1 });
      }
    } catch (error: any) {
      console.error('❌ Sync error:', error);
      showSnackbar(error.response?.data?.message || 'Senkronizasyon sırasında hata oluştu', 'error');
    } finally {
      setAnalysesSyncing(false);
    }
  };

  const handleManualUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualUploadFile) {
      showSnackbar('Lütfen bir ses dosyası seçin', 'error');
      return;
    }
    
    setManualUploadLoading(true);
    const formData = new FormData();
    formData.append('file', manualUploadFile);
    if (manualUploadAgentId) formData.append('agentId', manualUploadAgentId);
    if (manualUploadCustomerPhone) {
      if (manualUploadDirection === 'inbound') {
        formData.append('callerNumber', manualUploadCustomerPhone);
      } else {
        formData.append('calledNumber', manualUploadCustomerPhone);
      }
    }
    formData.append('direction', manualUploadDirection);

    try {
      const response = await api.post('/call-analyses/upload', formData, {
        timeout: 180000,
      });

      if (response.data && response.data.success) {
        showSnackbar(response.data.message || 'Ses dosyası başarıyla yüklendi ve analiz sırasına alındı', 'success');
        setManualUploadLoading(false);
        setManualUploadDialogOpen(false);
        setManualUploadFile(null);
        setManualUploadAgentId('');
        setManualUploadCustomerPhone('');
        setManualUploadDirection('inbound');
        setTimeout(() => {
          setAnalysesPage(1);
          loadAnalyses({ page: 1 }).catch((refreshError) => {
            console.warn('⚠️ Analysis list refresh after upload failed:', refreshError);
          });
        }, 300);
      }
    } catch (error: any) {
      console.error('❌ Manual upload error:', error);
      const message = error.code === 'ECONNABORTED'
        ? 'Dosya yükleme ve analiz başlatma işlemi uzun sürdü. Lütfen birazdan analiz listesini yenileyin.'
        : (error.response?.data?.message || 'Dosya yükleme sırasında hata oluştu');
      showSnackbar(message, 'error');
    } finally {
      setManualUploadLoading(false);
    }
  };

  // WhatsApp Şablon Fonksiyonları
  const loadWhatsappTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const data = await whatsappTemplateService.getTemplates();
      setWhatsappTemplates(data);
    } catch (err) {
      console.error('Error loading whatsapp templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleCreateWhatsappTemplate = async () => {
    if (!newTmplName.trim() || !newTmplContent.trim()) {
      showSnackbar('Lütfen şablon adı ve içeriğini doldurun', 'error');
      return;
    }
    try {
      const created = await whatsappTemplateService.createTemplate({
        name: newTmplName.trim(),
        content: newTmplContent.trim()
      });
      if (created) {
        setWhatsappTemplates(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
        setNewTmplName('');
        setNewTmplContent('');
        showSnackbar('Şablon başarıyla oluşturuldu', 'success');
      } else {
        showSnackbar('Şablon oluşturulamadı', 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Şablon oluşturulurken hata oluştu';
      showSnackbar(msg, 'error');
    }
  };

  const handleUpdateWhatsappTemplate = async () => {
    if (!editingTmplId || !editingTmplName.trim() || !editingTmplContent.trim()) {
      showSnackbar('Lütfen şablon adı ve içeriğini doldurun', 'error');
      return;
    }
    try {
      const updated = await whatsappTemplateService.updateTemplate(editingTmplId, {
        name: editingTmplName.trim(),
        content: editingTmplContent.trim()
      });
      if (updated) {
        setWhatsappTemplates(prev => prev.map(t => t._id === editingTmplId ? updated : t).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
        setEditingTmplId(null);
        setEditingTmplName('');
        setEditingTmplContent('');
        showSnackbar('Şablon başarıyla güncellendi', 'success');
      } else {
        showSnackbar('Şablon güncellenemedi', 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Şablon güncellenirken hata oluştu';
      showSnackbar(msg, 'error');
    }
  };

  const handleDeleteWhatsappTemplate = async (id: string) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    try {
      const success = await whatsappTemplateService.deleteTemplate(id);
      if (success) {
        setWhatsappTemplates(prev => prev.filter(t => t._id !== id));
        showSnackbar('Şablon başarıyla silindi', 'success');
      } else {
        showSnackbar('Şablon silinemedi', 'error');
      }
    } catch (err) {
      showSnackbar('Şablon silinirken hata oluştu', 'error');
    }
  };

  const handleInsertPlaceholder = (placeholder: string, isEditing: boolean) => {
    const input = contentInputRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const text = isEditing ? editingTmplContent : newTmplContent;
    const newText = text.substring(0, start) + placeholder + text.substring(end);
    
    if (isEditing) {
      setEditingTmplContent(newText);
    } else {
      setNewTmplContent(newText);
    }

    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  // Email Şablon Fonksiyonları
  const loadEmailTemplates = async () => {
    setLoadingEmailTemplates(true);
    try {
      const data = await emailTemplateService.getTemplates();
      setEmailTemplates(data);
    } catch (err) {
      console.error('Error loading email templates:', err);
    } finally {
      setLoadingEmailTemplates(false);
    }
  };

  const handleCreateEmailTemplate = async () => {
    if (!newEmailTmplName.trim() || !newEmailTmplSubject.trim() || !newEmailTmplContent.trim()) {
      showSnackbar('Lütfen şablon adı, konu ve içeriğini doldurun', 'error');
      return;
    }
    try {
      const created = await emailTemplateService.createTemplate({
        name: newEmailTmplName.trim(),
        subject: newEmailTmplSubject.trim(),
        content: newEmailTmplContent.trim(),
        emailAccountId: newEmailTmplAccountId || null
      });
      if (created) {
        setEmailTemplates(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
        setNewEmailTmplName('');
        setNewEmailTmplSubject('');
        setNewEmailTmplContent('');
        setNewEmailTmplAccountId('');
        showSnackbar('Şablon başarıyla oluşturuldu', 'success');
      } else {
        showSnackbar('Şablon oluşturulamadı', 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Şablon oluşturulurken hata oluştu';
      showSnackbar(msg, 'error');
    }
  };

  const handleUpdateEmailTemplate = async () => {
    if (!editingEmailTmplId || !editingEmailTmplName.trim() || !editingEmailTmplSubject.trim() || !editingEmailTmplContent.trim()) {
      showSnackbar('Lütfen şablon adı, konu ve içeriğini doldurun', 'error');
      return;
    }
    try {
      const updated = await emailTemplateService.updateTemplate(editingEmailTmplId, {
        name: editingEmailTmplName.trim(),
        subject: editingEmailTmplSubject.trim(),
        content: editingEmailTmplContent.trim(),
        emailAccountId: editingEmailTmplAccountId || null
      });
      if (updated) {
        setEmailTemplates(prev => prev.map(t => t._id === editingEmailTmplId ? updated : t).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
        setEditingEmailTmplId(null);
        setEditingEmailTmplName('');
        setEditingEmailTmplSubject('');
        setEditingEmailTmplContent('');
        setEditingEmailTmplAccountId('');
        showSnackbar('Şablon başarıyla güncellendi', 'success');
      } else {
        showSnackbar('Şablon güncellenemedi', 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Şablon güncellenirken hata oluştu';
      showSnackbar(msg, 'error');
    }
  };

  const handleDeleteEmailTemplate = async (id: string) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    try {
      const success = await emailTemplateService.deleteTemplate(id);
      if (success) {
        setEmailTemplates(prev => prev.filter(t => t._id !== id));
        showSnackbar('Şablon başarıyla silindi', 'success');
      } else {
        showSnackbar('Şablon silinemedi', 'error');
      }
    } catch (err) {
      showSnackbar('Şablon silinirken hata oluştu', 'error');
    }
  };

  const handleInsertEmailPlaceholder = (placeholder: string, isEditing: boolean) => {
    const input = emailContentInputRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const text = isEditing ? editingEmailTmplContent : newEmailTmplContent;
    const newText = text.substring(0, start) + placeholder + text.substring(end);
    
    if (isEditing) {
      setEditingEmailTmplContent(newText);
    } else {
      setNewEmailTmplContent(newText);
    }

    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  // E-posta Hesapları Fonksiyonları
  const loadEmailAccounts = async () => {
    setLoadingEmailAccounts(true);
    try {
      const data = await emailAccountService.getAccounts();
      setEmailAccounts(data);
    } catch (err) {
      console.error('Error loading email accounts:', err);
    } finally {
      setLoadingEmailAccounts(false);
    }
  };

  const handleCreateEmailAccount = async () => {
    if (!newEmailAccountName.trim() || !newEmailAccountEmail.trim() || !newEmailAccountHost.trim() || !newEmailAccountUser.trim() || !newEmailAccountPass.trim()) {
      showSnackbar('Lütfen tüm zorunlu alanları doldurun', 'error');
      return;
    }
    try {
      const created = await emailAccountService.createAccount({
        name: newEmailAccountName.trim(),
        email: newEmailAccountEmail.trim(),
        host: newEmailAccountHost.trim(),
        port: Number(newEmailAccountPort),
        secure: newEmailAccountSecure,
        user: newEmailAccountUser.trim(),
        pass: newEmailAccountPass.trim()
      });
      if (created) {
        setEmailAccounts(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
        setNewEmailAccountName('');
        setNewEmailAccountEmail('');
        setNewEmailAccountHost('');
        setNewEmailAccountPort(587);
        setNewEmailAccountSecure(false);
        setNewEmailAccountUser('');
        setNewEmailAccountPass('');
        showSnackbar('E-posta hesabı başarıyla eklendi', 'success');
      } else {
        showSnackbar('E-posta hesabı oluşturulamadı', 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'E-posta hesabı eklenirken hata oluştu';
      showSnackbar(msg, 'error');
    }
  };

  const handleUpdateEmailAccount = async () => {
    if (!editingEmailAccountId || !editingEmailAccountName.trim() || !editingEmailAccountEmail.trim() || !editingEmailAccountHost.trim() || !editingEmailAccountUser.trim() || !editingEmailAccountPass.trim()) {
      showSnackbar('Lütfen tüm zorunlu alanları doldurun', 'error');
      return;
    }
    try {
      const updated = await emailAccountService.updateAccount(editingEmailAccountId, {
        name: editingEmailAccountName.trim(),
        email: editingEmailAccountEmail.trim(),
        host: editingEmailAccountHost.trim(),
        port: Number(editingEmailAccountPort),
        secure: editingEmailAccountSecure,
        user: editingEmailAccountUser.trim(),
        pass: editingEmailAccountPass.trim()
      });
      if (updated) {
        setEmailAccounts(prev => prev.map(a => a._id === editingEmailAccountId ? updated : a).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
        setEditingEmailAccountId(null);
        setEditingEmailAccountName('');
        setEditingEmailAccountEmail('');
        setEditingEmailAccountHost('');
        setEditingEmailAccountPort(587);
        setEditingEmailAccountSecure(false);
        setEditingEmailAccountUser('');
        setEditingEmailAccountPass('');
        showSnackbar('E-posta hesabı başarıyla güncellendi', 'success');
      } else {
        showSnackbar('E-posta hesabı güncellenemedi', 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'E-posta hesabı güncellenirken hata oluştu';
      showSnackbar(msg, 'error');
    }
  };

  const handleDeleteEmailAccount = async (id: string) => {
    if (!window.confirm('Bu e-posta hesabını silmek istediğinize emin misiniz? Bu hesaba bağlı şablonlar hesapsız kalacaktır.')) return;
    try {
      const success = await emailAccountService.deleteAccount(id);
      if (success) {
        setEmailAccounts(prev => prev.filter(a => a._id !== id));
        loadEmailTemplates();
        showSnackbar('E-posta hesabı başarıyla silindi', 'success');
      } else {
        showSnackbar('E-posta hesabı silinemedi', 'error');
      }
    } catch (err) {
      showSnackbar('E-posta hesabı silinirken hata oluştu', 'error');
    }
  };

  const handleTestEmailAccount = async (isEditing: boolean) => {
    const host = isEditing ? editingEmailAccountHost : newEmailAccountHost;
    const port = isEditing ? editingEmailAccountPort : newEmailAccountPort;
    const secure = isEditing ? editingEmailAccountSecure : newEmailAccountSecure;
    const user = isEditing ? editingEmailAccountUser : newEmailAccountUser;
    const pass = isEditing ? editingEmailAccountPass : newEmailAccountPass;

    if (!host.trim() || !user.trim() || !pass.trim()) {
      showSnackbar('Lütfen host, kullanıcı adı ve şifre bilgilerini girin', 'warning');
      return;
    }

    setTestingSmtp(true);
    try {
      const result = await emailAccountService.testConnection({
        host: host.trim(),
        port: Number(port),
        secure,
        user: user.trim(),
        pass: pass.trim()
      });
      if (result.success) {
        showSnackbar(result.message, 'success');
      } else {
        showSnackbar(result.message, 'error');
      }
    } catch (err: any) {
      showSnackbar(err.message || 'Bağlantı testi başarısız oldu', 'error');
    } finally {
      setTestingSmtp(false);
    }
  };


  // Calls/Analyses sekmesine girince yükle
  useEffect(() => {
    if (activeSection === 'analyses' && (currentUser?.role === 'admin' || currentUser?.role === 'supervisor')) {
      setAnalysesPage(1);
      loadAnalyses({ page: 1 });
    }
    if (activeSection === 'calls') {
      setCallsPage(1);
      loadCalls({ page: 1 });
    }
    if (activeSection === 'messages') {
      loadUsers();
      loadSupervisorConversations();
    }
    if (activeSection === 'whatsapp') {
      loadWhatsappTemplates();
    }
    if (activeSection === 'email') {
      loadEmailTemplates();
      loadEmailAccounts();
    }
    if (activeSection === 'analyses') {
      fetchAnalysisFilterOptions();
    }
    if (activeSection === 'email-accounts') {
      loadEmailAccounts();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Sayfa veya filtreler değişince analizleri yükle
  useEffect(() => {
    if (activeSection === 'analyses' && (currentUser?.role === 'admin' || currentUser?.role === 'supervisor')) {
      loadAnalyses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysesPage, filterStatus, filterSentiment, filterAgentId, filterQueueId, filterDateRange, filterStartDate, filterEndDate]);

  useEffect(() => {
    if (activeSection !== 'analyses' || (currentUser?.role !== 'admin' && currentUser?.role !== 'supervisor')) {
      return;
    }

    const interval = window.setInterval(() => {
      loadAnalyses({ silent: true });
    }, 3000);

    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, analysesPage, filterStatus, filterSentiment, filterAgentId, filterQueueId, filterDateRange, filterStartDate, filterEndDate, filterCallerNumber, currentUser?.role]);

  // Sayfa veya limit değişince yeniden yükle
  useEffect(() => {
    if (activeSection === 'calls') {
      loadCalls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callsPage, callsLimit]);

  const handleCloseModal = () => {
    setIsPerformanceModalOpen(false);
    setSelectedAgent(null);
    setAgentPerformanceData(null);
    setAgentCallHistory([]);
    setAgentStatusDurations([]);
    setAgentQualityData({
      dailyAverage: 0,
      weeklyAverage: 0,
      monthlyAverage: 0,
      totalAnalyzed: 0
    });
  };

  // ✨ Update status counts from current agentsData
  const updateStatusCounts = () => {
    const statusCountsMap: Record<string, number> = {};

    // Initialize all statuses with 0 count
    agentStatuses.forEach((status: any) => {
      if (status.isActive !== false) {
        statusCountsMap[status.name] = 0;
      }
    });

    // Count agents by status
    agentsData.forEach((agent: any) => {
      const statusName = agent.status || 'Not Answering';
      if (statusCountsMap.hasOwnProperty(statusName)) {
        statusCountsMap[statusName]++;
      } else {
        // If status not found in database, add it with default color
        statusCountsMap[statusName] = (statusCountsMap[statusName] || 0) + 1;
      }
    });

    // ✨ Build status data array with colors from database
    const statusDataArray = Object.entries(statusCountsMap)
      .map(([statusName, count]) => {
        const statusInfo = agentStatuses.find((s: any) => s.name === statusName);
        return {
          label: statusName,
          count: count as number,
          color: statusInfo?.color || getStatusColor(statusName)
        };
      })
      .filter((item) => item.count > 0) // Only show statuses with agents
      .sort((a, b) => b.count - a.count); // Sort by count descending

    setStatusData(statusDataArray);
  };

  // ✨ Load live monitoring data
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }

    const loadData = async (showLoader = false) => {
      if (dashboardLoadInFlightRef.current) return;
      dashboardLoadInFlightRef.current = true;

      try {
        if (showLoader) {
          setLoading(true);
        }

        // ✨ Fetch agent statuses from database
        const statusesResponse = await reportsAPI.getAgentStatuses();
        const statuses = statusesResponse?.data?.data || statusesResponse?.data || [];
        const statusesList = Array.isArray(statuses) ? statuses : [];
        setAgentStatuses(statusesList);
        console.log('📊 Supervisor Dashboard: Agent statuses:', statusesList);

        // Fetch active agents - yeni agent-stats API'sinden gelen veri
        const agentsStatsResponse = await reportsAPI.getActiveAgents();
        const agentStats = agentsStatsResponse?.data?.data || agentsStatsResponse?.data || [];
        const agentStatsList = Array.isArray(agentStats) ? agentStats : [];

        // Fetch gerçek agent bilgileri (status, queue, vs) - eski endpoint
        const agentsInfoResponse = await api.get('/reports/agents');
        const agentsInfo = agentsInfoResponse?.data?.data || agentsInfoResponse?.data || [];
        const agentsInfoList = Array.isArray(agentsInfo) ? agentsInfo : [];

        console.log('📊 Supervisor Dashboard: Agent stats:', agentStatsList.length);
        console.log('📊 Supervisor Dashboard: Agent info:', agentsInfoList.length);

        // ✨ Tüm agent kullanıcılarını set et (filtreleme için)
        setAllAgents(agentsInfoList);

        // Transform agents data for display - stats ve info'yu birleştir
        const transformedAgents = agentsInfoList.map((agent: any) => {
          const agentId = agent._id;

          // Bu agent için stats bul
          const stat = agentStatsList.find((s: any) => s.agentId === agentId);

          // Stats verilerini al
          const totalCalls = stat?.totalCalls || 0;
          const incomingCalls = stat?.incomingCalls || 0;
          const outgoingCalls = stat?.outgoingCalls || 0;
          const totalDuration = stat?.totalDuration || 0;
          const avgCallDuration = stat?.avgCallDuration || 0;

          // Debug log
          console.log(`📊 Agent ${agent.firstName} ${agent.lastName}:`, {
            totalCalls,
            incomingCalls,
            outgoingCalls,
            stat
          });

          const totalCallDurationStr = totalDuration > 0
            ? `${Math.floor(totalDuration / 60)}:${String(totalDuration % 60).padStart(2, '0')}`
            : '00:00';

          // Status bilgisi
          const statusName = agent.currentStatusId?.name || 'Not Answering';

          // Mevcut agentsData'dan statusStartTime'ı al (eğer statü aynıysa)
          const existingAgent = agentsData.find((a: any) => a.id === agentId);
          let statusStartTime: Date;

          if (existingAgent && existingAgent.status === statusName && existingAgent.statusStartTime) {
            statusStartTime = existingAgent.statusStartTime;
          } else {
            statusStartTime = agent.statusStartTime ? new Date(agent.statusStartTime) : new Date();
          }

          const statusChangeTime = statusStartTime.toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });

          const now = new Date();
          const diffSeconds = Math.floor((now.getTime() - statusStartTime.getTime()) / 1000);
          const minutes = Math.floor(diffSeconds / 60);
          const seconds = diffSeconds % 60;
          const durationStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

          // Queue bilgisi
          const queueDisplay = agent.currentCall?.queueName
            || agent.selectedQueueName
            || (agent.queueIds && agent.queueIds.length > 0
              ? agent.queueIds.map((q: any) => q?.name || q?.extension).filter(Boolean).join(', ')
              : '-');

          return {
            id: agentId,
            name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || 'Unknown',
            avatar: (agent.firstName?.[0] || agent.email?.[0] || 'A').toUpperCase(),
            profilePicture: agent.profilePicture,
            extension: agent.extension || '-',
            status: statusName,
            duration: durationStr,
            statusStartTime: statusStartTime,
            statusChangeTime: statusChangeTime,
            queue: queueDisplay,
            callDirection: agent.callDirection || null,
            incomingCalls: incomingCalls,
            outgoingCalls: outgoingCalls,
            totalCalls: totalCalls,
            totalCallDuration: totalCallDurationStr,
            avgCallDuration: avgCallDuration,
            agent: agent
          };
        });

        setAgentsData(transformedAgents);

        // ✨ Calculate status counts dynamically from all statuses
        const statusCountsMap: Record<string, number> = {};

        // Initialize all statuses with 0 count
        statusesList.forEach((status: any) => {
          if (status.isActive !== false) {
            statusCountsMap[status.name] = 0;
          }
        });

        // Count agents by status
        transformedAgents.forEach((agent: any) => {
          const statusName = agent.status || 'Not Answering';
          if (statusCountsMap.hasOwnProperty(statusName)) {
            statusCountsMap[statusName]++;
          } else {
            // If status not found in database, add it with default color
            statusCountsMap[statusName] = (statusCountsMap[statusName] || 0) + 1;
          }
        });

        // ✨ Build status data array with colors from database
        const statusDataArray = Object.entries(statusCountsMap)
          .map(([statusName, count]) => {
            const statusInfo = statusesList.find((s: any) => s.name === statusName);
            return {
              label: statusName,
              count: count as number,
              color: statusInfo?.color || getStatusColor(statusName)
            };
          })
          .filter((item) => item.count > 0) // Only show statuses with agents
          .sort((a, b) => b.count - a.count); // Sort by count descending

        setStatusData(statusDataArray);

        // Fetch queue performance
        // ✨ Bugünün tarihi için tarih parametreleri gönder
        const todayIso = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const startOfDay = `${todayIso}T00:00:00.000Z`;
        const endOfDay = `${todayIso}T23:59:59.999Z`;
        const queueResponse = await reportsAPI.getQueuePerformance(startOfDay, endOfDay);
        // ✨ Axios response format: response.data = { success: true, data: {...} }
        const queues = queueResponse?.data?.data || queueResponse?.data || [];
        const queueList = Array.isArray(queues) ? queues : [];

        // ✨ Tüm aktif kuyrukları set et (filtreleme için)
        setAllQueues(queueList);

        // Transform queue data for display
        const transformedQueues = queueList.map((queue: any) => ({
          name: queue.name || queue.queueName || '-',
          total: queue.statistics?.totalCalls || queue.total || 0,
          answered: queue.statistics?.answeredCalls || queue.answered || 0,
          missed: queue.statistics?.missedCalls || queue.missed || 0,
          sl1: queue.statistics?.successRate || 0,
          queue: queue
        }));

        setQueueData(transformedQueues);

        // ✨ Fetch active calls
        const activeCallsResponse = await callAPI.getActive();
        const activeCallsList = activeCallsResponse?.data?.data || activeCallsResponse?.data || [];
        const activeCallsArray = Array.isArray(activeCallsList) ? activeCallsList
          .filter((call: any) => call.queueId) // Kuyruksuz çağrıları gösterme
          .map((call: any) => {
            // Extract agent ID if it's an object
            const agentId = call.agentId ? (typeof call.agentId === 'object' ? call.agentId._id : call.agentId) : undefined;
            return {
              ...call,
              agentId: agentId,
              agentChannel: call.channel, // Map channel to agentChannel
              agentExtension: call.agentId?.extension || call.agentExtension
            };
          }) : [];
        console.log('📞 Supervisor Dashboard: Active calls:', activeCallsArray);
        setActiveCalls(activeCallsArray);

      } catch (error) {
        console.error('❌ Error loading supervisor dashboard data:', error);
      } finally {
        dashboardLoadInFlightRef.current = false;
        setLoading(false);
        // Sayfa yüklendiğinde animasyonu başlat
        if (showLoader) {
          setTimeout(() => {
            setIsLoaded(true);
          }, 100);
        }
      }
    };

    void loadData(true);

    // ✨ Socket.IO connection for live updates
    const socketUrl = SOCKET_URL;
    const newSocket = io(socketUrl);
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('🔌 Supervisor Dashboard: Socket connected:', newSocket.id);
      // Join supervisor room to receive updates
      newSocket.emit('join-supervisor-room');
      console.log('✅ Supervisor Dashboard: Joined supervisor room');
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Supervisor Dashboard: Socket disconnected');
    });

    // ✨ Listen for agent status changes (correct event names)
    newSocket.on('agent-status-change', (data: any) => {
      console.log('🔄 Supervisor Dashboard: Agent status changed:', data);
      // Update agent data in real-time
      if (data.agentId) {
        setAgentsData(prevAgents => prevAgents.map(agent => {
          if (agent.id === data.agentId || agent.agent?._id === data.agentId) {
            // ✨ Statü değiştiğinde statusStartTime'ı güncelle (yeni statü başlangıcı)
            const newStatusStartTime = data.timestamp ? new Date(data.timestamp) : new Date();
            return {
              ...agent,
              status: data.newStatusName || data.statusName || agent.status,
              statusStartTime: newStatusStartTime, // ✨ Yeni statü başlangıç zamanı
              // ✨ Update new fields from WebSocket
              callDirection: data.callDirection || null,
              queue: data.queueName || agent.queue,
              incomingCalls: data.incomingCalls ?? agent.incomingCalls,
              outgoingCalls: data.outgoingCalls ?? agent.outgoingCalls,
              totalCallDuration: data.totalCallDuration || agent.totalCallDuration,
              agent: {
                ...agent.agent,
                currentStatusId: data.newStatusId ? { name: data.newStatusName } : agent.agent?.currentStatusId
              }
            };
          }
          return agent;
        }));

        // ✨ Status counts'u güncellemek için status data'yı yeniden hesapla
        updateStatusCounts();
      }
    });

    // Also listen for agentStatusUpdated event (for compatibility)
    newSocket.on('agentStatusUpdated', (data: any) => {
      console.log('🔄 Supervisor Dashboard: Agent status updated:', data);
      if (data.agentId) {
        // ✨ Real-time update without reloading all data
        setAgentsData(prevAgents => prevAgents.map(agent => {
          if (agent.id === data.agentId || agent.agent?._id === data.agentId) {
            const newStatusStartTime = data.timestamp ? new Date(data.timestamp) : new Date();
            return {
              ...agent,
              status: data.statusName || agent.status,
              statusStartTime: newStatusStartTime,
              // ✨ Update new fields from WebSocket
              callDirection: data.callDirection || null,
              queue: data.queueName || agent.queue,
              incomingCalls: data.incomingCalls ?? agent.incomingCalls,
              outgoingCalls: data.outgoingCalls ?? agent.outgoingCalls,
              totalCallDuration: data.totalCallDuration || agent.totalCallDuration,
              agent: {
                ...agent.agent,
                currentStatusId: data.statusId ? { name: data.statusName } : agent.agent?.currentStatusId
              }
            };
          }
          return agent;
        }));
        updateStatusCounts();
      }
    });

    // ✨ Listen for agent queue changes
    newSocket.on('agent-queue-changed', (data: any) => {
      console.log('🔄 Supervisor Dashboard: Agent queue changed:', data);
      if (data.agentId) {
        setAgentsData(prevAgents => prevAgents.map(agent => {
          if (agent.id === data.agentId || agent.agent?._id === data.agentId) {
            return {
              ...agent,
              queue: data.queueName || '-'
            };
          }
          return agent;
        }));
      }
    });

    // ✨ Listen for incoming calls
    newSocket.on('incoming-call', (data: any) => {
      console.log('📞 Supervisor Dashboard: Incoming call:', data);
      // Kuyruksuz çağrıları ekleme
      if (!data.queueId) return;
      // Add to active calls if not already present
      setActiveCalls(prevCalls => {
        const exists = prevCalls.some(call => call._id === data.callId || call.id === data.callId);
        if (!exists && data.callId) {
          return [...prevCalls, {
            _id: data.callId,
            callerNumber: data.callerNumber,
            calledNumber: data.calledNumber || '',
            callerName: data.callerName || '',
            status: 'ringing',
            direction: 'incoming',
            startTime: data.timestamp || new Date().toISOString(),
            queueId: data.queueId,
            agentId: data.agentId
          }];
        }
        return prevCalls;
      });
    });

    // ✨ Listen for new messages (supervisor sees all)
    newSocket.on('new-message', (data: any) => {
      console.log('💬 Supervisor: New message detected:', data);
      // Reload conversations if on messages tab
      if (activeSection === 'messages') {
        loadSupervisorConversations();

        // If viewing this conversation, add message immediately
        if (selectedConversation) {
          const user1 = selectedConversation.lastMessage?.senderId?._id;
          const user2 = selectedConversation.lastMessage?.receiverId?._id;
          if (user1 && user2 &&
            (data.senderId === user1 || data.senderId === user2 ||
              data.receiverId === user1 || data.receiverId === user2)) {
            // Add message to current conversation immediately
            setMessages(prev => {
              // Check if message already exists (avoid duplicates)
              if (prev.some(msg => msg._id === data.messageId)) {
                return prev;
              }
              // Add new message
              return [...prev, {
                _id: data.messageId,
                senderId: { _id: data.senderId },
                receiverId: { _id: data.receiverId },
                content: data.content,
                isRead: false,
                createdAt: new Date().toISOString()
              }];
            });
          }
        }
      }
    });

    // ✨ Listen for call answered
    newSocket.on('call-answered', (data: any) => {
      console.log('📞 Supervisor Dashboard: Call answered:', data);
      // Update active calls - extract ID from agentId if it's an object
      const agentId = typeof data.agentId === 'object' ? data.agentId._id : data.agentId;
      setActiveCalls(prevCalls => prevCalls.map(call =>
        (call._id === data.callId || call.id === data.callId) ? {
          ...call,
          status: 'answered',
          agentId: agentId,
          agentChannel: data.agentChannel,
          agentExtension: data.agentExtension,
          answerTime: new Date().toISOString()
        } : call
      ));
    });

    // ✨ Listen for call ended
    newSocket.on('call-ended', (data: any) => {
      console.log('📞 Supervisor Dashboard: Call ended:', data);
      // Remove from active calls
      setActiveCalls(prevCalls => prevCalls.filter(call => call._id !== data?.callId && call.id !== data?.callId));
    });

    // ✨ Listen for call status updates
    newSocket.on('call-status-update', (data: any) => {
      console.log('📞 Supervisor Dashboard: Call status update:', data);
      if (data.status === 'ended') {
        setActiveCalls(prevCalls => prevCalls.filter(call => call._id !== data.callId));
      } else {
        // Extract agent ID if it's an object
        const agentId = data.agentId ? (typeof data.agentId === 'object' ? data.agentId._id : data.agentId) : undefined;
        setActiveCalls(prevCalls => prevCalls.map(call =>
          call._id === data.callId ? {
            ...call,
            status: data.status,
            ...(agentId && { agentId }),
            ...(data.agentChannel && { agentChannel: data.agentChannel }),
            ...(data.agentExtension && { agentExtension: data.agentExtension })
          } : call
        ));
      }
    });

    // Refresh data every 30 seconds
    const refreshInterval = setInterval(() => {
      void loadData(false);
    }, 30000);

    // ✨ Duration updates are now handled by CallDurationTimer component
    // Removed durationUpdateInterval to prevent unnecessary re-renders

    return () => {
      clearInterval(refreshInterval);
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [navigate]);

  const parsedSummaryText = (summaryObj: any) => {
    if (!summaryObj) return 'Özet bilgisi bulunamadı.';
    if (typeof summaryObj === 'string') return summaryObj;

    // Check for standard summary fields
    if (summaryObj.summary) return summaryObj.summary;
    if (summaryObj.text_summary) return summaryObj.text_summary;
    if (summaryObj.general_summary) return summaryObj.general_summary;
    if (summaryObj.fallback_summary) return summaryObj.fallback_summary;

    // Format strengths and weaknesses if available
    const parts = [];
    if (summaryObj.strengths && Array.isArray(summaryObj.strengths) && summaryObj.strengths.length > 0) {
      parts.push("💪 Temsilcinin Güçlü Yönleri:\n" + summaryObj.strengths.map((s: string) => `• ${s}`).join('\n'));
    }
    if (summaryObj.weaknesses && Array.isArray(summaryObj.weaknesses) && summaryObj.weaknesses.length > 0) {
      parts.push("📈 Gelişime Açık Yönler:\n" + summaryObj.weaknesses.map((w: string) => `• ${w}`).join('\n'));
    }

    if (parts.length > 0) {
      return parts.join('\n\n');
    }

    // Fallback message
    if (summaryObj.overall_score !== undefined) {
      return `Çağrı Değerlendirmesi: Kalite Puanı ${summaryObj.overall_score}/${summaryObj.max_score || 100}. Detaylı kriter değerlendirmeleri için 'Kalite Puanlama' sekmesini inceleyebilirsiniz.`;
    }

    return JSON.stringify(summaryObj);
  };

  // 1. / 2. analiz karsilastirmasi icin tam kaydi ceker (liste yaniti transkript/ozet tasimaz)
  const loadAnalizKarsilastirma = async (analysisId: string) => {
    try {
      const response = await api.get(`/call-analyses/${analysisId}`);
      const detay = response?.data?.data;
      setSelectedAnalysis((onceki: any) => (
        onceki && onceki._id === analysisId
          ? ({ ...onceki, ...(detay || {}), _karsilastirmaYuklendi: true, _karsilastirmaHata: !detay } as any)
          : onceki
      ));
    } catch (e) {
      console.error('Analiz karsilastirma detayi alinamadi:', e);
      setSelectedAnalysis((onceki: any) => (
        onceki && onceki._id === analysisId ? ({ ...onceki, _karsilastirmaHata: true } as any) : onceki
      ));
    }
  };

  const parseAnalysisSummary = (summary: any) => {
    if (!summary) return null;
    if (typeof summary === 'object') return summary;
    try {
      return JSON.parse(summary);
    } catch (e) {
      return null;
    }
  };

  const getCriticalPenalties = (summaryObj: any) => {
    if (!summaryObj || !Array.isArray(summaryObj.penalties)) return [];
    return summaryObj.penalties.filter((p: any) => p?.occurred && p?.is_critical);
  };

  const getCriteriaGaps = (summaryObj: any, limit = 3) => {
    if (!summaryObj || !Array.isArray(summaryObj.criteria)) return [];
    return summaryObj.criteria
      .map((c: any) => ({
        ...c,
        lost: Math.max(0, Number(c.max || 0) - Number(c.earned || 0))
      }))
      .filter((c: any) => c.lost > 0)
      .sort((a: any, b: any) => b.lost - a.lost)
      .slice(0, limit);
  };

  // 1. / 2. analiz puan hucresi. Resmi skor ("Kalite Skoru") ikisinden yuksek olanidir.
  const renderAnalizPuani = (analysis: any, tur: 1 | 2) => {
    if (!analysis || analysis.status !== 'completed') return '-';
    const ikinci = analysis.secondAnalysis;
    const ikinciTamam = !!ikinci && ikinci.status === 'completed';
    const gecerliMi = ikinciTamam && Number(analysis.officialSource || 1) === tur;
    const puanRengi = (p: number) => (p >= 80 ? '#10b981' : (p >= 50 ? '#f59e0b' : '#ef4444'));
    const puanGoster = (ham: any) => {
      const p = Number(ham || 0);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: gecerliMi ? 800 : 500, color: puanRengi(p) }}>
            {p}
          </Typography>
          <Typography variant="caption" color="textSecondary">/100</Typography>
          {gecerliMi && (
            <Chip size="small" label="Geçerli" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />
          )}
        </Box>
      );
    };
    // Puanlanamayan cagri (erken kapanma, bos kayit, IVR): 0 puan gibi gosterilmez
    if (analysis.notEvaluable) {
      return tur === 1
        ? <Chip size="small" variant="outlined" label="Değerlendirilemedi" sx={{ height: 20, fontSize: '0.65rem' }} />
        : <Typography variant="caption" color="textSecondary">-</Typography>;
    }
    if (tur === 1) {
      const ilkPuan = analysis.firstAnalysis && analysis.firstAnalysis.score !== undefined && analysis.firstAnalysis.score !== null
        ? analysis.firstAnalysis.score
        : analysis.score;
      return puanGoster(ilkPuan);
    }
    if (!ikinci || !ikinci.status) {
      return <Typography variant="caption" color="textSecondary">-</Typography>;
    }
    if (ikinci.status === 'pending' || ikinci.status === 'processing') {
      return <Chip size="small" color="warning" variant="outlined" label="Analiz ediliyor" />;
    }
    if (ikinciTamam) {
      return puanGoster(ikinci.score);
    }
    return (
      <Tooltip title={ikinci.errorMessage || ''}>
        <Typography variant="caption" color={ikinci.status === 'failed' ? 'error' : 'textSecondary'}>
          {ikinci.status === 'failed' ? 'Başarısız' : 'Yapılamadı'}
        </Typography>
      </Tooltip>
    );
  };

  const getQualityDecision = (analysis: any) => {
    if (!analysis || analysis.status !== 'completed') {
      return { label: 'Bekliyor', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' };
    }
    const summaryObj = parseAnalysisSummary(analysis.summary);
    const hasCritical = getCriticalPenalties(summaryObj).length > 0;
    if (hasCritical) {
      return { label: 'Kritik Hata', color: '#991b1b', bg: '#fef2f2', border: '#fecaca' };
    }
    if (Number(analysis.score || 0) >= 80) {
      return { label: 'Geçti', color: '#047857', bg: '#ecfdf5', border: '#a7f3d0' };
    }
    return { label: 'Kaldı', color: '#b45309', bg: '#fffbeb', border: '#fde68a' };
  };

  const analysesPageInsights = useMemo(() => {
    const completed = analysesData.filter((analysis: any) => analysis.status === 'completed');
    const passed = completed.filter((analysis: any) => Number(analysis.score || 0) >= 80).length;
    const failedQuality = completed.length - passed;
    let criticalCount = 0;
    const gapMap: Record<string, { name: string; lost: number; count: number }> = {};

    completed.forEach((analysis: any) => {
      const summaryObj = parseAnalysisSummary(analysis.summary);
      if (getCriticalPenalties(summaryObj).length > 0) criticalCount += 1;
      getCriteriaGaps(summaryObj, 16).forEach((gap: any) => {
        const key = String(gap.id || gap.name);
        if (!gapMap[key]) {
          gapMap[key] = { name: gap.name || `Kriter ${gap.id}`, lost: 0, count: 0 };
        }
        gapMap[key].lost += gap.lost;
        gapMap[key].count += 1;
      });
    });

    const topGaps = Object.values(gapMap)
      .sort((a, b) => b.lost - a.lost)
      .slice(0, 3);

    return {
      completedCount: completed.length,
      passed,
      failedQuality,
      criticalCount,
      passRate: completed.length ? Math.round((passed / completed.length) * 100) : 0,
      topGaps
    };
  }, [analysesData]);

  // ✨ Memoize Agents Table to prevent unnecessary re-renders when agents data hasn't changed
  const agentsTableJSX = useMemo(() => {
    return (
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 900, color: "#374151", fontSize: "0.75rem" }}>Agent</TableCell>
              <TableCell sx={{ fontWeight: 900, color: "#374151", fontSize: "0.75rem" }}>Durum</TableCell>
              <TableCell sx={{ fontWeight: 900, color: "#374151", fontSize: "0.75rem" }}>Süresi</TableCell>
              <TableCell sx={{ fontWeight: 900, color: "#374151", fontSize: "0.75rem" }}>Kuyruk</TableCell>
              <TableCell sx={{ fontWeight: 900, color: "#374151", fontSize: "0.75rem" }}>Çağrı Yönü</TableCell>
              <TableCell sx={{ fontWeight: 900, color: "#374151", fontSize: "0.75rem" }}>Gelen Arama</TableCell>
              <TableCell sx={{ fontWeight: 900, color: "#374151", fontSize: "0.75rem" }}>Giden Arama</TableCell>
              <TableCell sx={{ fontWeight: 900, color: "#374151", fontSize: "0.75rem" }}>Toplam Arama</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agentsData.map((agent) => (
              <TableRow
                key={agent.id}
                hover
                sx={{
                  cursor: "default",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#f0f9ff",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                  },
                  "&:nth-of-type(even)": { bgcolor: "#f8fafc" },
                  borderBottom: "1px solid #e5e7eb"
                }}
              >
                <TableCell sx={{ fontWeight: 500, color: "#374151", fontSize: "0.8rem" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* Üç nokta menü butonu - isim ve avatarın solunda */}
                    <IconButton
                      size="small"
                      onClick={(e) => openAgentMenu(e, agent)}
                      sx={{ mr: 0.5 }}
                      aria-label="Ajan menüsü"
                    >
                      <IconDotsVertical size={18} />
                    </IconButton>
                    <Avatar sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.7rem",
                      bgcolor: getStatusColor(agent.status)
                    }}>
                      {agent.profilePicture ? (
                        <Box component="img" src={agent.profilePicture} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      ) : (
                        <Typography sx={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'rgba(255,255,255,0.5)' }}>{agent.firstName?.[0] || 'A'}</Typography>
                      )}
                    </Avatar>
                    {agent.name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={agent.status}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(agent.status),
                      color: "white",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      borderRadius: 1,
                      boxShadow: `0 2px 8px ${getStatusColor(agent.status)}40`,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#6b7280", fontSize: "0.8rem" }}>
                  <CallDurationTimer startTime={agent.statusStartTime} />
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#6b7280", fontSize: "0.8rem" }}>
                  {agent.queue || '-'}
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#6b7280", fontSize: "0.8rem" }}>
                  {agent.callDirection || '-'}
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#3b82f6", fontSize: "0.8rem" }}>
                  {agent.incomingCalls || 0}
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#8b5cf6", fontSize: "0.8rem" }}>
                  {agent.outgoingCalls || 0}
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#10b981", fontSize: "0.8rem" }}>
                  {(agent.incomingCalls || 0) + (agent.outgoingCalls || 0)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [agentsData, agentStatuses]);

  // ✨ Memoize Active Calls Table to prevent unnecessary re-renders when active calls data hasn't changed
  const activeCallsTableJSX = useMemo(() => {
    const getStatusLabel = (status: string) => {
      switch (status) {
        case 'ringing': return 'Çalıyor';
        case 'answered': return 'Yanıtlandı';
        case 'waiting': return 'Bekliyor';
        default: return status || 'Bilinmiyor';
      }
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'ringing': return '#f59e0b';
        case 'answered': return '#10b981';
        case 'waiting': return '#3b82f6';
        default: return '#6b7280';
      }
    };

    return (
      <TableContainer sx={{ maxHeight: "280px", overflow: "auto" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem" }}>Arayan</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem" }}>Temsilci</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem" }}>Kuyruk</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem" }}>Durum</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem" }}>Süre</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeCalls.map((call: any) => (
              <TableRow
                key={call._id || call.id}
                sx={{
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#f0f9ff",
                    transform: "translateY(-1px)",
                  },
                  "&:nth-of-type(even)": { bgcolor: "#f8fafc" },
                  borderBottom: "1px solid #e5e7eb"
                }}
              >
                <TableCell sx={{ fontWeight: 500, color: "#374151", fontSize: "0.8rem" }}>
                  {call?.direction === 'outgoing' ? (call.calledNumber || '-') : (call.callerNumber || '-')}
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#374151", fontSize: "0.8rem" }}>
                  {call.agentId
                    ? (typeof call.agentId === 'object'
                      ? `${call.agentId.firstName || ''} ${call.agentId.lastName || ''}`.trim() || call.agentId.email || '-'
                      : '-')
                    : '-'}
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#374151", fontSize: "0.8rem" }}>
                  {call.queueId
                    ? (typeof call.queueId === 'object'
                      ? call.queueId.name || '-'
                      : '-')
                    : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(call.status)}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(call.status),
                      color: "white",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      borderRadius: 1,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: "#10b981", fontSize: "0.8rem" }}>
                  <CallDurationTimer startTime={call.startTime} answerTime={call.answerTime} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [activeCalls]);

  const totalAgents = statusData.reduce((sum, status) => sum + status.count, 0);

  if (loading && agentsData.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        fontFamily: "'Axiforma', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        {/* Sidebar */}
        <audio
          id="remoteAudio"
          autoPlay
          playsInline
          style={{ display: 'none' }}
          onLoadedMetadata={() => console.log('🔊 Remote audio loaded')}
          onError={(e) => console.error('❌ Remote audio error:', e)}
          onPlay={() => console.log('▶️ Remote audio started playing')}
          onPause={() => console.log('⏸️ Remote audio paused')}
          onCanPlay={() => console.log('🎵 Remote audio can play')}
          onLoadStart={() => console.log('🔄 Remote audio load started')}
          onVolumeChange={(e) => console.log('🔊 Volume:', (e.target as HTMLAudioElement).volume)}
        />

        {/* Additional audio elements from agent-p */}
        <audio autoPlay></audio>
        <audio autoPlay></audio>
        <Box
          sx={{
            width: "130px",
            bgcolor: "#1e3a8a",
            backdropFilter: "blur(10px)",
            boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
            borderRight: "1px solid rgba(255, 255, 255, 0.1)",
            py: 1.5,
            px: 1.2,
            display: "flex",
            flexDirection: "column",
            position: "sticky",
            top: 0,
            height: "100vh",
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
            <Box
              component="img"
              src="/percrm-logo.svg"
              alt="PerCRM"
              sx={{
                width: '80px',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1, mt: 0, px: 0.5 }}>
              <Box onClick={() => setActiveSection('dashboard')} sx={{ cursor: 'pointer', width: '100%' }}>
                <NavItem icon={<IconUsers size={26} color={activeSection === 'dashboard' ? '#ffffff' : '#93c5fd'} />} label="Dashboard" active={activeSection === 'dashboard'} />
              </Box>
              <Box onClick={() => setActiveSection('calls')} sx={{ cursor: 'pointer', width: '100%' }}>
                <NavItem icon={<IconHeadset size={26} color={activeSection === 'calls' ? '#ffffff' : '#93c5fd'} />} label="Çağrılar" active={activeSection === 'calls'} />
              </Box>
              <Box onClick={() => setActiveSection('reports')} sx={{ cursor: 'pointer', width: '100%' }}>
                <NavItem icon={<IconPercentage size={26} color={activeSection === 'reports' ? '#ffffff' : '#93c5fd'} />} label="Raporlar" active={activeSection === 'reports'} />
              </Box>
              <Box onClick={() => setActiveSection('support')} sx={{ cursor: 'pointer', width: '100%' }}>
                <NavItem icon={<IconHelp size={26} color={activeSection === 'support' ? '#ffffff' : '#93c5fd'} />} label="Destek" active={activeSection === 'support'} />
              </Box>
              <Box onClick={() => setActiveSection('messages')} sx={{ cursor: 'pointer', width: '100%' }}>
                <NavItem icon={<IconSend size={26} color={activeSection === 'messages' ? '#ffffff' : '#93c5fd'} />} label="Mesajlar" active={activeSection === 'messages'} />
              </Box>
              <Box onClick={() => setActiveSection('campaigns')} sx={{ cursor: 'pointer', width: '100%' }}>
                <NavItem icon={<IconSpeakerphone size={26} color={activeSection === 'campaigns' ? '#ffffff' : '#93c5fd'} />} label="Dialer" active={activeSection === 'campaigns'} />
              </Box>
              <Box onClick={() => setActiveSection('appointments')} sx={{ cursor: 'pointer', width: '100%' }}>
                <NavItem icon={<IconCalendarEvent size={26} color={activeSection === 'appointments' ? '#ffffff' : '#93c5fd'} />} label="Randevular" active={activeSection === 'appointments'} />
              </Box>

              {/* Şablonlar grubu */}
              <Box
                onClick={() => setTemplatesGroupOpen(o => !o)}
                sx={{
                  cursor: 'pointer', width: '100%',
                  p: '10px 8px',
                  borderRadius: 2,
                  bgcolor: (['whatsapp','email','email-accounts'] as any[]).includes(activeSection)
                    ? 'rgba(255,255,255,0.25)'
                    : 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4,
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' }
                }}
              >
                <IconTemplate size={26} color={(['whatsapp','email','email-accounts'] as any[]).includes(activeSection) ? '#ffffff' : '#93c5fd'} />
                <Typography variant="caption" sx={{ color: 'white', fontSize: '0.72rem', fontWeight: 500, lineHeight: 1.3, textAlign: 'center' }}>
                  Şablonlar
                </Typography>
                {templatesGroupOpen
                  ? <IconChevronUp size={12} color="#93c5fd" />
                  : <IconChevronDown size={12} color="#93c5fd" />}
              </Box>

              {/* Alt öğeler — açıldığında göster */}
              {templatesGroupOpen && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 0.5, borderLeft: '2px solid rgba(255,255,255,0.2)', ml: 0.5 }}>
                  <Box onClick={(e) => { e.stopPropagation(); setActiveSection('whatsapp'); }} sx={{ cursor: 'pointer', width: '100%' }}>
                    <NavItem icon={<IconBrandWhatsapp size={22} color={activeSection === 'whatsapp' ? '#ffffff' : '#93c5fd'} />} label="WhatsApp" active={activeSection === 'whatsapp'} />
                  </Box>
                  <Box onClick={(e) => { e.stopPropagation(); setActiveSection('email'); }} sx={{ cursor: 'pointer', width: '100%' }}>
                    <NavItem icon={<IconMail size={22} color={activeSection === 'email' ? '#ffffff' : '#93c5fd'} />} label="E-posta" active={activeSection === 'email'} />
                  </Box>
                  <Box onClick={(e) => { e.stopPropagation(); setActiveSection('email-accounts'); }} sx={{ cursor: 'pointer', width: '100%' }}>
                    <NavItem icon={<IconServer size={22} color={activeSection === 'email-accounts' ? '#ffffff' : '#93c5fd'} />} label="SMTP" active={activeSection === 'email-accounts'} />
                  </Box>
                </Box>
              )}

              {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && (
                <Box onClick={() => setActiveSection('analyses')} sx={{ cursor: 'pointer', width: '100%' }}>
                  <NavItem icon={<IconFileAnalytics size={26} color={activeSection === 'analyses' ? '#ffffff' : '#93c5fd'} />} label="Analizler" active={activeSection === 'analyses'} />
                </Box>
              )}
            </Box>

            {/* Profile Section - Bottom of Sidebar in normal Flex flow */}
            <Box
              className="profile-menu-container"
              sx={{
                mt: "auto",
                pt: 2,
                pb: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.8,
                cursor: "pointer",
                width: "100%",
                borderRadius: 2,
                transition: "background-color 0.2s",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.1)"
                }
              }}
              onClick={(e) => setProfileMenuAnchorEl(e.currentTarget)}
            >
              <Box sx={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                bgcolor: "#f59e0b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
              }}>
                {userData?.profilePicture ? (
                  <Box component="img" src={userData?.profilePicture} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                ) : (
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ffffff' }}>
                    {(currentUser?.firstName?.[0] || userData?.firstName?.[0] || 'S').toUpperCase()}
                    {(currentUser?.lastName?.[0] || userData?.lastName?.[0] || '').toUpperCase()}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: "white", textAlign: "center", fontSize: "0.68rem", fontWeight: 600, px: 0.5 }}>
                {currentUser?.firstName || 'Supervisor'} {currentUser?.lastName || ''}
              </Typography>
              <IconDotsVertical size={16} color="rgba(255, 255, 255, 0.6)" />
            </Box>

            {/* Profile Menu - Portal MUI Menu (Never clipped by overflow) */}
            <Menu
              anchorEl={profileMenuAnchorEl}
              open={Boolean(profileMenuAnchorEl)}
              onClose={() => setProfileMenuAnchorEl(null)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                  minWidth: 190,
                  ml: 1.5,
                  border: "1px solid #e2e8f0",
                  overflow: "hidden"
                }
              }}
            >
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileMenuAnchorEl(null);
                  setProfileDialogOpen(true);
                }}
                sx={{ py: 1.2, px: 2, gap: 1.5 }}
              >
                <IconUser size={18} color="#374151" />
                <Typography variant="body2" sx={{ color: "#374151", fontWeight: 600 }}>
                  Çalışan Kartı
                </Typography>
              </MenuItem>

              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileMenuAnchorEl(null);
                  navigate('/');
                }}
                sx={{ py: 1.2, px: 2, gap: 1.5 }}
              >
                <IconUsers size={18} color="#374151" />
                <Typography variant="body2" sx={{ color: "#374151", fontWeight: 600 }}>
                  Ana Menü
                </Typography>
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              <MenuItem
                onClick={async (e) => {
                  e.stopPropagation();
                  setProfileMenuAnchorEl(null);
                  try {
                    console.log('🔐 Logout attempt');
                    await authService.logout();
                    console.log('✅ Logout successful');
                    localStorage.clear();
                    window.location.href = '/login';
                  } catch (error) {
                    console.error('❌ Logout error:', error);
                    localStorage.clear();
                    window.location.href = '/login';
                  }
                }}
                sx={{ py: 1.2, px: 2, gap: 1.5 }}
              >
                <IconX size={18} color="#ef4444" />
                <Typography variant="body2" sx={{ color: "#ef4444", fontWeight: 600 }}>
                  Çıkış Yap
                </Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{
          flexGrow: 1,
          p: 3,
          pb: 2,
          overflowY: 'auto',
          height: '100vh',
          // Tüm tipografi Arial, başlık renkleri #152b69
          fontFamily: 'Arial, Helvetica, sans-serif',
          '& .MuiTypography-root': { fontFamily: 'Arial, Helvetica, sans-serif' },
          '& .MuiTypography-h1, & .MuiTypography-h2, & .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
            color: '#152b69 !important',
            fontFamily: 'Arial, Helvetica, sans-serif'
          }
        }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                width: '100%',
                bgcolor: 'white',
                borderRadius: 3,
                px: { xs: 2, sm: 4 },
                py: { xs: 2, sm: 3 },
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'relative',
                gap: { xs: 2, sm: 4 }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                width: '100%',
                flexDirection: { xs: 'column', md: 'row' }
              }}>
                <Box sx={{ flexShrink: 0 }}>
                  <img 
                    src="/percrm-logo.svg" 
                    alt="PerCRM" 
                    style={{ height: '52px', width: 'auto', objectFit: 'contain' }} 
                  />
                </Box>

                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '1.6rem', sm: '2rem', md: '2.4rem' } }}>
                    {activeSection === 'analyses' ? 'Yapay Zeka Destekli Kalite Kontrol Paneli' : 'Çağrı Merkezi Paneli'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem' }, color: '#64748b' }}>
                    {activeSection === 'analyses' ? 'Yapay zeka analizleri ve kalite değerlendirmeleri' : 'Gerçek zamanlı veriler ve performans metrikleri'}
                  </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

          {/* Call Monitoring Indicator */}
          {isMonitoring && monitoringAgent && (
            <Box sx={{
              mb: 2,
              bgcolor: '#dbeafe',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
              border: '1px solid #3b82f6'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  bgcolor: '#ef4444',
                  borderRadius: '50%',
                  animation: `${pulse} 2s ease-in-out infinite`
                }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e40af' }}>
                  🎧 Dinleme Aktif: {monitoringAgent.name}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleStopMonitoring}
                sx={{
                  bgcolor: '#ef4444',
                  '&:hover': { bgcolor: '#dc2626' }
                }}
              >
                Dinlemeyi Durdur
              </Button>
            </Box>
          )}

          {activeSection === 'dashboard' && (
            /* Stats Cards */
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Overview Chart */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: "350px", borderRadius: 3, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", border: "1px solid rgba(0, 0, 0, 0.05)" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#374151" }}>
                      Agent Durumu
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "240px", gap: 3 }}>
                      {/* Donut Chart - Dynamic - Sol taraf */}
                      <Box sx={{ position: "relative", width: 160, height: 160, flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: 160,
                            height: 160,
                            borderRadius: "50%",
                            background: (() => {
                              if (statusData.length === 0 || totalAgents === 0) {
                                return 'conic-gradient(#e5e7eb 0deg 360deg)';
                              }

                              // Build conic gradient dynamically from all statuses
                              let gradientParts: string[] = [];
                              let currentAngle = 0;

                              statusData.forEach((status, index) => {
                                const angle = (status.count / totalAgents) * 360;
                                const startAngle = currentAngle;
                                const endAngle = currentAngle + angle;

                                gradientParts.push(`${status.color} ${startAngle}deg ${endAngle}deg`);
                                currentAngle = endAngle;
                              });

                              // Fill remaining space if needed
                              if (currentAngle < 360) {
                                gradientParts.push(`#e5e7eb ${currentAngle}deg 360deg`);
                              }

                              return `conic-gradient(${gradientParts.join(', ')})`;
                            })(),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              width: "140px",
                              height: "140px",
                              borderRadius: "50%",
                              backgroundColor: "white",
                              zIndex: 1,
                            }
                          }}
                        >
                          <Box sx={{
                            position: "relative",
                            zIndex: 2,
                            textAlign: "center",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: "-40px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.3) 100%)",
                              boxShadow: "0 0 15px rgba(255, 255, 255, 0.8)",
                              filter: "blur(0.5px)",
                            }
                          }}>
                            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1e3a8a", textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
                              TOPLAM
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: "bold", color: "#1e3a8a", textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", letterSpacing: "2px" }}>
                              {totalAgents}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Legend - Sağ taraf, alt alta */}
                      <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        justifyContent: "center",
                        flex: 1
                      }}>
                        {statusData.map((status, index) => (
                          <Box key={index} sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                            }
                          }}>
                            <Box sx={{
                              width: "14px",
                              height: "14px",
                              borderRadius: "50%",
                              bgcolor: status.color,
                              boxShadow: `0 2px 8px ${status.color}50`,
                              background: `radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8), ${status.color})`,
                              flexShrink: 0
                            }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151", flex: 1 }}>
                              {status.label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: status.color }}>
                              {status.count}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tables */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Card sx={{ height: "350px", borderRadius: 3, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", border: "1px solid rgba(0, 0, 0, 0.05)" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#374151" }}>
                      Temsilci ve Statü Tablosu
                    </Typography>

                    <Box sx={{ maxHeight: "280px", overflow: "auto" }}>
                      {agentsTableJSX}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Ajan satır menüsü */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={closeAgentMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}

          >
            <MenuItem
              onClick={() => {
                console.log('🔍 MT Performansı clicked, menuAgent:', menuAgent);
                if (menuAgent) {
                  closeAgentMenu();
                  handleAgentClick(menuAgent);
                } else {
                  console.error('❌ menuAgent is null/undefined');
                  closeAgentMenu();
                }
              }}
            >
              MT Performansı
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (menuAgent) {
                  handleOpenStatusChangeDialog(menuAgent);
                }
              }}
            >
              Statü Değiştir
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (menuAgent) {
                  handleStartMonitoring(menuAgent);
                }
              }}
              disabled={!activeCalls.some((call: any) => {
                const match = call.agentId === menuAgent?.id || call.agentExtension === menuAgent?.extension;
                if (!match) {
                  console.log('🔍 No match for call:', { callAgentId: call.agentId, callExtension: call.agentExtension, menuAgentId: menuAgent?.id, menuAgentExtension: menuAgent?.extension });
                }
                return match;
              })}
            >
              Çağrıyı Dinle
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (menuAgent) {
                  handleStartWhisper(menuAgent);
                }
              }}
              disabled={!activeCalls.some((call: any) => {
                const match = call.agentId === menuAgent?.id || call.agentExtension === menuAgent?.extension;
                if (!match) {
                  console.log('🔍 No match for call:', { callAgentId: call.agentId, callExtension: call.agentExtension, menuAgentId: menuAgent?.id, menuAgentExtension: menuAgent?.extension });
                }
                return match;
              })}
            >
              Fısılda
            </MenuItem>
          </Menu>

          {/* Statü Değiştirme Dialog'u */}
          <Dialog
            open={statusChangeDialogOpen}
            onClose={handleCloseStatusChangeDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              Statü Değiştir - {statusChangeAgent?.name}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Yeni Statü</InputLabel>
                <Select
                  value={selectedStatusId}
                  onChange={(e) => setSelectedStatusId(e.target.value)}
                  label="Yeni Statü"
                >
                  {agentStatusContext.statuses.map((status: any) => (
                    <MenuItem key={status._id} value={status._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: status.color
                          }}
                        />
                        {status.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#F9FAFB' }}>
              <Button onClick={handleCloseStatusChangeDialog} color="inherit">
                İptal
              </Button>
              <Button
                onClick={handleChangeAgentStatus}
                variant="contained"
                sx={{
                  bgcolor: '#4F46E5',
                  '&:hover': { bgcolor: '#4338CA' }
                }}
              >
                Değiştir
              </Button>
            </DialogActions>
          </Dialog>

          {activeSection === 'dashboard' && (
            /* ✨ Active Calls and Queue Statistics Section - Swapped order */
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Anlık Çağrılar - Moved to first position */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: "350px", borderRadius: 3, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", border: "1px solid rgba(0, 0, 0, 0.05)" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#374151" }}>
                      Anlık Çağrılar
                    </Typography>
                    {activeCalls.length > 0 ? (
                      activeCallsTableJSX
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          Şu anda aktif çağrı bulunmuyor
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Kuyruk İstatistikleri - Moved to second position */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Card sx={{ height: "350px", borderRadius: 3, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", border: "1px solid rgba(0, 0, 0, 0.05)" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#374151" }}>
                      Kuyruk İstatistikleri
                    </Typography>
                    <TableContainer sx={{ maxHeight: "280px", overflow: "auto" }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: "#374151", bgcolor: "#f8fafc", fontSize: "0.75rem" }}>Kuyruk Adı</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#374151", bgcolor: "#f8fafc", fontSize: "0.75rem" }}>Toplam Arama</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#374151", bgcolor: "#f8fafc", fontSize: "0.75rem" }}>Giden Arama</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#374151", bgcolor: "#f8fafc", fontSize: "0.75rem" }}>Gelen Arama</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#374151", bgcolor: "#f8fafc", fontSize: "0.75rem" }}>Kaçan Çağrı</TableCell>

                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            const activeQueues = queueData.filter((queue) => {
                              const queueStatus = queue.queue?.status || queue.status;
                              return !queueStatus || queueStatus === 'active';
                            });

                            if (activeQueues.length === 0) {
                              return (
                                <TableRow>
                                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: '#9ca3af', fontSize: '0.85rem' }}>
                                    Aktif durumda kuyruk bulunmamaktadır.
                                  </TableCell>
                                </TableRow>
                              );
                            }

                            return activeQueues.map((queue, index) => {
                              const stats = queue.queue?.statistics || {};
                              const totalCalls = stats.totalCalls || 0;
                              const incomingCalls = stats.incomingCalls || 0;
                              const outgoingCalls = stats.outgoingCalls || 0;
                              const answeredCalls = stats.answeredCalls || 0;
                              const missedCalls = stats.missedCalls || 0;
                              const avgAnsweredDuration = stats.avgAnsweredDuration || 0;
                              const _successRate = stats.successRate || 0;

                              return (
                                <TableRow key={index} sx={{
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    bgcolor: "#f0f9ff",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                                  },
                                  "&:nth-of-type(even)": { bgcolor: "#f8fafc" },
                                  borderBottom: "1px solid #e5e7eb"
                                }}>
                                  <TableCell sx={{ fontWeight: 500, color: "#374151", fontSize: "0.8rem" }}>
                                    <Chip
                                      label={queue.name}
                                      size="small"
                                      sx={{
                                        bgcolor: "#152b69",
                                        color: "white",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        borderRadius: 1,
                                      }}
                                    /></TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: "#6b7280", fontSize: "0.8rem" }}>{totalCalls}</TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: "#3b82f6", fontSize: "0.8rem" }}>{outgoingCalls}</TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: "#8b5cf6", fontSize: "0.8rem" }}>{incomingCalls}</TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: "#10b981", fontSize: "0.8rem" }}>{missedCalls}</TableCell>
                                </TableRow>
                              );
                            });
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* 📅 Tüm Agent Randevuları Sekmesi (Supervisor View) */}
          {activeSection === 'appointments' && (
            <Box>
              {/* Statü Özet Kartları */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {(() => {
                  const now = new Date();
                  const total = supervisorAppointments.length;
                  const todayCount = supervisorAppointments.filter(a => {
                    const d = new Date(a.appointmentDate);
                    return d.toDateString() === now.toDateString();
                  }).length;
                  const upcomingCount = supervisorAppointments.filter(a => new Date(a.appointmentDate) > now && a.status !== 'completed').length;
                  const overdueCount = supervisorAppointments.filter(a => new Date(a.appointmentDate) <= now && a.status !== 'completed').length;
                  const completedCount = supervisorAppointments.filter(a => a.status === 'completed').length;

                  return (
                    <>
                      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #e2e8f0', p: 2 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Toplam Randevu</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: '#152b69', mt: 0.5 }}>{total}</Typography>
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #e2e8f0', p: 2 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Bugünkü Randevular</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563eb', mt: 0.5 }}>{todayCount}</Typography>
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #e2e8f0', p: 2 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Gelecek Randevular</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0284c7', mt: 0.5 }}>{upcomingCount}</Typography>
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #fca5a5', p: 2 }}>
                          <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700 }}>⚠️ Gecikenler</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: '#dc2626', mt: 0.5 }}>{overdueCount}</Typography>
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #a7f3d0', p: 2 }}>
                          <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700 }}>✓ Tamamlananlar</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: '#059669', mt: 0.5 }}>{completedCount}</Typography>
                        </Card>
                      </Grid>
                    </>
                  );
                })()}
              </Grid>

              {/* Filtre ve Arama Çubuğu */}
              <Card sx={{ borderRadius: 3, p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2} alignItems="center">
                  {/* Temsilci Seçimi */}
                  <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', display: 'block', mb: 0.5 }}>
                      👤 Temsilci Filtresi
                    </Typography>
                    <Select
                      size="small"
                      fullWidth
                      value={supApptAgentFilter}
                      onChange={(e) => setSupApptAgentFilter(e.target.value)}
                      sx={{ bgcolor: 'white', borderRadius: 2 }}
                    >
                      <MenuItem value="all">Tüm Temsilciler</MenuItem>
                      {agentsData.map((agent: any) => (
                        <MenuItem key={agent._id} value={agent._id}>
                          {`${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email} ({agent.extension || 'Dahili Yok'})
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  {/* Period Filtresi */}
                  <Grid size={{ xs: 12, sm: 8, md: 5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', display: 'block', mb: 0.5 }}>
                      📅 Zaman Filtresi
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {[
                        { id: 'all', label: 'Tümü' },
                        { id: 'today', label: 'Bugün' },
                        { id: 'upcoming', label: 'Gelecek' },
                        { id: 'overdue', label: '⚠️ Gecikenler' },
                        { id: 'completed', label: '✓ Tamamlananlar' }
                      ].map((btn) => (
                        <Button
                          key={btn.id}
                          size="small"
                          variant={supApptFilter === btn.id ? 'contained' : 'outlined'}
                          onClick={() => setSupApptFilter(btn.id as any)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            py: 0.4,
                            bgcolor: supApptFilter === btn.id ? (btn.id === 'overdue' ? '#ef4444' : '#152b69') : 'white',
                            borderColor: btn.id === 'overdue' ? '#ef4444' : '#cbd5e1',
                            color: supApptFilter === btn.id ? 'white' : (btn.id === 'overdue' ? '#ef4444' : '#475569')
                          }}
                        >
                          {btn.label}
                        </Button>
                      ))}
                    </Box>
                  </Grid>

                  {/* Arama Kutusu */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', display: 'block', mb: 0.5 }}>
                      🔍 Müşteri / Not Ara
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Müşteri adı, tel, not veya sonuç kodu..."
                      value={supApptSearch}
                      onChange={(e) => setSupApptSearch(e.target.value)}
                      sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Randevu Kartları Grid */}
              {loadingSupervisorAppointments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : supervisorAppointments.length === 0 ? (
                <Card sx={{ p: 5, textAlign: 'center', borderRadius: 3, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                  <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Kriterlere uygun randevu kaydı bulunamadı.
                  </Typography>
                </Card>
              ) : (
                <Grid container spacing={2}>
                  {supervisorAppointments.map((appt) => {
                    const apptDate = new Date(appt.appointmentDate);
                    const isPast = apptDate < new Date() && appt.status !== 'completed';
                    
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={appt._id}>
                        <Card
                          sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                            border: isPast ? '2px solid #fca5a5' : appt.status === 'completed' ? '2px solid #a7f3d0' : '1px solid #e2e8f0',
                            bgcolor: isPast ? '#fff5f5' : appt.status === 'completed' ? '#f0fdf4' : 'white',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box>
                              {/* Header & Status Chip */}
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Chip
                                  size="small"
                                  label={
                                    appt.status === 'completed' ? '✓ Arandı / Tamamlandı' :
                                    isPast ? '⚠️ Gecikmiş' : 'Bekliyor'
                                  }
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.72rem',
                                    bgcolor: appt.status === 'completed' ? '#dcfce7' : isPast ? '#fee2e2' : '#e0f2fe',
                                    color: appt.status === 'completed' ? '#15803d' : isPast ? '#991b1b' : '#0369a1'
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                  {apptDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                                </Typography>
                              </Box>

                              {/* Customer Details */}
                              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.3 }}>
                                {`${appt.customerFirstName || ''} ${appt.customerLastName || ''}`.trim() || appt.customerPhone}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                📞 {appt.customerPhone}
                              </Typography>

                              {/* Temsilci Rozeti */}
                              <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 2, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 26, height: 26, bgcolor: '#152b69', fontSize: '0.75rem', fontWeight: 700 }}>
                                  {(appt.agentName || 'T')[0]?.toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#475569', display: 'block', lineHeight: 1 }}>
                                    Atanan Temsilci
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                    {appt.agentName || 'Temsilci'}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Scheduled Time & Finish Code */}
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 600 }}>
                                  🕒 Planlanan Saat: <strong>{apptDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</strong>
                                </Typography>
                                {appt.finishCode && (
                                  <Typography variant="caption" sx={{ color: '#475569', display: 'block', fontWeight: 600, mt: 0.3 }}>
                                    🏷️ Sonuç Kodu: <strong>{appt.finishCode}</strong>
                                  </Typography>
                                )}
                              </Box>

                              {/* Notes */}
                              {appt.notes && (
                                <Typography variant="body2" sx={{ color: '#475569', fontStyle: 'italic', bgcolor: '#f8fafc', p: 1, borderRadius: 1.5, fontSize: '0.78rem', mb: 1.5 }}>
                                  "{appt.notes}"
                                </Typography>
                              )}
                            </Box>

                            {/* Action Buttons */}
                            {appt.status !== 'completed' && (
                              <Box sx={{ display: 'flex', gap: 1, pt: 1, borderTop: '1px solid #f1f5f9' }}>
                                <Button
                                  variant="outlined"
                                  color="success"
                                  size="small"
                                  fullWidth
                                  onClick={async () => {
                                    try {
                                      await api.post(`/appointments/${appt._id}/status`, { status: 'completed' });
                                      fetchSupervisorAppointments();
                                    } catch (e) {
                                      console.error('Status error:', e);
                                    }
                                  }}
                                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                                >
                                  ✓ Arandı İşaretle
                                </Button>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}

          {/* Çağrılar Sekmesi */}
          {activeSection === 'calls' && (
            <Box>
              {/* Filtreleme Paneli */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid rgba(0,0,0,0.05)', mb: 3, bgcolor: '#f8fafc' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#152b69' }}>
                    🔍 Filtreleme
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Çağrı Yönü Filtresi */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                          Çağrı Yönü
                        </Typography>
                        <Select
                          size="small"
                          value={callsFilterDirection}
                          onChange={(e) => {
                            setCallsFilterDirection(e.target.value);
                            setCallsPage(1);
                            loadCalls({ page: 1, direction: e.target.value });
                          }}
                          sx={{ width: '100%', bgcolor: 'white' }}
                        >
                          <MenuItem value="all">Tümü</MenuItem>
                          <MenuItem value="incoming">Gelen</MenuItem>
                          <MenuItem value="outgoing">Giden</MenuItem>
                        </Select>
                      </Box>
                    </Grid>

                    {/* Durum Filtresi */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                          Durum
                        </Typography>
                        <Select
                          size="small"
                          value={callsFilterStatus}
                          onChange={(e) => {
                            setCallsFilterStatus(e.target.value);
                            setCallsPage(1);
                            loadCalls({ page: 1, status: e.target.value });
                          }}
                          sx={{ width: '100%', bgcolor: 'white' }}
                        >
                          <MenuItem value="all">Tümü</MenuItem>
                          <MenuItem value="ended">Tamamlandı</MenuItem>
                          <MenuItem value="completed">Tamamlandı</MenuItem>
                          <MenuItem value="answered">Yanıtlandı</MenuItem>
                          <MenuItem value="ringing">Çalıyor</MenuItem>
                          <MenuItem value="waiting">Bekliyor</MenuItem>
                          <MenuItem value="missed">Kaçırıldı</MenuItem>
                          <MenuItem value="failed">Başarısız</MenuItem>
                          <MenuItem value="rejected">Reddedildi</MenuItem>
                          <MenuItem value="cancelled">İptal Edildi</MenuItem>
                          <MenuItem value="no-answer">Cevap Yok</MenuItem>
                          <MenuItem value="busy">Meşgul</MenuItem>
                          <MenuItem value="unavailable">Kullanılamaz</MenuItem>
                          <MenuItem value="initiated">Başlatıldı</MenuItem>
                          <MenuItem value="active">Aktif</MenuItem>
                          <MenuItem value="held">Beklemede</MenuItem>
                          <MenuItem value="transferred">Transfer Edildi</MenuItem>
                        </Select>
                      </Box>
                    </Grid>

                    {/* Temsilci Filtresi */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                          Temsilci
                        </Typography>
                        <Select
                          size="small"
                          value={callsFilterAgent}
                          onChange={(e) => {
                            setCallsFilterAgent(e.target.value);
                            setCallsPage(1);
                            loadCalls({ page: 1, agent: e.target.value });
                          }}
                          sx={{ width: '100%', bgcolor: 'white' }}
                        >
                          <MenuItem value="">Tümü</MenuItem>
                          {allAgents
                            .map((agent: any) => ({
                              id: agent._id,
                              name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || '-'
                            }))
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((agent: any) => (
                              <MenuItem key={agent.id} value={agent.name}>{agent.name}</MenuItem>
                            ))}
                        </Select>
                      </Box>
                    </Grid>

                    {/* Kuyruk Filtresi */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                          Kuyruk
                        </Typography>
                        <Select
                          size="small"
                          value={callsFilterQueue}
                          onChange={(e) => {
                            setCallsFilterQueue(e.target.value);
                            setCallsPage(1);
                            loadCalls({ page: 1, queue: e.target.value });
                          }}
                          sx={{ width: '100%', bgcolor: 'white' }}
                        >
                          <MenuItem value="">Tümü</MenuItem>
                          {allQueues
                            .map((queue: any) => ({
                              id: queue._id || queue.id,
                              name: queue.name || queue.queueName || '-'
                            }))
                            .filter((queue: any) => queue.name !== '-')
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((queue: any) => (
                              <MenuItem key={queue.id} value={queue.name}>{queue.name}</MenuItem>
                            ))}
                        </Select>
                      </Box>
                    </Grid>

                    {/* Telefon Filtresi */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                          Telefon
                        </Typography>
                        <Box
                          component="input"
                          type="text"
                          placeholder="Telefon ara..."
                          value={callsFilterPhoneInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCallsFilterPhoneInput(val);
                            if (callsPhoneDebounceRef.current) clearTimeout(callsPhoneDebounceRef.current);
                            callsPhoneDebounceRef.current = setTimeout(() => {
                              setCallsFilterPhone(val);
                              setCallsPage(1);
                              loadCalls({ page: 1, phone: val });
                            }, 500);
                          }}
                          sx={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            fontSize: '0.875rem',
                            fontFamily: 'inherit',
                            '&:focus': {
                              outline: 'none',
                              borderColor: '#3b82f6',
                              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                            }
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Başlangıç Tarihi Filtresi */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                          Başlangıç Tarihi
                        </Typography>
                        <Box
                          component="input"
                          type="date"
                          value={callsFilterStartDate}
                          onChange={(e) => {
                            setCallsFilterStartDate(e.target.value);
                            setCallsPage(1);
                            loadCalls({ page: 1, startDate: e.target.value });
                          }}
                          sx={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            fontSize: '0.875rem',
                            fontFamily: 'inherit',
                            '&:focus': {
                              outline: 'none',
                              borderColor: '#3b82f6',
                              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                            }
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Bitiş Tarihi Filtresi */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                          Bitiş Tarihi
                        </Typography>
                        <Box
                          component="input"
                          type="date"
                          value={callsFilterEndDate}
                          onChange={(e) => {
                            setCallsFilterEndDate(e.target.value);
                            setCallsPage(1);
                            loadCalls({ page: 1, endDate: e.target.value });
                          }}
                          sx={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            fontSize: '0.875rem',
                            fontFamily: 'inherit',
                            '&:focus': {
                              outline: 'none',
                              borderColor: '#3b82f6',
                              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                            }
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Finish Code Filtresi */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                          Finish Code
                        </Typography>
                        <Select
                          size="small"
                          value={callsFilterFinishCode}
                          onChange={(e) => {
                            setCallsFilterFinishCode(e.target.value);
                            setCallsPage(1);
                            loadCalls({ page: 1, finishCode: e.target.value });
                          }}
                          sx={{ width: '100%', bgcolor: 'white' }}
                        >
                          <MenuItem value="">Tümü</MenuItem>
                          <MenuItem value="Görüşme Yapıldı">Görüşme Yapıldı</MenuItem>
                          <MenuItem value="Ses Yok">Ses Yok</MenuItem>
                          <MenuItem value="Kara Liste">Kara Liste</MenuItem>
                          <MenuItem value="Numara Kullanılmıyor">Numara Kullanılmıyor</MenuItem>
                          <MenuItem value="Meşgul">Meşgul</MenuItem>
                          <MenuItem value="Ulaşılamadı">Ulaşılamadı</MenuItem>
                          <MenuItem value="Daha Sonra Aranacak">Daha Sonra Aranacak</MenuItem>
                        </Select>
                      </Box>
                    </Grid>

                    {/* Temizle Butonu */}
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setCallsFilterDirection('all');
                            setCallsFilterStatus('all');
                            setCallsFilterAgent('');
                            setCallsFilterQueue('');
                            setCallsFilterPhone('');
                            setCallsFilterPhoneInput('');
                            setCallsFilterStartDate('');
                            setCallsFilterEndDate('');
                            setCallsFilterFinishCode('');
                            setCallsPage(1);
                            loadCalls({
                              page: 1, direction: 'all', status: 'all',
                              agent: '', queue: '', phone: '',
                              startDate: '', endDate: '', finishCode: ''
                            });
                          }}
                          sx={{ width: '100%' }}
                        >
                          Temizle
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Çağrılar Tablosu */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid rgba(0,0,0,0.05)', mb: 3 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#152b69' }}>
                      Tüm Çağrılar
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<span>📊</span>}
                      onClick={handleExportCallsExcel}
                      sx={{ textTransform: 'none', fontWeight: 500, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                    >
                      Excel Raporu Al
                    </Button>
                  </Box>
                  <TableContainer sx={{ maxHeight: '65vh', overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Tarih/Saat</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Yön</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Telefon</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Müşteri</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Temsilci</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Kuyruk</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Finish Code</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Süre</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Kayıt</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {callsLoading ? (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={24} sx={{ mr: 1.5 }} />
                                <Typography variant="body2" color="text.secondary">Yükleniyor…</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : calls.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">Kayıt bulunamadı</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          calls.map((c: any) => {
                            const dir = c.direction || '-';
                            const phone = dir === 'outgoing' ? (c.calledNumber || c.callerNumber || '-') : (c.callerNumber || c.calledNumber || '-');
                            // For outgoing calls the customer is the party being called (calledNumber/customerPhone)
                            const customer = c.displayCustomerName || (dir === 'outgoing' ? (c.customerPhone || c.calledNumber || c.callerName || phone) : (c.callerName || c.calledNumber || phone)) || '-';
                            const agentName = c.agentId ? `${c.agentId.firstName || ''} ${c.agentId.lastName || ''}`.trim() : '-';
                            const queueName = c.queueId?.name || '-';
                            const status = c.status || '-';

                            // Durum çevirisi
                            const getStatusTR = (statusEn: string) => {
                              const statusMap: Record<string, string> = {
                                'ended': 'Tamamlandı',
                                'completed': 'Tamamlandı',
                                'failed': 'Başarısız',
                                'pending': 'Beklemede',
                                'ringing': 'Çalıyor',
                                'answered': 'Yanıtlandı',
                                'missed': 'Kaçırıldı',
                                'unanswered': 'Cevapsız',
                                'rejected': 'Reddedildi',
                                'cancelled': 'İptal Edildi',
                                'no-answer': 'Cevap Yok',
                                'busy': 'Meşgul',
                                'unavailable': 'Kullanılamaz',
                                'error': 'Hata',
                                'unknown': 'Bilinmiyor'
                              };
                              return statusMap[statusEn?.toLowerCase()] || statusEn;
                            };

                            const _statusTR = getStatusTR(status);
                            const startTime = c.startTime || c.createdAt;
                            const duration = formatDurationHHMMSS(c.duration);

                            // Finish Code - müşteri kartından tarih bazlı al
                            const finishCode = c.finishCode || '-';

                            return (
                              <TableRow key={c._id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#f8fafc' } }}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTR(startTime)}</TableCell>
                                <TableCell sx={{ textTransform: 'capitalize' }}>{dir === 'incoming' ? 'Gelen' : (dir === 'outgoing' ? 'Giden' : dir)}</TableCell>
                                <TableCell>{phone}</TableCell>
                                <TableCell>{customer}</TableCell>
                                <TableCell>{agentName || '-'}</TableCell>
                                <TableCell>{queueName}</TableCell>

                                <TableCell sx={{ fontWeight: 500, color: '#6b7280', fontSize: '0.8rem' }}>
                                  {finishCode}
                                </TableCell>
                                <TableCell>{duration}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    {(c.recording && c.recording.hasRecording) && (
                                      <>
                                        <IconButton size="small" onClick={async () => {
                                          try {
                                            // Double-check recording availability via context
                                            let recStatus = callRecordingContext.getRecordingStatus(c._id);
                                            if (!recStatus || !recStatus.recording || !recStatus.recording.hasRecording) {
                                              // Try to fetch recording metadata by callId
                                              console.debug('No recording in context for callId', c._id, '— fetching recordings');
                                              const fetched = await callRecordingContext.fetchRecordings({ callId: c._id, limit: 5 });
                                              console.debug('Fetched recordings for callId', c._id, fetched);
                                              recStatus = callRecordingContext.getRecordingStatus(c._id);
                                            }
                                            if (!recStatus || !recStatus.recording || !recStatus.recording.hasRecording) {
                                              // Fallback: attempt to stream using callId directly
                                              console.debug('Fallback: attempting stream using callId', c._id);
                                              const url = crPlay(c._id);
                                              if (!url) {
                                                showSnackbar('Kayıt bulunamadı', 'warning');
                                                return;
                                              }
                                              try {
                                                const res = await fetch(url, { method: 'HEAD' });
                                                if (!res.ok) {
                                                  showSnackbar('Kayıt akışı bulunamadı (sunucu hatası)', 'warning');
                                                  return;
                                                }
                                              } catch (e) {
                                                console.error('HEAD request for recording failed', e);
                                                // Continue to try to load – in some servers HEAD isn't permitted
                                              }
                                              setPlayerDialog({ open: true, audioUrl: url, title: `${agentName} • ${phone}` });
                                              return;
                                            }
                                            const url = crPlay(c._id);
                                            if (url) setPlayerDialog({ open: true, audioUrl: url, title: `${agentName} • ${phone}` });
                                          } catch (e) {
                                            console.error('Play error', e);
                                            showSnackbar('Kayıt oynatılırken hata oluştu', 'error');
                                          }
                                        }} title="Dinle">
                                          <IconPlayerPlay size={16} />
                                        </IconButton>

                                        <IconButton size="small" onClick={async () => {
                                          // Double-check before starting download
                                          let recStatus = callRecordingContext.getRecordingStatus(c._id);
                                          if (!recStatus || !recStatus.recording || !recStatus.recording.hasRecording) {
                                            console.debug('No recording in context for callId, fetching for download: ', c._id);
                                            await callRecordingContext.fetchRecordings({ callId: c._id, limit: 5 });
                                            recStatus = callRecordingContext.getRecordingStatus(c._id);
                                          }
                                          if (!recStatus || !recStatus.recording || !recStatus.recording.hasRecording) {
                                            // Fallback: attempt to download using callId directly
                                            try {
                                              const url = crPlay(c._id).replace('/stream', '/download');
                                              // quick check
                                              try {
                                                const head = await fetch(url, { method: 'HEAD' });
                                                if (!head.ok) {
                                                  showSnackbar('Kayıt dosyası sunucuda bulunamadı', 'warning');
                                                  return;
                                                }
                                              } catch (err) {
                                                console.debug('HEAD for download failed', err);
                                              }
                                              crDownload(c._id);
                                            } catch (e) {
                                              console.error('Download error', e);
                                              showSnackbar('Kayıt indirilirken hata oluştu', 'error');
                                            }
                                            return;
                                          }
                                          try {
                                            crDownload(c._id);
                                          } catch (e) {
                                            console.error('Download error', e);
                                            showSnackbar('Kayıt indirilirken hata oluştu', 'error');
                                          }
                                        }} title="İndir">
                                          <IconDownload size={16} />
                                        </IconButton>
                                      </>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Basit Sayfalama */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Toplam: {callsTotal} | Sayfa {callsPage}/{callsTotalPages}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button variant="outlined" size="small" disabled={callsPage <= 1 || callsLoading} onClick={() => setCallsPage((p) => Math.max(1, p - 1))}>Önceki</Button>
                      <Button variant="outlined" size="small" disabled={callsPage >= callsTotalPages || callsLoading} onClick={() => setCallsPage((p) => p + 1)}>Sonraki</Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Raporlar Sekmesi */}
          {activeSection === 'reports' && (
            <Box>
              {/* Date Range Picker ve Tab Butonları */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid rgba(0,0,0,0.05)', mb: 3, bgcolor: '#f8fafc' }}>
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#152b69' }}>
                        📊 Rapor Modülü
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <DatePicker
                            label="Başlangıç Tarihi"
                            value={dayjs(reportDateRange.startDate)}
                            onChange={(date: any) => setReportDateRange({ ...reportDateRange, startDate: date ? date.toDate() : new Date() })}
                            slotProps={{ textField: { size: 'small', sx: { bgcolor: 'white' } } }}
                          />
                          <DatePicker
                            label="Bitiş Tarihi"
                            value={dayjs(reportDateRange.endDate)}
                            onChange={(date: any) => setReportDateRange({ ...reportDateRange, endDate: date ? date.toDate() : new Date() })}
                            slotProps={{ textField: { size: 'small', sx: { bgcolor: 'white' } } }}
                          />
                        </Box>
                      </LocalizationProvider>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap' }}>
                        <Button
                          variant={activeReportTab === 'calls' ? 'contained' : 'outlined'}
                          onClick={() => setActiveReportTab('calls')}
                          sx={{
                            borderRadius: 2,
                            bgcolor: activeReportTab === 'calls' ? '#4f46e5' : 'white',
                            '&:hover': { bgcolor: activeReportTab === 'calls' ? '#4338ca' : '#f3f4f6' }
                          }}
                        >
                          Çağrı Raporu
                        </Button>
                        <Button
                          variant={activeReportTab === 'agents' ? 'contained' : 'outlined'}
                          onClick={() => setActiveReportTab('agents')}
                          sx={{
                            borderRadius: 2,
                            bgcolor: activeReportTab === 'agents' ? '#10b981' : 'white',
                            '&:hover': { bgcolor: activeReportTab === 'agents' ? '#059669' : '#f3f4f6' }
                          }}
                        >
                          Agent Raporu
                        </Button>
                        <Button
                          variant={activeReportTab === 'queues' ? 'contained' : 'outlined'}
                          onClick={() => setActiveReportTab('queues')}
                          sx={{
                            borderRadius: 2,
                            bgcolor: activeReportTab === 'queues' ? '#8b5cf6' : 'white',
                            '&:hover': { bgcolor: activeReportTab === 'queues' ? '#7c3aed' : '#f3f4f6' }
                          }}
                        >
                          Kuyruk Raporu
                        </Button>
                        <Button
                          variant={activeReportTab === 'status' ? 'contained' : 'outlined'}
                          onClick={() => setActiveReportTab('status')}
                          sx={{
                            borderRadius: 2,
                            bgcolor: activeReportTab === 'status' ? '#ec4899' : 'white',
                            '&:hover': { bgcolor: activeReportTab === 'status' ? '#db2777' : '#f3f4f6' }
                          }}
                        >
                          Statü Raporu
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Report Content */}
              {activeReportTab === 'calls' && (
                <CallReports
                  startDate={reportDateRange.startDate.toISOString().split('T')[0]}
                  endDate={reportDateRange.endDate.toISOString().split('T')[0]}
                />
              )}
              {activeReportTab === 'agents' && (
                <AgentReports
                  startDate={reportDateRange.startDate.toISOString().split('T')[0]}
                  endDate={reportDateRange.endDate.toISOString().split('T')[0]}
                />
              )}
              {activeReportTab === 'queues' && (
                <QueueReports
                  startDate={reportDateRange.startDate.toISOString().split('T')[0]}
                  endDate={reportDateRange.endDate.toISOString().split('T')[0]}
                />
              )}
              {activeReportTab === 'status' && (
                <StatusReports
                  startDate={reportDateRange.startDate.toISOString().split('T')[0]}
                  endDate={reportDateRange.endDate.toISOString().split('T')[0]}
                />
              )}
            </Box>
          )}

          {/* Destek Talebi Sekmesi */}
          {activeSection === 'support' && (
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              <Card sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                border: '1px solid rgba(0,0,0,0.05)',
                bgcolor: '#f8fafc'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconHelp size={32} color="#4f46e5" />
                    <Typography variant="h5" sx={{ fontWeight: 'bold', ml: 2, color: '#152b69' }}>
                      Destek Talebi
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
                    Herhangi bir sorun veya talep için aşağıdaki formu doldurarak bize ulaşabilirsiniz.
                    Talebiniz en kısa sürede değerlendirilecektir.
                  </Typography>

                  {/* Öncelik Seçimi */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Öncelik</InputLabel>
                    <Select
                      value={supportPriority}
                      label="Öncelik"
                      onChange={(e) => setSupportPriority(e.target.value as 'low' | 'medium' | 'high' | 'critical')}
                      sx={{ bgcolor: 'white' }}
                    >
                      <MenuItem value="low">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22c55e' }} />
                          Düşük
                        </Box>
                      </MenuItem>
                      <MenuItem value="medium">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                          Orta
                        </Box>
                      </MenuItem>
                      <MenuItem value="high">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
                          Yüksek
                        </Box>
                      </MenuItem>
                      <MenuItem value="critical">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#dc2626' }} />
                          Kritik
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Konu */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                      Konu
                    </Typography>
                    <input
                      type="text"
                      value={supportSubject}
                      onChange={(e) => setSupportSubject(e.target.value)}
                      placeholder="Destek talebinizin konusu"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        outline: 'none',
                        backgroundColor: 'white',
                        boxSizing: 'border-box'
                      }}
                    />
                  </Box>

                  {/* Mesaj */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                      Mesaj
                    </Typography>
                    <textarea
                      value={supportMessage}
                      onChange={(e) => setSupportMessage(e.target.value)}
                      placeholder="Sorununuzu veya talebinizi detaylı olarak açıklayın..."
                      rows={6}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        outline: 'none',
                        backgroundColor: 'white',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box'
                      }}
                    />
                  </Box>

                  {/* Gönder Butonu */}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSendSupport}
                    disabled={sendingSupport || !supportSubject.trim() || !supportMessage.trim()}
                    startIcon={sendingSupport ? <CircularProgress size={20} color="inherit" /> : <IconSend size={20} />}
                    sx={{
                      bgcolor: '#4f46e5',
                      py: 1.5,
                      fontSize: '16px',
                      fontWeight: 600,
                      borderRadius: 2,
                      '&:hover': { bgcolor: '#4338ca' },
                      '&:disabled': { bgcolor: '#9ca3af' }
                    }}
                  >
                    {sendingSupport ? 'Gönderiliyor...' : 'Destek Talebi Gönder'}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* ✨ Mesajlar Sekmesi */}
          {activeSection === 'messages' && (
            <Box sx={{ height: 'calc(100vh - 150px)' }}>
              <Box sx={{ height: '100%', display: 'flex', border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', bgcolor: 'white' }}>
                {/* Conversations List */}
                <Box sx={{ width: '350px', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <IconSend size={24} />
                      Tüm Mesajlaşmalar
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', mb: 1.5, display: 'block' }}>
                      Agent'lar arası tüm konuşmaları görüntüleyin
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={() => setShowUserSelectDialog(true)}
                      sx={{
                        bgcolor: '#3b82f6',
                        '&:hover': { bgcolor: '#2563eb' },
                        textTransform: 'none'
                      }}
                    >
                      + Yeni Mesaj
                    </Button>
                  </Box>
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {loadingMessages ? (
                      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                        <CircularProgress />
                      </Box>
                    ) : conversations.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
                        <IconSend size={48} color="#94a3b8" style={{ marginBottom: 16 }} />
                        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                          Henüz mesajlaşma bulunmuyor
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowUserSelectDialog(true)}
                          sx={{ textTransform: 'none' }}
                        >
                          İlk Mesajı Gönder
                        </Button>
                      </Box>
                    ) : (
                      conversations.map((conv: any) => {
                        // Get both users involved in conversation
                        const user1 = conv.lastMessage?.senderId;
                        const user2 = conv.lastMessage?.receiverId;
                        const lastMessagePreview = conv.lastMessage?.content?.substring(0, 60) || '';
                        const timestamp = conv.lastMessage?.createdAt ? new Date(conv.lastMessage.createdAt).toLocaleString('tr-TR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '';

                        return (
                          <Box
                            key={`${conv._id?.user1}-${conv._id?.user2}`}
                            onClick={() => {
                              setSelectedConversation(conv);
                              loadConversationMessages(user1?._id, user2?._id);
                            }}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                              bgcolor: selectedConversation?._id?.user1 === conv._id?.user1 && selectedConversation?._id?.user2 === conv._id?.user2 ? '#f8fafc' : 'white',
                              '&:hover': { bgcolor: '#f8fafc' },
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Box sx={{ display: 'flex', position: 'relative' }}>
                                <Avatar sx={{ bgcolor: '#3b82f6', width: 36, height: 36 }}>
                                  {user1?.firstName?.charAt(0).toUpperCase() || 'U'}
                                </Avatar>
                                <Avatar sx={{ bgcolor: '#8b5cf6', width: 36, height: 36, ml: -1.5, border: '2px solid white' }}>
                                  {user2?.firstName?.charAt(0).toUpperCase() || 'U'}
                                </Avatar>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                                  {user1?.firstName} {user1?.lastName} ↔ {user2?.firstName} {user2?.lastName}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {lastMessagePreview}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem', mt: 0.5, display: 'block' }}>
                                  {timestamp}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Box>

                {/* Messages Display */}
                {selectedConversation ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', position: 'relative' }}>
                          <Avatar sx={{ bgcolor: '#3b82f6', width: 40, height: 40 }}>
                            {selectedConversation.lastMessage?.senderId?.firstName?.charAt(0).toUpperCase() || 'U'}
                          </Avatar>
                          <Avatar sx={{ bgcolor: '#8b5cf6', width: 40, height: 40, ml: -1.5, border: '2px solid white' }}>
                            {selectedConversation.lastMessage?.receiverId?.firstName?.charAt(0).toUpperCase() || 'U'}
                          </Avatar>
                        </Box>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {selectedConversation.lastMessage?.senderId?.firstName} {selectedConversation.lastMessage?.senderId?.lastName} ↔ {selectedConversation.lastMessage?.receiverId?.firstName} {selectedConversation.lastMessage?.receiverId?.lastName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Mesajlaşma Geçmişi
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Messages */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: '#f8fafc' }}>
                      {messages.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                            Mesaj bulunamadı
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {messages.map((msg: any) => {
                            const senderName = `${msg.senderId?.firstName || ''} ${msg.senderId?.lastName || ''}`.trim();
                            const receiverName = `${msg.receiverId?.firstName || ''} ${msg.receiverId?.lastName || ''}`.trim();
                            return (
                              <Box
                                key={msg._id}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  gap: 0.5
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ bgcolor: '#3b82f6', width: 28, height: 28, fontSize: '0.85rem' }}>
                                    {msg.senderId?.firstName?.charAt(0).toUpperCase() || 'U'}
                                  </Avatar>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                                    {senderName} → {receiverName}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                    {new Date(msg.createdAt).toLocaleString('tr-TR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    p: 2,
                                    ml: 4.5,
                                    borderRadius: 2,
                                    bgcolor: 'white',
                                    maxWidth: '70%',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    border: '1px solid #e2e8f0'
                                  }}
                                >
                                  <Typography variant="body2" sx={{ color: '#1e293b', wordBreak: 'break-word' }}>
                                    {msg.content}
                                  </Typography>
                                  {msg.isRead && (
                                    <Typography variant="caption" sx={{ color: '#10b981', display: 'block', mt: 0.5 }}>
                                      ✓✓ Okundu
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>

                    {/* Message Input - Only show for new conversations with selectedUser */}
                    {selectedUser && !selectedConversation && (
                      <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: 'white' }}>
                        <Box sx={{ mb: 1.5 }}>
                          <Chip
                            avatar={<Avatar sx={{ bgcolor: '#3b82f6' }}>{(selectedUser.firstName?.[0] || '?').toUpperCase()}</Avatar>}
                            label={`${selectedUser.firstName} ${selectedUser.lastName}`}
                            onDelete={() => setSelectedUser(null)}
                            sx={{ bgcolor: '#f1f5f9' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Mesajınızı yazın..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                                e.preventDefault();
                                handleSendMessage();
                              }
                            }}
                            multiline
                            maxRows={3}
                          />
                          <Button
                            variant="contained"
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim()}
                            sx={{
                              bgcolor: '#3b82f6',
                              '&:hover': { bgcolor: '#2563eb' },
                              minWidth: '100px'
                            }}
                          >
                            Gönder
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : selectedUser ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header for new conversation */}
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#3b82f6', width: 40, height: 40 }}>
                          {(selectedUser.firstName?.[0] || '?').toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {selectedUser.firstName} {selectedUser.lastName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Yeni Mesajlaşma
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Empty state for new conversation */}
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <IconSend size={64} color="#94a3b8" style={{ marginBottom: 16 }} />
                        <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                          İlk Mesajı Gönderin
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          {selectedUser.firstName} ile mesajlaşmaya başlayın
                        </Typography>
                      </Box>
                    </Box>

                    {/* Message Input */}
                    <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: 'white' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Mesajınızı yazın..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          multiline
                          maxRows={3}
                        />
                        <Button
                          variant="contained"
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim()}
                          sx={{
                            bgcolor: '#3b82f6',
                            '&:hover': { bgcolor: '#2563eb' },
                            minWidth: '100px'
                          }}
                        >
                          Gönder
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <IconSend size={64} color="#94a3b8" style={{ marginBottom: 16 }} />
                      <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                        Mesajlaşmayı Görüntüle
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        Soldaki listeden bir konuşma seçin
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* User Selection Dialog */}
              <Dialog
                open={showUserSelectDialog}
                onClose={() => setShowUserSelectDialog(false)}
                maxWidth="xs"
                fullWidth
              >
                <DialogTitle>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Kullanıcı Seç</Typography>
                    <IconButton size="small" onClick={() => setShowUserSelectDialog(false)}>
                      ✕
                    </IconButton>
                  </Box>
                </DialogTitle>
                <DialogContent dividers>
                  <List>
                    {allUsers.map((user) => (
                      <ListItem
                        key={user._id}
                        onClick={() => {
                          setSelectedUser(user);
                          setSelectedConversation(null);
                          setMessages([]);
                          setShowUserSelectDialog(false);
                        }}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f8fafc' }
                        }}
                      >
                        <Avatar sx={{ bgcolor: '#3b82f6', mr: 2 }}>
                          {(user.firstName?.[0] || user.email?.[0] || '?').toUpperCase()}
                        </Avatar>
                        <ListItemText
                          primary={user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                          secondary={user.role}
                        />
                      </ListItem>
                    ))}
                  </List>
                </DialogContent>
              </Dialog>
            </Box>
          )}

          {/* WhatsApp Şablonları Sekmesi */}
          {activeSection === 'whatsapp' && (
            <Box sx={{ height: 'calc(100vh - 150px)', overflow: 'auto', p: 1 }}>
              <Grid container spacing={3}>
                {/* Sol Taraf: Şablon Ekleme / Düzenleme Formu */}
                <Grid size={{ xs: 12, md: 5 }}>
                  <Card sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    bgcolor: '#ffffff'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconBrandWhatsapp size={32} color="#25D366" />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', ml: 2, color: '#152b69' }}>
                          {editingTmplId ? 'Şablonu Düzenle' : 'Yeni Şablon Ekle'}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                        Temsilcilerin müşteri kartlarında hızlıca kullanabileceği genel mesaj şablonlarını buradan yönetebilirsiniz.
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                          label="Şablon Başlığı (Örn: Geri Dönüş Şablonu)"
                          size="small"
                          fullWidth
                          value={editingTmplId ? editingTmplName : newTmplName}
                          onChange={(e) => editingTmplId ? setEditingTmplName(e.target.value) : setNewTmplName(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                            }
                          }}
                        />

                        <Box>
                          <TextField
                            label="Mesaj İçeriği"
                            size="small"
                            multiline
                            rows={6}
                            fullWidth
                            inputRef={contentInputRef}
                            value={editingTmplId ? editingTmplContent : newTmplContent}
                            onChange={(e) => editingTmplId ? setEditingTmplContent(e.target.value) : setNewTmplContent(e.target.value)}
                            placeholder="Müşteriye gönderilecek mesaj içeriğini yazın..."
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              }
                            }}
                          />
                          {/* Dinamik Değişken Yardımcıları */}
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1, fontWeight: 600 }}>
                              Kullanılabilir Değişkenler (Ekleme için tıklayın):
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label="{ad}"
                                size="small"
                                onClick={() => handleInsertPlaceholder('{ad}', !!editingTmplId)}
                                sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: '#c7d2fe' } }}
                                title="Müşteri Adı"
                              />
                              <Chip
                                label="{soyad}"
                                size="small"
                                onClick={() => handleInsertPlaceholder('{soyad}', !!editingTmplId)}
                                sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: '#c7d2fe' } }}
                                title="Müşteri Soyadı"
                              />
                              <Chip
                                label="{telefon}"
                                size="small"
                                onClick={() => handleInsertPlaceholder('{telefon}', !!editingTmplId)}
                                sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: '#c7d2fe' } }}
                                title="Müşteri Telefon Numarası"
                              />
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
                          {editingTmplId && (
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setEditingTmplId(null);
                                setEditingTmplName('');
                                setEditingTmplContent('');
                              }}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                              İptal
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={editingTmplId ? handleUpdateWhatsappTemplate : handleCreateWhatsappTemplate}
                            disabled={editingTmplId ? (!editingTmplName.trim() || !editingTmplContent.trim()) : (!newTmplName.trim() || !newTmplContent.trim())}
                            sx={{
                              bgcolor: '#152b69',
                              '&:hover': { bgcolor: '#0f1f4d' },
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 3
                            }}
                          >
                            {editingTmplId ? 'Değişiklikleri Kaydet' : 'Şablonu Ekle'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Sağ Taraf: Şablon Listesi */}
                <Grid size={{ xs: 12, md: 7 }}>
                  <Card sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    bgcolor: '#ffffff',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#152b69' }}>
                        Mevcut Şablonlar ({whatsappTemplates.length})
                      </Typography>

                      {loadingTemplates ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 8 }}>
                          <CircularProgress sx={{ color: '#152b69' }} />
                        </Box>
                      ) : whatsappTemplates.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
                          <Typography variant="body1" color="textSecondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                            Kayıtlı şablon bulunamadı.
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Sol taraftaki formdan ilk şablonu oluşturabilirsiniz.
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
                          {whatsappTemplates.map((tmpl) => (
                            <Box
                              key={tmpl._id}
                              sx={{
                                p: 2.5,
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                bgcolor: '#f8fafc',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#cbd5e1',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                  {tmpl.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingTmplId(tmpl._id || null);
                                      setEditingTmplName(tmpl.name);
                                      setEditingTmplContent(tmpl.content);
                                    }}
                                    sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#f1f5f9' }, borderRadius: 1.5 }}
                                    title="Düzenle"
                                  >
                                    <IconPencil size={16} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => tmpl._id && handleDeleteWhatsappTemplate(tmpl._id)}
                                    sx={{ bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fecaca' }, borderRadius: 1.5 }}
                                    title="Sil"
                                  >
                                    <IconX size={16} />
                                  </IconButton>
                                </Box>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-line',
                                  bgcolor: '#ffffff',
                                  p: 2,
                                  borderRadius: 1.5,
                                  border: '1px dashed #e2e8f0',
                                  color: '#475569',
                                  fontSize: '0.9rem',
                                  lineHeight: 1.6
                                }}
                              >
                                {tmpl.content}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* E-posta Şablonları Sekmesi */}
          {activeSection === 'email' && (
            <Box sx={{ height: 'calc(100vh - 150px)', overflow: 'auto', p: 1 }}>
              <Grid container spacing={3}>
                {/* Sol Taraf: Şablon Ekleme / Düzenleme Formu */}
                <Grid size={{ xs: 12, md: 5 }}>
                  <Card sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    bgcolor: '#ffffff'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconMail size={32} color="#0284c7" />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', ml: 2, color: '#152b69' }}>
                          {editingEmailTmplId ? 'Şablonu Düzenle' : 'Yeni Şablon Ekle'}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                        Temsilcilerin müşteri kartlarında hızlıca kullanabileceği genel e-posta şablonlarını buradan yönetebilirsiniz.
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                          label="Şablon Başlığı (Örn: Teklif Şablonu)"
                          size="small"
                          fullWidth
                          value={editingEmailTmplId ? editingEmailTmplName : newEmailTmplName}
                          onChange={(e) => editingEmailTmplId ? setEditingEmailTmplName(e.target.value) : setNewEmailTmplName(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                            }
                          }}
                        />

                        <TextField
                          label="E-posta Konusu (Subject)"
                          size="small"
                          fullWidth
                          value={editingEmailTmplId ? editingEmailTmplSubject : newEmailTmplSubject}
                          onChange={(e) => editingEmailTmplId ? setEditingEmailTmplSubject(e.target.value) : setNewEmailTmplSubject(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                            }
                          }}
                        />

                        <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                          <InputLabel id="email-account-select-label">Bağlı Gönderici E-posta Hesabı</InputLabel>
                          <Select
                            labelId="email-account-select-label"
                            label="Bağlı Gönderici E-posta Hesabı"
                            value={editingEmailTmplId ? editingEmailTmplAccountId : newEmailTmplAccountId}
                            onChange={(e) => editingEmailTmplId ? setEditingEmailTmplAccountId(e.target.value) : setNewEmailTmplAccountId(e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Varsayılan (Hesap Tanımlanmamış)</em>
                            </MenuItem>
                            {emailAccounts.map((acc) => (
                              <MenuItem key={acc._id} value={acc._id}>
                                {acc.name} ({acc.email})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Box>
                          <TextField
                            label="Mesaj İçeriği"
                            size="small"
                            multiline
                            rows={6}
                            fullWidth
                            inputRef={emailContentInputRef}
                            value={editingEmailTmplId ? editingEmailTmplContent : newEmailTmplContent}
                            onChange={(e) => editingEmailTmplId ? setEditingEmailTmplContent(e.target.value) : setNewEmailTmplContent(e.target.value)}
                            placeholder="Müşteriye gönderilecek e-posta mesaj içeriğini yazın..."
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              }
                            }}
                          />
                          {/* Dinamik Değişken Yardımcıları */}
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1, fontWeight: 600 }}>
                              Kullanılabilir Değişkenler (Ekleme için tıklayın):
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label="{ad}"
                                size="small"
                                onClick={() => handleInsertEmailPlaceholder('{ad}', !!editingEmailTmplId)}
                                sx={{ bgcolor: '#e0f2fe', color: '#0284c7', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: '#bae6fd' } }}
                                title="Müşteri Adı"
                              />
                              <Chip
                                label="{soyad}"
                                size="small"
                                onClick={() => handleInsertEmailPlaceholder('{soyad}', !!editingEmailTmplId)}
                                sx={{ bgcolor: '#e0f2fe', color: '#0284c7', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: '#bae6fd' } }}
                                title="Müşteri Soyadı"
                              />
                              <Chip
                                label="{telefon}"
                                size="small"
                                onClick={() => handleInsertEmailPlaceholder('{telefon}', !!editingEmailTmplId)}
                                sx={{ bgcolor: '#e0f2fe', color: '#0284c7', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: '#bae6fd' } }}
                                title="Müşteri Telefon Numarası"
                              />
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
                          {editingEmailTmplId && (
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setEditingEmailTmplId(null);
                                setEditingEmailTmplName('');
                                setEditingEmailTmplSubject('');
                                setEditingEmailTmplContent('');
                              }}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                              İptal
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={editingEmailTmplId ? handleUpdateEmailTemplate : handleCreateEmailTemplate}
                            disabled={editingEmailTmplId ? (!editingEmailTmplName.trim() || !editingEmailTmplSubject.trim() || !editingEmailTmplContent.trim()) : (!newEmailTmplName.trim() || !newEmailTmplSubject.trim() || !newEmailTmplContent.trim())}
                            sx={{
                              bgcolor: '#0284c7',
                              '&:hover': { bgcolor: '#0369a1' },
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 3
                            }}
                          >
                            {editingEmailTmplId ? 'Değişiklikleri Kaydet' : 'Şablonu Ekle'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Sağ Taraf: Şablon Listesi */}
                <Grid size={{ xs: 12, md: 7 }}>
                  <Card sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    bgcolor: '#ffffff',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#152b69' }}>
                        Mevcut E-posta Şablonları ({emailTemplates.length})
                      </Typography>

                      {loadingEmailTemplates ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 8 }}>
                          <CircularProgress sx={{ color: '#0284c7' }} />
                        </Box>
                      ) : emailTemplates.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
                          <Typography variant="body1" color="textSecondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                            Kayıtlı e-posta şablonu bulunamadı.
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Sol taraftaki formdan ilk şablonu oluşturabilirsiniz.
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" flexDirection="column" gap={2} sx={{ maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
                          {emailTemplates.map((tmpl) => (
                            <Box
                              key={tmpl._id}
                              sx={{
                                p: 2.5,
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                bgcolor: '#f8fafc',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#cbd5e1',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                    {tmpl.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                                    Konu: {tmpl.subject}
                                  </Typography>
                                  {tmpl.emailAccountId && typeof tmpl.emailAccountId === 'object' && (tmpl.emailAccountId as any).email && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip
                                        icon={<IconServer size={12} />}
                                        label={(tmpl.emailAccountId as any).name + ' — ' + (tmpl.emailAccountId as any).email}
                                        size="small"
                                        sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 500, fontSize: '0.7rem', height: 20 }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingEmailTmplId(tmpl._id || null);
                                      setEditingEmailTmplName(tmpl.name);
                                      setEditingEmailTmplSubject(tmpl.subject);
                                      setEditingEmailTmplContent(tmpl.content);
                                      const accountId = typeof tmpl.emailAccountId === 'object' && tmpl.emailAccountId !== null
                                        ? (tmpl.emailAccountId as any)._id || ''
                                        : tmpl.emailAccountId || '';
                                      setEditingEmailTmplAccountId(accountId);
                                    }}
                                    sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#f1f5f9' }, borderRadius: 1.5 }}
                                    title="Düzenle"
                                  >
                                    <IconPencil size={16} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => tmpl._id && handleDeleteEmailTemplate(tmpl._id)}
                                    sx={{ bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fecaca' }, borderRadius: 1.5 }}
                                    title="Sil"
                                  >
                                    <IconX size={16} />
                                  </IconButton>
                                </Box>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-line',
                                  bgcolor: '#ffffff',
                                  p: 2,
                                  borderRadius: 1.5,
                                  border: '1px dashed #e2e8f0',
                                  color: '#475569',
                                  fontSize: '0.9rem',
                                  lineHeight: 1.6
                                }}
                              >
                                {tmpl.content}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}


          {/* E-posta Hesapları (SMTP) Sekmesi */}
          {activeSection === 'email-accounts' && (
            <Box sx={{ height: 'calc(100vh - 150px)', overflow: 'auto', p: 1 }}>
              <Grid container spacing={3}>
                {/* Sol: Form */}
                <Grid size={{ xs: 12, md: 5 }}>
                  <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)', bgcolor: '#ffffff' }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconServer size={32} color="#0284c7" />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', ml: 2, color: '#152b69' }}>
                          {editingEmailAccountId ? 'Hesabı Düzenle' : 'Yeni SMTP Hesabı Ekle'}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                        E-posta şablonlarına bağlanacak SMTP gönderici hesaplarını buradan yapılandırın. Şifreler güvenli şekilde saklanır.
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                          label="Hesap Adı (Örn: Şirket Maili)"
                          size="small"
                          fullWidth
                          value={editingEmailAccountId ? editingEmailAccountName : newEmailAccountName}
                          onChange={(e) => editingEmailAccountId ? setEditingEmailAccountName(e.target.value) : setNewEmailAccountName(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />

                        <TextField
                          label="Gönderici E-posta Adresi"
                          size="small"
                          fullWidth
                          type="email"
                          value={editingEmailAccountId ? editingEmailAccountEmail : newEmailAccountEmail}
                          onChange={(e) => editingEmailAccountId ? setEditingEmailAccountEmail(e.target.value) : setNewEmailAccountEmail(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            label="SMTP Host"
                            size="small"
                            fullWidth
                            placeholder="smtp.gmail.com"
                            value={editingEmailAccountId ? editingEmailAccountHost : newEmailAccountHost}
                            onChange={(e) => editingEmailAccountId ? setEditingEmailAccountHost(e.target.value) : setNewEmailAccountHost(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                          />
                          <TextField
                            label="Port"
                            size="small"
                            sx={{ width: 110, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                            type="number"
                            value={editingEmailAccountId ? editingEmailAccountPort : newEmailAccountPort}
                            onChange={(e) => editingEmailAccountId ? setEditingEmailAccountPort(Number(e.target.value)) : setNewEmailAccountPort(Number(e.target.value))}
                          />
                        </Box>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={editingEmailAccountId ? editingEmailAccountSecure : newEmailAccountSecure}
                              onChange={(e) => editingEmailAccountId ? setEditingEmailAccountSecure(e.target.checked) : setNewEmailAccountSecure(e.target.checked)}
                              sx={{ color: '#0284c7', '&.Mui-checked': { color: '#0284c7' } }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: '#475569' }}>
                              SSL/TLS kullan (port 465 için genellikle gerekli)
                            </Typography>
                          }
                        />

                        <TextField
                          label="SMTP Kullanıcı Adı"
                          size="small"
                          fullWidth
                          value={editingEmailAccountId ? editingEmailAccountUser : newEmailAccountUser}
                          onChange={(e) => editingEmailAccountId ? setEditingEmailAccountUser(e.target.value) : setNewEmailAccountUser(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />

                        <TextField
                          label="SMTP Şifre / Uygulama Şifresi"
                          size="small"
                          fullWidth
                          type="password"
                          value={editingEmailAccountId ? editingEmailAccountPass : newEmailAccountPass}
                          onChange={(e) => editingEmailAccountId ? setEditingEmailAccountPass(e.target.value) : setNewEmailAccountPass(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                          helperText="Gmail kullanıyorsanız 'Uygulama Şifresi' oluşturmanız gerekebilir."
                        />

                        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            onClick={() => handleTestEmailAccount(!!editingEmailAccountId)}
                            disabled={testingSmtp}
                            startIcon={testingSmtp ? <CircularProgress size={16} /> : <IconWifi size={18} />}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#0284c7', color: '#0284c7' }}
                          >
                            {testingSmtp ? 'Test ediliyor...' : 'Bağlantıyı Test Et'}
                          </Button>

                          {editingEmailAccountId && (
                            <Button
                              variant="outlined"
                              color="inherit"
                              onClick={() => {
                                setEditingEmailAccountId(null);
                                setEditingEmailAccountName('');
                                setEditingEmailAccountEmail('');
                                setEditingEmailAccountHost('');
                                setEditingEmailAccountPort(587);
                                setEditingEmailAccountSecure(false);
                                setEditingEmailAccountUser('');
                                setEditingEmailAccountPass('');
                              }}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                              İptal
                            </Button>
                          )}

                          <Button
                            variant="contained"
                            onClick={editingEmailAccountId ? handleUpdateEmailAccount : handleCreateEmailAccount}
                            disabled={
                              editingEmailAccountId
                                ? (!editingEmailAccountName.trim() || !editingEmailAccountEmail.trim() || !editingEmailAccountHost.trim() || !editingEmailAccountUser.trim() || !editingEmailAccountPass.trim())
                                : (!newEmailAccountName.trim() || !newEmailAccountEmail.trim() || !newEmailAccountHost.trim() || !newEmailAccountUser.trim() || !newEmailAccountPass.trim())
                            }
                            sx={{
                              bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' },
                              borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3
                            }}
                          >
                            {editingEmailAccountId ? 'Kaydet' : 'Hesabı Ekle'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Sağ: Hesap Listesi */}
                <Grid size={{ xs: 12, md: 7 }}>
                  <Card sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    bgcolor: '#ffffff',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#152b69' }}>
                        Kayıtlı SMTP Hesapları ({emailAccounts.length})
                      </Typography>

                      {loadingEmailAccounts ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 8 }}>
                          <CircularProgress sx={{ color: '#0284c7' }} />
                        </Box>
                      ) : emailAccounts.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
                          <IconServer size={48} color="#cbd5e1" />
                          <Typography variant="body1" color="textSecondary" sx={{ mt: 2, mb: 1, fontStyle: 'italic' }}>
                            Kayıtlı SMTP hesabı bulunamadı.
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Sol taraftaki formdan ilk e-posta hesabınızı ekleyebilirsiniz.
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" flexDirection="column" gap={2} sx={{ maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
                          {emailAccounts.map((acc) => (
                            <Box
                              key={acc._id}
                              sx={{
                                p: 2.5,
                                border: editingEmailAccountId === acc._id ? '2px solid #0284c7' : '1px solid #e2e8f0',
                                borderRadius: 2,
                                bgcolor: editingEmailAccountId === acc._id ? '#f0f9ff' : '#f8fafc',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <IconServer size={18} color="#0284c7" />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                      {acc.name}
                                    </Typography>
                                    <Chip
                                      label={acc.secure ? 'SSL/TLS' : 'STARTTLS'}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        bgcolor: acc.secure ? '#dcfce7' : '#fef9c3',
                                        color: acc.secure ? '#166534' : '#854d0e',
                                        fontWeight: 600
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ color: '#475569', mb: 0.3 }}>
                                    📧 {acc.email}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                    🔌 {acc.host}:{acc.port} · 👤 {acc.user}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingEmailAccountId(acc._id || null);
                                      setEditingEmailAccountName(acc.name);
                                      setEditingEmailAccountEmail(acc.email);
                                      setEditingEmailAccountHost(acc.host);
                                      setEditingEmailAccountPort(acc.port);
                                      setEditingEmailAccountSecure(acc.secure);
                                      setEditingEmailAccountUser(acc.user);
                                      setEditingEmailAccountPass(acc.pass);
                                    }}
                                    sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#f1f5f9' }, borderRadius: 1.5 }}
                                    title="Düzenle"
                                  >
                                    <IconPencil size={16} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => acc._id && handleDeleteEmailAccount(acc._id)}
                                    sx={{ bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fecaca' }, borderRadius: 1.5 }}
                                    title="Sil"
                                  >
                                    <IconX size={16} />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}


          {/* Dialer Yönetimi Sekmesi */}
          {activeSection === 'campaigns' && (
            <Box sx={{ height: 'calc(100vh - 150px)', overflow: 'auto' }}>
              <CampaignDashboard />
            </Box>
          )}

          {/* Çağrı Analizleri Sekmesi */}
          {activeSection === 'analyses' && (currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && (
            <Box sx={{ height: 'calc(100vh - 150px)', overflow: 'auto', p: 1 }}>

              {/* Stats Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Toplam Analiz</Typography>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1, color: '#152b69' }}>{analysesStats.totalAnalyses || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Ortalama Kalite Skoru</Typography>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1, color: (analysesStats.averageScore || 0) >= 80 ? '#10b981' : ((analysesStats.averageScore || 0) >= 50 ? '#f59e0b' : '#ef4444') }}>
                        {analysesStats.averageScore ? Math.round(analysesStats.averageScore) : 0} / 100
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Pozitif / Nötr / Negatif</Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#10b981', display: 'block' }}>Pozitif</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>{analysesStats.positiveCount || 0}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#64748b', display: 'block' }}>Nötr</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#64748b' }}>{analysesStats.neutralCount || 0}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#ef4444', display: 'block' }}>Negatif</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ef4444' }}>{analysesStats.negativeCount || 0}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Sırada / Başarısız</Typography>
                      <Box sx={{ display: 'flex', gap: 4, mt: 1.5 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#3b82f6', display: 'block' }}>Bekleyen</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3b82f6' }}>
                            {Math.max(0, (analysesStats.totalAnalyses || 0) - (analysesStats.completedAnalyses || 0) - (analysesStats.failedAnalyses || 0))}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#ef4444', display: 'block' }}>Başarısız</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ef4444' }}>{analysesStats.failedAnalyses || 0}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 8px 28px rgba(15,23,42,0.08)', border: '1px solid #e2e8f0' }}>
                    <CardContent sx={{ p: 2.25 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#152b69' }}>
                            Yönetici Karar Özeti
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Aktif sayfadaki tamamlanan analizlere göre
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={analysesPageInsights.passRate >= 80 ? 'Baraj Üstü' : 'Yakın Takip'}
                          sx={{
                            bgcolor: analysesPageInsights.passRate >= 80 ? '#dcfce7' : '#fef3c7',
                            color: analysesPageInsights.passRate >= 80 ? '#166534' : '#92400e',
                            fontWeight: 800
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.25 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>Geçer Baraj</Typography>
                          <Typography sx={{ fontSize: '1.35rem', color: '#152b69', fontWeight: 900 }}>80 / 100</Typography>
                        </Box>
                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <Typography sx={{ fontSize: '0.72rem', color: '#166534', fontWeight: 700 }}>Başarı Oranı</Typography>
                          <Typography sx={{ fontSize: '1.35rem', color: '#047857', fontWeight: 900 }}>%{analysesPageInsights.passRate}</Typography>
                        </Box>
                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: analysesPageInsights.criticalCount > 0 ? '#fef2f2' : '#f8fafc', border: analysesPageInsights.criticalCount > 0 ? '1px solid #fecaca' : '1px solid #e2e8f0' }}>
                          <Typography sx={{ fontSize: '0.72rem', color: analysesPageInsights.criticalCount > 0 ? '#991b1b' : '#64748b', fontWeight: 700 }}>Kritik Alarm</Typography>
                          <Typography sx={{ fontSize: '1.35rem', color: analysesPageInsights.criticalCount > 0 ? '#dc2626' : '#152b69', fontWeight: 900 }}>{analysesPageInsights.criticalCount}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 8px 28px rgba(15,23,42,0.08)', border: '1px solid #e2e8f0' }}>
                    <CardContent sx={{ p: 2.25 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#152b69', mb: 1 }}>
                        En Çok Puan Kaybettiren Kriterler
                      </Typography>
                      {analysesPageInsights.topGaps.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {analysesPageInsights.topGaps.map((gap) => (
                            <Box key={gap.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1, borderRadius: 1.25, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {gap.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                                  {gap.count} çağrıda açık
                                </Typography>
                              </Box>
                              <Chip size="small" label={`-${gap.lost}`} sx={{ bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 900 }} />
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <Typography sx={{ color: '#166534', fontWeight: 700, fontSize: '0.85rem' }}>
                            Bu sayfada öne çıkan kriter kaybı görünmüyor.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Filters Panel */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid rgba(0,0,0,0.05)', mb: 3, bgcolor: '#f8fafc' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#152b69' }}>
                      🔍 Filtreleme & Senkronizasyon
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        onClick={() => setManualUploadDialogOpen(true)}
                        startIcon={<IconUpload size={18} />}
                        sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                      >
                        Ses Dosyası Yükle
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleTriggerSync}
                        disabled={analysesSyncing}
                        startIcon={analysesSyncing ? <CircularProgress size={16} color="inherit" /> : <IconFileAnalytics size={18} />}
                        sx={{ bgcolor: '#1e3a8a', '&:hover': { bgcolor: '#172554' } }}
                      >
                        {analysesSyncing ? 'Eşitleniyor...' : 'Verileri Eşitle'}
                      </Button>
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    {/* Zaman Aralığı (Dönem) */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                        📅 Zaman Aralığı
                      </Typography>
                      <Select
                        size="small"
                        value={filterDateRange}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFilterDateRange(val);
                          setAnalysesPage(1);
                          if (val !== 'custom') {
                            setFilterStartDate('');
                            setFilterEndDate('');
                            loadAnalyses({ page: 1, dateRange: val, startDate: '', endDate: '' });
                          }
                        }}
                        sx={{ width: '100%', bgcolor: 'white' }}
                      >
                        <MenuItem value="all">Tüm Zamanlar</MenuItem>
                        <MenuItem value="today">Bugün (Günlük)</MenuItem>
                        <MenuItem value="yesterday">Dün</MenuItem>
                        <MenuItem value="week">Bu Hafta (Son 7 Gün)</MenuItem>
                        <MenuItem value="month">Bu Ay (Son 30 Gün)</MenuItem>
                        <MenuItem value="custom">Özel Tarih Aralığı</MenuItem>
                      </Select>
                    </Grid>

                    {/* Özel Tarih Aralığı Seçilirse */}
                    {filterDateRange === 'custom' && (
                      <>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                            Başlangıç Tarihi
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={filterStartDate}
                            onChange={(e) => {
                              setFilterStartDate(e.target.value);
                              setAnalysesPage(1);
                              loadAnalyses({ page: 1, startDate: e.target.value });
                            }}
                            sx={{ width: '100%', bgcolor: 'white' }}
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                            Bitiş Tarihi
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={filterEndDate}
                            onChange={(e) => {
                              setFilterEndDate(e.target.value);
                              setAnalysesPage(1);
                              loadAnalyses({ page: 1, endDate: e.target.value });
                            }}
                            sx={{ width: '100%', bgcolor: 'white' }}
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Temsilci Seçimi */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                        👤 Temsilci
                      </Typography>
                      <Select
                        size="small"
                        value={filterAgentId}
                        onChange={(e) => {
                          setFilterAgentId(e.target.value);
                          setAnalysesPage(1);
                          loadAnalyses({ page: 1, agentId: e.target.value });
                        }}
                        sx={{ width: '100%', bgcolor: 'white' }}
                      >
                        <MenuItem value="all">Tüm Temsilciler</MenuItem>
                        {(() => {
                          const agentList = analysisFilterAgents.length > 0 ? analysisFilterAgents : allAgents;
                          return agentList.map((agent: any) => {
                            const name = `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.username || agent.email || 'İsimsiz';
                            return (
                              <MenuItem key={agent._id} value={agent._id}>
                                {name} {agent.role && agent.role !== 'agent' ? `(${agent.role})` : ''}
                              </MenuItem>
                            );
                          });
                        })()}
                      </Select>
                    </Grid>

                    {/* Kuyruk Seçimi */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                        🎯 Kuyruk
                      </Typography>
                      <Select
                        size="small"
                        value={filterQueueId}
                        onChange={(e) => {
                          setFilterQueueId(e.target.value);
                          setAnalysesPage(1);
                          loadAnalyses({ page: 1, queueId: e.target.value });
                        }}
                        sx={{ width: '100%', bgcolor: 'white' }}
                      >
                        <MenuItem value="all">Tüm Kuyruklar</MenuItem>
                        {(analysisFilterQueues.length > 0 ? analysisFilterQueues : allQueues).map((queue: any) => (
                          <MenuItem key={queue._id} value={queue._id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    {/* Arayan Numara */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                        📞 Telefon Numarası
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Arayan / aranan numara..."
                        value={filterCallerNumber}
                        onChange={(e) => {
                          setFilterCallerNumber(e.target.value);
                          setAnalysesPage(1);
                          loadAnalyses({ page: 1, callerNumber: e.target.value });
                        }}
                        sx={{ width: '100%', bgcolor: 'white' }}
                      />
                    </Grid>

                    {/* Analiz Durumu */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                        ⚙️ Analiz Durumu
                      </Typography>
                      <Select
                        size="small"
                        value={filterStatus}
                        onChange={(e) => {
                          setFilterStatus(e.target.value);
                          setAnalysesPage(1);
                          loadAnalyses({ page: 1, status: e.target.value });
                        }}
                        sx={{ width: '100%', bgcolor: 'white' }}
                      >
                        <MenuItem value="all">Tümü</MenuItem>
                        <MenuItem value="completed">Tamamlandı</MenuItem>
                        <MenuItem value="processing">İşleniyor</MenuItem>
                        <MenuItem value="second_processing">2. Analiz İşleniyor</MenuItem>
                        <MenuItem value="second_completed">2. Analiz Tamamlandı</MenuItem>
                        <MenuItem value="pending">Sırada Bekliyor</MenuItem>
                        <MenuItem value="failed">Başarısız</MenuItem>
                      </Select>
                    </Grid>

                    {/* Duygu Analizi */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                        🎭 Duygu Analizi
                      </Typography>
                      <Select
                        size="small"
                        value={filterSentiment}
                        onChange={(e) => {
                          setFilterSentiment(e.target.value);
                          setAnalysesPage(1);
                          loadAnalyses({ page: 1, sentiment: e.target.value });
                        }}
                        sx={{ width: '100%', bgcolor: 'white' }}
                      >
                        <MenuItem value="all">Tümü</MenuItem>
                        <MenuItem value="positive">Pozitif 😊</MenuItem>
                        <MenuItem value="neutral">Nötr 😐</MenuItem>
                        <MenuItem value="negative">Negatif 😞</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>

                  {/* Filtreleri Temizle Butonu */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setFilterCallerNumber('');
                        setFilterStatus('all');
                        setFilterSentiment('all');
                        setFilterAgentId('all');
                        setFilterQueueId('all');
                        setFilterDateRange('all');
                        setFilterStartDate('');
                        setFilterEndDate('');
                        setAnalysesPage(1);
                        loadAnalyses({
                          page: 1,
                          callerNumber: '',
                          status: 'all',
                          sentiment: 'all',
                          agentId: 'all',
                          queueId: 'all',
                          dateRange: 'all',
                          startDate: '',
                          endDate: ''
                        });
                      }}
                      startIcon={<span>🔄</span>}
                      sx={{ textTransform: 'none', color: '#64748b', borderColor: '#cbd5e1', '&:hover': { bgcolor: '#f1f5f9' } }}
                    >
                      Filtreleri Temizle
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Analyses Table */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#152b69' }}>
                      Ses Analiz Kayıtları
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<span>📊</span>}
                      onClick={handleExportAnalysesExcel}
                      sx={{ textTransform: 'none', fontWeight: 500, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                    >
                      Excel Raporu Al
                    </Button>
                  </Box>
                  {analysesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5 }}>
                      <CircularProgress />
                    </Box>
                  ) : analysesData.length === 0 ? (
                    <Box sx={{ p: 5, textAlign: 'center' }}>
                      <Typography color="textSecondary">Eşleşen ses analiz kaydı bulunamadı.</Typography>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Temsilci</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Müşteri</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Kuyruk</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sonuç Kodu</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Çağrı Tarihi</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Süre</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Duygu</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Kalite Skoru</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>1. Analiz</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>2. Analiz</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Karar</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Durum</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>İşlem</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analysesData.map((analysis) => {
                            let sentimentEmoji = '😐';
                            let sentimentColor = 'default';
                            if (analysis.sentiment === 'positive') { sentimentEmoji = '😊'; sentimentColor = 'success'; }
                            if (analysis.sentiment === 'negative') { sentimentEmoji = '😞'; sentimentColor = 'error'; }
                            if (analysis.sentiment === 'neutral') { sentimentEmoji = '😐'; sentimentColor = 'warning'; }

                            let statusColor: 'default' | 'primary' | 'success' | 'error' | 'warning' = 'default';
                            let statusText = 'Bekliyor';
                            if (analysis.status === 'processing') { statusColor = 'warning'; statusText = 'İşleniyor'; }
                            if (analysis.status === 'completed') { statusColor = 'success'; statusText = 'Tamamlandı'; }
                            if (analysis.status === 'failed') {
                              if (analysis.errorMessage && analysis.errorMessage.includes('Analiz Edilemedi')) {
                                statusColor = 'default';
                                statusText = analysis.errorMessage;
                              } else {
                                statusColor = 'error';
                                statusText = 'Başarısız';
                              }
                            }
                            // 1. analiz bitti ama 2. analiz suruyorsa durumu belirt
                            if (analysis.status === 'completed' && ['pending', 'processing'].includes(analysis.secondAnalysis?.status)) {
                              statusColor = 'warning';
                              statusText = '2. Analiz İşleniyor';
                            }
                            const qualityDecision = getQualityDecision(analysis);

                            return (
                              <TableRow key={analysis._id} hover>
                                <TableCell>
                                  {(() => {
                                    const analysisAgent = (analysis.callId?.agentId && typeof analysis.callId.agentId === 'object')
                                      ? analysis.callId.agentId
                                      : null;
                                    const agentId = analysisAgent
                                      ? analysisAgent._id
                                      : (analysis.callId?.agentId || analysis.agentId);
                                    return agentId ? (
                                      <Button
                                        onClick={() => handleOpenAgentStats(agentId, analysis.agentName, analysisAgent)}
                                        variant="text"
                                        sx={{
                                          textTransform: 'none',
                                          p: 0,
                                          minWidth: 0,
                                          textAlign: 'left',
                                          justifyContent: 'flex-start',
                                          color: '#3b82f6',
                                          fontWeight: '600',
                                          fontFamily: 'inherit',
                                          fontSize: 'inherit',
                                          '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
                                        }}
                                      >
                                        {analysis.agentName}
                                      </Button>
                                    ) : (
                                      analysis.agentName
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>{analysis.callerNumber}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={analysis.queueName || analysis.callId?.queueId?.name || '-'}
                                    sx={{
                                      bgcolor: '#eff6ff',
                                      color: '#1d4ed8',
                                      fontWeight: 600,
                                      border: '1px solid #bfdbfe'
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {analysis.finishCode || analysis.callId?.finishCode ? (
                                    <Chip
                                      size="small"
                                      label={analysis.finishCode || analysis.callId?.finishCode}
                                      sx={{
                                        bgcolor: '#f0fdf4',
                                        color: '#15803d',
                                        fontWeight: 600,
                                        border: '1px solid #bbf7d0'
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="textSecondary">-</Typography>
                                  )}
                                </TableCell>
                                <TableCell>{new Date(analysis.callStartTime).toLocaleString('tr-TR')}</TableCell>
                                <TableCell>{analysis.callDuration} sn</TableCell>
                                <TableCell>
                                  {analysis.status === 'completed' ? (
                                    <Tooltip title={`Güven Skoru: %${Math.round(analysis.sentimentScore * 100)}`}>
                                      <Chip size="small" label={`${sentimentEmoji} ${analysis.sentiment === 'positive' ? 'Pozitif' : (analysis.sentiment === 'negative' ? 'Negatif' : 'Nötr')}`} color={sentimentColor as any} />
                                    </Tooltip>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>
                                  {analysis.status === 'completed' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: analysis.score >= 80 ? '#10b981' : (analysis.score >= 50 ? '#f59e0b' : '#ef4444') }}>
                                        {analysis.score}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">/100</Typography>
                                    </Box>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>{renderAnalizPuani(analysis, 1)}</TableCell>
                                <TableCell>{renderAnalizPuani(analysis, 2)}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={qualityDecision.label}
                                    sx={{
                                      bgcolor: qualityDecision.bg,
                                      color: qualityDecision.color,
                                      border: `1px solid ${qualityDecision.border}`,
                                      fontWeight: 800
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" label={statusText} color={statusColor} />
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={analysis.status !== 'completed'}
                                    onClick={async () => {
                                      setSelectedAnalysis(analysis);
                                      setModalTab(0);
                                      setDetailModalOpen(true);
                                    }}
                                  >
                                    Detayları Gör
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {/* Pagination */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <Pagination
                      count={Math.ceil(analysesTotal / analysesLimit)}
                      page={analysesPage}
                      onChange={(_e: any, page: number) => setAnalysesPage(page)}
                      color="primary"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>

      {/* Inline audio player dialog for supervisor calls */}
      <Dialog open={playerDialog.open} onClose={() => setPlayerDialog({ ...playerDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Çağrı Kaydı - {playerDialog.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <div ref={waveformRef} style={{ width: '100%' }} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <IconButton onClick={togglePlayPause} size="small" sx={{ mr: 1, bgcolor: '#2563eb', color: 'white' }}>
                {isPlaying ? <IconPlayerPause size={18} /> : <IconPlayerPlay size={18} />}
              </IconButton>
              <IconButton onClick={stopPlayback} size="small" sx={{ mr: 1 }}>
                <IconPlayerStop size={18} />
              </IconButton>
              <Typography variant="caption">{new Date(currentTime * 1000).toISOString().substr(11, 8)} / {new Date(duration * 1000).toISOString().substr(11, 8)}</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '35%' }}>
              <IconVolume size={16} />
              <Slider
                value={volume}
                min={0}
                max={1}
                step={0.01}
                onChange={(_e, v) => changeVolume(v as number)}
              />
            </Box>
          </Box>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <Button variant="contained" onClick={() => setPlayerDialog({ ...playerDialog, open: false })}>Kapat</Button>
        </Box>
      </Dialog>

      {/* Call Analysis Details Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedAnalysis && (
          <>
            <DialogTitle sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#152b69', fontFamily: 'Arial' }}>
                  Çağrı Değerlendirme Detayı
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="caption" color="textSecondary">
                    Temsilci: <strong style={{ color: '#0f172a' }}>{selectedAnalysis.agentName}</strong>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">•</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Müşteri: <strong style={{ color: '#0f172a' }}>{selectedAnalysis.callerNumber}</strong>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">•</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Kuyruk: <strong style={{ color: '#1d4ed8' }}>{selectedAnalysis.queueName || selectedAnalysis.callId?.queueId?.name || 'Bilinmiyor'}</strong>
                  </Typography>
                  {selectedAnalysis.finishCode || selectedAnalysis.callId?.finishCode ? (
                    <>
                      <Typography variant="caption" color="textSecondary">•</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Sonuç Kodu: <strong style={{ color: '#15803d' }}>{selectedAnalysis.finishCode || selectedAnalysis.callId?.finishCode}</strong>
                      </Typography>
                    </>
                  ) : null}
                  <Typography variant="caption" color="textSecondary">•</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tarih: {new Date(selectedAnalysis.callStartTime).toLocaleString('tr-TR')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {(() => {
                  const decision = getQualityDecision(selectedAnalysis);
                  return (
                    <Chip
                      size="small"
                      label={decision.label}
                      sx={{
                        bgcolor: decision.bg,
                        color: decision.color,
                        border: `1px solid ${decision.border}`,
                        fontWeight: 900
                      }}
                    />
                  );
                })()}
                <IconButton onClick={() => setDetailModalOpen(false)} size="small">
                  <IconX />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}>
                <Tabs value={modalTab} onChange={(_e, val) => { setModalTab(val); if (val === 3 && selectedAnalysis?._id && !selectedAnalysis?._karsilastirmaYuklendi) { loadAnalizKarsilastirma(selectedAnalysis._id); } }} color="primary">
                  <Tab label="Özet & Transkript" />
                  <Tab label="Değerlendirme Kriterleri" />
                  <Tab label="Anahtar Kelimeler & Duygu" />
                  {selectedAnalysis?.secondAnalysis?.status && <Tab label="1. / 2. Analiz Karşılaştırma" />}
                </Tabs>
              </Box>

              <Box sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
                {/* Tab 3: 1. / 2. analiz karsilastirmasi */}
                {modalTab === 3 && selectedAnalysis?.secondAnalysis?.status && (() => {
                  const ilk: any = selectedAnalysis.firstAnalysis || {};
                  const ikinci: any = selectedAnalysis.secondAnalysis || {};
                  const gecerli = Number(selectedAnalysis.officialSource || 1);
                  const ikinciTamam = ikinci.status === 'completed';
                  const ikinciSuruyor = ikinci.status === 'pending' || ikinci.status === 'processing';

                  if (selectedAnalysis._karsilastirmaHata) {
                    return <Alert severity="error">Karşılaştırma verisi alınamadı. Pencereyi kapatıp tekrar deneyin.</Alert>;
                  }
                  if (!selectedAnalysis._karsilastirmaYuklendi) {
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, p: 4 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="textSecondary">Analizler yükleniyor...</Typography>
                      </Box>
                    );
                  }

                  const ilkOzet: any = parseAnalysisSummary(ilk.summary) || (gecerli === 1 ? parseAnalysisSummary(selectedAnalysis.summary) : null) || {};
                  const ikinciOzet: any = parseAnalysisSummary(ikinci.summary) || {};
                  const ilkPuan = ilk.score ?? (gecerli === 1 ? selectedAnalysis.score : null);
                  const ilkTranskript = ilk.transcription ?? (gecerli === 1 ? selectedAnalysis.transcription : '');

                  const ilkKriterler: any[] = Array.isArray(ilkOzet.criteria) ? ilkOzet.criteria : [];
                  const ikinciKriterler: any[] = Array.isArray(ikinciOzet.criteria) ? ikinciOzet.criteria : [];
                  const adlar: string[] = [];
                  [...ilkKriterler, ...ikinciKriterler].forEach((c: any) => {
                    if (c?.name && !adlar.includes(c.name)) adlar.push(c.name);
                  });
                  const bul = (liste: any[], ad: string) => liste.find((c: any) => c?.name === ad);
                  const hucre = (c: any) => {
                    if (!c) return '-';
                    if (c.applicable === false) return 'N/A';
                    return `${c.earned ?? 0} / ${c.max ?? 0}`;
                  };
                  const puanRengi = (p: any) => {
                    const n = Number(p || 0);
                    return n >= 80 ? '#10b981' : (n >= 50 ? '#f59e0b' : '#ef4444');
                  };
                  const cezalar = (ozet: any) => (Array.isArray(ozet.penalties) ? ozet.penalties.filter((p: any) => p?.occurred) : []);

                  const puanKarti = (baslik: string, puan: any, tur: number, altMetin?: string) => (
                    <Card sx={{
                      flex: 1, p: 2, borderRadius: 2, boxShadow: 'none',
                      border: gecerli === tur && ikinciTamam ? '2px solid #10b981' : '1px solid #e2e8f0',
                      bgcolor: gecerli === tur && ikinciTamam ? '#f0fdf4' : 'white'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>{baslik}</Typography>
                        {gecerli === tur && ikinciTamam && <Chip size="small" label="Geçerli" color="success" sx={{ height: 20, fontSize: '0.68rem' }} />}
                      </Box>
                      {altMetin ? (
                        <Typography sx={{ mt: 1, fontWeight: 700, color: '#b45309' }}>{altMetin}</Typography>
                      ) : (
                        <Typography variant="h4" sx={{ fontWeight: 900, mt: 0.5, color: puanRengi(puan) }}>
                          {puan ?? '-'}<Typography component="span" variant="body2" color="textSecondary"> / 100</Typography>
                        </Typography>
                      )}
                    </Card>
                  );

                  const transkriptKutusu = (metin: any) => (
                    <Box sx={{
                      maxHeight: 360, overflowY: 'auto', p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                      borderRadius: 1.5, whiteSpace: 'pre-wrap', fontSize: '0.8rem', lineHeight: 1.55, color: '#1e293b'
                    }}>
                      {metin ? String(metin) : 'Transkript bulunmuyor.'}
                    </Box>
                  );

                  return (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        {puanKarti('1. Analiz', ilkPuan, 1)}
                        {puanKarti('2. Analiz', ikinci.score, 2,
                          ikinciSuruyor ? 'Analiz ediliyor' : (!ikinciTamam ? (ikinci.errorMessage || 'Yapılamadı') : undefined))}
                      </Box>
                      {ikinciTamam && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                          Resmi skor iki analizden yüksek olanıdır ({gecerli}. analiz).
                          {ikinci.completedAt ? ` 2. analiz: ${new Date(ikinci.completedAt).toLocaleString('tr-TR')}` : ''}
                        </Typography>
                      )}

                      {ikinciTamam && adlar.length > 0 && (
                        <>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 1, color: '#334155' }}>Kriter Karşılaştırması</Typography>
                          <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, mb: 2.5 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Kriter</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 110 }}>1. Analiz</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 110 }}>2. Analiz</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 80 }}>Fark</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {adlar.map((ad) => {
                                  const c1 = bul(ilkKriterler, ad);
                                  const c2 = bul(ikinciKriterler, ad);
                                  const h1 = hucre(c1);
                                  const h2 = hucre(c2);
                                  const sayisal = c1 && c2 && c1.applicable !== false && c2.applicable !== false;
                                  const fark = sayisal ? Number(c2.earned || 0) - Number(c1.earned || 0) : null;
                                  const farkli = h1 !== h2;
                                  return (
                                    <TableRow key={ad} sx={{ bgcolor: farkli ? '#fffbeb' : 'inherit' }}>
                                      <TableCell sx={{ fontWeight: farkli ? 700 : 400 }}>
                                        {ad}
                                        {farkli && (c1?.evidence || c2?.evidence) && (
                                          <Box sx={{ mt: 0.5, fontSize: '0.72rem', color: '#64748b' }}>
                                            <div>1: {c1?.evidence || '-'}</div>
                                            <div>2: {c2?.evidence || '-'}</div>
                                          </Box>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'center' }}>{h1}</TableCell>
                                      <TableCell sx={{ textAlign: 'center' }}>{h2}</TableCell>
                                      <TableCell sx={{ textAlign: 'center', fontWeight: 700, color: fark === null || fark === 0 ? '#64748b' : (fark > 0 ? '#047857' : '#b91c1c') }}>
                                        {fark === null ? (farkli ? '≠' : '-') : (fark > 0 ? `+${fark}` : fark)}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}

                      <Grid container spacing={2}>
                        {[
                          { tur: 1, baslik: '1. Analiz', ozet: ilkOzet, transkript: ilkTranskript, hazir: true },
                          { tur: 2, baslik: '2. Analiz', ozet: ikinciOzet, transkript: ikinci.transcription, hazir: ikinciTamam }
                        ].map((kolon) => (
                          <Grid key={kolon.tur} size={{ xs: 12, md: 6 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 1, color: '#334155' }}>{kolon.baslik}</Typography>
                            {!kolon.hazir ? (
                              <Alert severity={ikinciSuruyor ? 'info' : 'warning'}>
                                {ikinciSuruyor ? '2. analiz devam ediyor, tamamlanınca burada görünecek.' : (ikinci.errorMessage || '2. analiz yapılamadı.')}
                              </Alert>
                            ) : (
                              <>
                                {kolon.ozet?.call_summary && (
                                  <Typography variant="body2" sx={{ mb: 1.5, color: '#334155' }}>{kolon.ozet.call_summary}</Typography>
                                )}
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Cezalar: </Typography>
                                  {cezalar(kolon.ozet).length === 0 ? (
                                    <Typography variant="caption" color="textSecondary">Yok</Typography>
                                  ) : cezalar(kolon.ozet).map((p: any) => (
                                    <Chip key={`${kolon.tur}-${p.id}`} size="small" label={`${p.name} (${p.penalty})`}
                                      sx={{ mr: 0.5, mb: 0.5, bgcolor: '#fef2f2', color: '#b91c1c', height: 20, fontSize: '0.7rem' }} />
                                  ))}
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 0.5 }}>Transkript</Typography>
                                {transkriptKutusu(kolon.transkript)}
                              </>
                            )}
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  );
                })()}

                {/* Tab 0: Summary & Transcription */}
                {modalTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      {(() => {
                        const summaryObj = parseAnalysisSummary(selectedAnalysis.summary);
                        const decision = getQualityDecision(selectedAnalysis);
                        const criticalPenalties = getCriticalPenalties(summaryObj);
                        const topGaps = getCriteriaGaps(summaryObj, 3);
                        return (
                          <Card sx={{
                            borderRadius: 2,
                            border: `1px solid ${decision.border}`,
                            bgcolor: decision.bg,
                            boxShadow: 'none'
                          }}>
                            <CardContent sx={{ p: 2 }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: decision.color, textTransform: 'uppercase' }}>
                                    Denetim Kararı
                                  </Typography>
                                  <Typography sx={{ fontSize: '1.9rem', fontWeight: 900, color: decision.color, lineHeight: 1.15 }}>
                                    {decision.label}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.5 }}>
                                    Skor: {selectedAnalysis.score || 0} / 100
                                  </Typography>
                                  {selectedAnalysis.secondAnalysis?.status && (
                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                                      1. Analiz: {selectedAnalysis.firstAnalysis?.score ?? selectedAnalysis.score ?? '-'} · 2. Analiz: {selectedAnalysis.secondAnalysis.status === 'completed'
                                        ? selectedAnalysis.secondAnalysis.score
                                        : (['pending', 'processing'].includes(selectedAnalysis.secondAnalysis.status) ? 'Analiz ediliyor' : '-')}
                                      {selectedAnalysis.secondAnalysis.status === 'completed' ? ` (geçerli: ${selectedAnalysis.officialSource || 1}. analiz)` : ''}
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155', mb: 0.75 }}>
                                    Kritik Uyum Kontrolü
                                  </Typography>
                                  {criticalPenalties.length > 0 ? (
                                    criticalPenalties.map((p: any) => (
                                      <Typography key={p.id} sx={{ fontSize: '0.82rem', color: '#991b1b', fontWeight: 700 }}>
                                        {p.name}: {p.evidence || 'kritik hata tespit edildi'}
                                      </Typography>
                                    ))
                                  ) : (
                                    <Typography sx={{ fontSize: '0.82rem', color: '#166534', fontWeight: 700 }}>
                                      Sıfırlayan kritik hata tespit edilmedi.
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 5 }}>
                                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155', mb: 0.75 }}>
                                    En Büyük Kriter Açıkları
                                  </Typography>
                                  {topGaps.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                      {topGaps.map((gap: any) => (
                                        <Chip
                                          key={`${gap.id}-${gap.name}`}
                                          size="small"
                                          label={`${gap.name}: -${gap.lost}`}
                                          sx={{ bgcolor: '#fff', color: '#92400e', fontWeight: 800, border: '1px solid #fde68a' }}
                                        />
                                      ))}
                                    </Box>
                                  ) : (
                                    <Typography sx={{ fontSize: '0.82rem', color: '#166534', fontWeight: 700 }}>
                                      Belirgin kriter açığı bulunmadı.
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        );
                      })()}
                    </Grid>

                    {/* Audio Player Button */}
                    <Grid size={{ xs: 12 }}>
                      <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', borderRadius: 2 }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton
                              onClick={() => {
                                // Open standard WaveSurfer dialog
                                const url = crPlay(selectedAnalysis.callId._id || selectedAnalysis.callId);
                                if (url) {
                                  setPlayerDialog({
                                    open: true,
                                    audioUrl: url,
                                    title: `${selectedAnalysis.agentName} • ${selectedAnalysis.callerNumber}`
                                  });
                                }
                              }}
                              sx={{ bgcolor: '#2563eb', color: 'white', '&:hover': { bgcolor: '#1d4ed8' } }}
                            >
                              <IconPlayerPlay size={20} />
                            </IconButton>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>Ses Kaydını Oynat</Typography>
                              <Typography variant="caption" color="textSecondary">Çağrının ses dökümü ve dalga formu oynatıcısı</Typography>
                            </Box>
                          </Box>
                          <Button
                            variant="outlined"
                            onClick={async () => {
                              try {
                                await crDownload(selectedAnalysis.callId._id || selectedAnalysis.callId);
                              } catch (error) {
                                console.error('Download error', error);
                                showSnackbar('Kayıt indirilirken hata oluştu', 'error');
                              }
                            }}
                            startIcon={<IconDownload size={18} />}
                          >
                            İndir
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* AI Summary */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#152b69', mb: 1 }}>
                        🤖 Yapay Zeka Özeti
                      </Typography>
                      <Card sx={{ bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#1e3a8a', whiteSpace: 'pre-line' }}>
                            {(() => {
                              try {
                                const summaryObj = typeof selectedAnalysis.summary === 'string'
                                  ? JSON.parse(selectedAnalysis.summary)
                                  : selectedAnalysis.summary;
                                return parsedSummaryText(summaryObj);
                              } catch (e) {
                                return 'Özet ayrıştırılamadı.';
                              }
                            })()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Transcript */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#152b69', mb: 1 }}>
                        🗣️ Ses Dökümü (Transkript)
                      </Typography>
                      <Box sx={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: '#fafafa',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        {selectedAnalysis.transcription ? (
                          selectedAnalysis.transcription.split('\n').map((line: string, index: number) => {
                            const isAgent = line.startsWith('Temsilci:') || line.toLowerCase().includes('agent:') || line.toLowerCase().includes('temsilci:');
                            return (
                              <Box key={index} sx={{ mb: 1.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isAgent ? 'bold' : 'normal',
                                    color: isAgent ? '#2563eb' : '#374151'
                                  }}
                                >
                                  {line}
                                </Typography>
                              </Box>
                            );
                          })
                        ) : (
                          <Typography variant="body2" color="textSecondary">Deşifre metni bulunmamaktadır.</Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {/* Tab 1: Quality Criteria details */}
                {modalTab === 1 && (
                  <Box>
                    {/* Score card summary */}
                    <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                      <Card sx={{ flex: 1, p: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                        <Typography variant="caption" color="textSecondary">Değerlendirme Puanı</Typography>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {selectedAnalysis.score} / 100
                        </Typography>
                      </Card>
                      {(() => {
                        try {
                          const summaryObj = typeof selectedAnalysis.summary === 'string'
                            ? JSON.parse(selectedAnalysis.summary)
                            : selectedAnalysis.summary;

                          const earnedSum = summaryObj.criteria?.reduce((sum: number, c: any) => sum + (c.earned || 0), 0) || 0;
                          const maxSum = summaryObj.criteria?.reduce((sum: number, c: any) => sum + (c.max || 0), 0) || 0;
                          const penaltySum = summaryObj.penalties?.reduce((sum: number, p: any) => sum + (p.occurred ? (p.penalty || 0) : 0), 0) || 0;
                          const criticalOccurred = summaryObj.penalties?.some((p: any) => p.occurred && p.is_critical) || false;

                          return (
                            <>
                              <Card sx={{ flex: 1, p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                <Typography variant="caption" color="textSecondary">Kazanılan / Maksimum Puan</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: '#374151' }}>
                                  {earnedSum} / {maxSum}
                                </Typography>
                              </Card>
                              <Card sx={{ flex: 1, p: 2, bgcolor: penaltySum < 0 ? '#fef2f2' : 'white', border: penaltySum < 0 ? '1px solid #fecaca' : '1px solid #e2e8f0', borderRadius: 2 }}>
                                <Typography variant="caption" color="textSecondary">Toplam Ceza Puanı</Typography>
                                <Typography variant="h5" color={penaltySum < 0 ? 'error.main' : 'textSecondary'} sx={{ fontWeight: 'bold', mt: 1 }}>
                                  {penaltySum} {criticalOccurred && '(Kritik Hata!)'}
                                </Typography>
                              </Card>
                            </>
                          );
                        } catch (e) {
                          return null;
                        }
                      })()}
                    </Box>

                    {/* Criteria Table */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#152b69', mb: 1 }}>
                      📋 Kriter Değerlendirmeleri
                    </Typography>
                    <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 4 }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Kriter Adı</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '80px', textAlign: 'center' }}>Skor</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Kanıt / Değerlendirme</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            try {
                              const summaryObj = typeof selectedAnalysis.summary === 'string'
                                ? JSON.parse(selectedAnalysis.summary)
                                : selectedAnalysis.summary;
                              return summaryObj.criteria?.map((c: any) => (
                                <TableRow key={c.id} hover>
                                  <TableCell sx={{ fontWeight: 500 }}>{c.name}</TableCell>
                                  <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: c.earned > 0 ? '#10b981' : '#ef4444' }}>
                                    {c.earned} / {c.max}
                                  </TableCell>
                                  <TableCell sx={{ color: '#4b5563', fontSize: '0.85rem' }}>{c.evidence || c.reason || '-'}</TableCell>
                                </TableRow>
                              )) || <TableRow><TableCell colSpan={3}>Kriter bulunamadı.</TableCell></TableRow>;
                            } catch (e) {
                              return <TableRow><TableCell colSpan={3}>Veri ayrıştırılamadı.</TableCell></TableRow>;
                            }
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Penalties Table */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#152b69', mb: 1 }}>
                      🚨 Kritik Hatalar ve Cezalar
                    </Typography>
                    <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ceza Türü</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '80px', textAlign: 'center' }}>Ceza Puanı</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '120px', textAlign: 'center' }}>Durum</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Değerlendirme</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            try {
                              const summaryObj = typeof selectedAnalysis.summary === 'string'
                                ? JSON.parse(selectedAnalysis.summary)
                                : selectedAnalysis.summary;
                              return summaryObj.penalties?.map((p: any) => (
                                <TableRow key={p.id} hover sx={{ bgcolor: p.occurred ? '#fff5f5' : 'inherit' }}>
                                  <TableCell sx={{ fontWeight: 500 }}>
                                    {p.name} {p.is_critical && <Chip size="small" label="Kritik" color="error" sx={{ ml: 1, height: '18px', fontSize: '0.65rem' }} />}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#ef4444' }}>
                                    {p.penalty || 0}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'center' }}>
                                    <Chip size="small" label={p.occurred ? 'Hata Var' : 'Yok'} color={p.occurred ? 'error' : 'default'} />
                                  </TableCell>
                                  <TableCell sx={{ color: '#4b5563', fontSize: '0.85rem' }}>{p.evidence || p.reason || '-'}</TableCell>
                                </TableRow>
                              )) || <TableRow><TableCell colSpan={4}>Ceza kriteri bulunamadı.</TableCell></TableRow>;
                            } catch (e) {
                              return <TableRow><TableCell colSpan={4}>Veri ayrıştırılamadı.</TableCell></TableRow>;
                            }
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Tab 2: Keywords & Sentiment */}
                {modalTab === 2 && (
                  <Grid container spacing={3}>
                    {/* Sentiment Gauge */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, height: '100%' }}>
                        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#152b69', mb: 2 }}>
                            Duygu Analiz Çarkı
                          </Typography>
                          <Box sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            background: selectedAnalysis.sentiment === 'positive'
                              ? 'conic-gradient(#10b981 0deg 360deg)'
                              : (selectedAnalysis.sentiment === 'negative' ? 'conic-gradient(#ef4444 0deg 360deg)' : 'conic-gradient(#64748b 0deg 360deg)'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              width: 100,
                              height: 100,
                              borderRadius: '50%',
                              bgcolor: 'white'
                            }
                          }}>
                            <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                              <Typography variant="h3" sx={{ m: 0 }}>
                                {selectedAnalysis.sentiment === 'positive' ? '😊' : (selectedAnalysis.sentiment === 'negative' ? '😞' : '😐')}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mt: 0.5, color: '#374151' }}>
                                {selectedAnalysis.sentiment === 'positive' ? 'POZİTİF' : (selectedAnalysis.sentiment === 'negative' ? 'NEGATİF' : 'NÖTR')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ mt: 3, fontWeight: 500, color: '#4b5563' }}>
                            Duygu Analizi Güven Skoru: %{Math.round(selectedAnalysis.sentimentScore * 100)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Keywords Cloud */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, height: '100%' }}>
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#152b69', mb: 2 }}>
                            Önemli Anahtar Kelimeler
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedAnalysis.keywords && selectedAnalysis.keywords.length > 0 ? (
                              selectedAnalysis.keywords.map((kw: string, index: number) => (
                                <Chip key={index} label={kw} variant="outlined" color="primary" />
                              ))
                            ) : (
                              <Typography variant="body2" color="textSecondary">Anahtar kelime bulunamadı.</Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', p: 2, bgcolor: '#f8fafc' }}>
              <Button variant="contained" onClick={() => setDetailModalOpen(false)}>
                Kapat
              </Button>
            </Box>
          </>
        )}
      </Dialog>

      {/* Temsilci Genel İstatistik ve Özet Modalı */}
      <Dialog
        open={agentStatsDialogOpen}
        onClose={handleCloseAgentStatsDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)' } }}
      >
        <DialogTitle sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

            <Box sx={{
              width: 380,
              height: 240,
              borderRadius: '12px',
              overflow: 'hidden',
              position: 'relative',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 40%, #2563eb 100%)',
              boxShadow: '0 10px 40px rgba(30, 58, 138, 0.5), 0 2px 8px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.15)',
              fontFamily: '"Inter", "Roboto", sans-serif',
            }}>
              {/* Decorative diagonal stripe */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '120px',
                height: '100%',
                background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.06) 100%)',
                clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0 100%)',
              }} />

              {/* Top bar - Logo + Title */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.15)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img src="/percrm-logo.svg" alt="PerCRM" style={{ height: '18px', width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                </Box>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#f59e0b', letterSpacing: 2.5, textTransform: 'uppercase' }}>
                  Personel Kimlik Kartı
                </Typography>
              </Box>

              {/* Main content - Photo left, Info right */}
              <Box sx={{ display: 'flex', px: 2, py: 1.5, gap: 2, flex: 1 }}>

                {/* Left - Photo */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Box sx={{
                    width: 90,
                    height: 110,
                    borderRadius: '6px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    overflow: 'hidden',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {selectedAgentForStats?.profilePicture ? (
                      <Box component="img" src={selectedAgentForStats.profilePicture} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Typography sx={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'rgba(255,255,255,0.5)' }}>
                        {selectedAgentForStats?.firstName?.[0] || selectedAgentForStats?.name?.charAt(0) || 'A'}{selectedAgentForStats?.lastName?.[0]}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{
                    bgcolor: selectedAgentForStats?.bloodType ? '#dc2626' : 'rgba(255,255,255,0.15)',
                    px: 1.2,
                    py: 0.2,
                    borderRadius: '4px',
                  }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', letterSpacing: 0.5 }}>
                      {selectedAgentForStats?.bloodType || '—'}
                    </Typography>
                  </Box>
                </Box>

                {/* Right - Info */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                  {/* Name */}
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#ffffff', lineHeight: 1.2, mb: 0.3 }}>
                    {selectedAgentForStats?.firstName ? `${selectedAgentForStats.firstName} ${selectedAgentForStats.lastName || ''}` : selectedAgentForStats?.name}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.6rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: 1.5, mb: 1.2,
                    display: 'inline-block',
                    bgcolor: 'rgba(245, 158, 11, 0.15)',
                    px: 1,
                    py: 0.2,
                    borderRadius: '3px',
                    width: 'fit-content'
                  }}>
                    {selectedAgentForStats?.role === 'admin' ? 'Yönetici' : selectedAgentForStats?.role === 'supervisor' ? 'Süpervizör' : 'Müşteri Temsilcisi'}
                  </Typography>

                  {/* Detail rows */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', minWidth: 65, textTransform: 'uppercase' }}>Acil İrtibat</Typography>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {selectedAgentForStats?.emergencyContactName || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', minWidth: 65, textTransform: 'uppercase' }}>Telefon</Typography>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#4ade80' }}>
                        {selectedAgentForStats?.emergencyContactPhone || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', minWidth: 65, textTransform: 'uppercase' }}>Adres</Typography>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {selectedAgentForStats?.address || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Bottom bar */}
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '20px',
                background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color: '#1e3a8a', letterSpacing: 3, textTransform: 'uppercase' }}>
                  PerCRM • Personel Yönetim Sistemi
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                {selectedAgentForStats?.name} - Temsilci Performans Özeti
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Analiz Edilen Toplam Çağrı: {agentStatsData.totalAnalyzed} adet
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseAgentStatsDialog} size="small" sx={{ color: '#64748b' }}>
            <IconX />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: '#fdfdfd' }}>
          {agentStatsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
              <CircularProgress size={48} sx={{ color: '#3b82f6' }} />
              <Typography variant="body2" color="textSecondary">İstatistikler hesaplanıyor, lütfen bekleyin...</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>

              {/* Rapor Dönemi Altbaşlığı */}
              {agentStatsData.dateRange && (
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconClock size={16} />
                  Rapor Dönemi: {new Date(agentStatsData.dateRange.startDate).toLocaleDateString('tr-TR')} - {new Date(agentStatsData.dateRange.endDate).toLocaleDateString('tr-TR')} (Son 30 Gün)
                </Typography>
              )}

              {/* 1. PERFORMANS ÖZETİ VE ANA METRİKLER (PDF 3 Ana KPI) */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a8a', mb: -1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                1. Performans Özeti ve Ana Metrikler
              </Typography>
              
              <Grid container spacing={2.5}>
                {[
                  { label: 'Analiz Edilen Çağrı', value: `${agentStatsData.totalAnalyzed} Adet`, colorClass: '#3b82f6', sub: 'Toplam Ses Analizi' },
                  { label: 'Ortalama Skor', value: `${agentStatsData.overallAverage || 0} Puan`, colorClass: (agentStatsData.overallAverage || 0) >= 80 ? '#10b981' : ((agentStatsData.overallAverage || 0) >= 60 ? '#f59e0b' : '#ef4444'), sub: 'Hedef: 80+ Puan' },
                  { label: 'Hedefe Ulaşma Oranı', value: `%${agentStatsData.qualityRate || 0}`, colorClass: (agentStatsData.qualityRate || 0) >= 80 ? '#10b981' : ((agentStatsData.qualityRate || 0) >= 50 ? '#f59e0b' : '#ef4444'), sub: '80+ Puan Alan Çağrı Oranı' }
                ].map((card, index) => (
                  <Grid size={{ xs: 12, sm: 4 }} key={index}>
                    <Card sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 3.5,
                      boxShadow: 'none',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: card.colorClass
                      }
                    }}>
                      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {card.label}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: '800', color: card.colorClass, fontFamily: 'monospace' }}>
                          {card.value}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {card.sub}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Dönemsel Gelişim Skorları */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a8a', mb: -1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Dönemsel Gelişim Skorları
              </Typography>

              <Grid container spacing={2.5}>
                {[
                  { label: 'Günlük Ortalama', value: agentStatsData.dailyAverage, colorClass: agentStatsData.dailyAverage >= 85 ? '#10b981' : (agentStatsData.dailyAverage >= 70 ? '#f59e0b' : '#ef4444') },
                  { label: 'Haftalık Ortalama', value: agentStatsData.weeklyAverage, colorClass: agentStatsData.weeklyAverage >= 85 ? '#10b981' : (agentStatsData.weeklyAverage >= 70 ? '#f59e0b' : '#ef4444') },
                  { label: 'Aylık Ortalama', value: agentStatsData.monthlyAverage, colorClass: agentStatsData.monthlyAverage >= 85 ? '#10b981' : (agentStatsData.monthlyAverage >= 70 ? '#f59e0b' : '#ef4444') }
                ].map((card, index) => (
                  <Grid size={{ xs: 12, sm: 4 }} key={index}>
                    <Card sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 3.5,
                      boxShadow: 'none',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: card.colorClass
                      }
                    }}>
                      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {card.label}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: '800', color: card.colorClass, fontFamily: 'monospace' }}>
                          {card.value > 0 ? `${card.value} / 100` : '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {card.value >= 80 ? 'Başarılı Seviye' : (card.value > 0 ? 'Geliştirilmeli' : 'Veri Yok')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Summary Text Card */}
              <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: 'none', bgcolor: '#f8fafc' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconFileAnalytics size={20} color="#3b82f6" />
                    Genel Temsilci Özeti
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                    {agentStatsData.summaryText}
                  </Typography>
                </CardContent>
              </Card>

              {/* Güçlü Yönler ve Gelişime Açık Alanlar */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a8a', mb: -1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                2. Güçlü Yönler ve Gelişime Açık Alanlar
              </Typography>
              <Grid container spacing={2.5}>
                {/* Strengths */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid #d1fae5',
                    bgcolor: '#f0fdf4',
                    height: '100%'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#065f46', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconTrendingUp size={20} color="#10b981" />
                      En Güçlü Yönleri
                    </Typography>
                    {agentStatsData.strengths && agentStatsData.strengths.length > 0 ? (
                      <List sx={{ p: 0, m: 0 }}>
                        {agentStatsData.strengths.map((str: string, i: number) => (
                          <ListItem key={i} sx={{ p: 0, pb: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ minWidth: 20, color: '#10b981', display: 'inline-flex', mt: 0.25 }}>✓</Box>
                            <ListItemText
                              primary={str}
                              primaryTypographyProps={{ variant: 'body2', sx: { color: '#0f766e', fontWeight: '500' } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Öne çıkan belirgin bir güçlü yön henüz analiz edilmedi.
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Weaknesses */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid #fef3c7',
                    bgcolor: '#fffbeb',
                    height: '100%'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#92400e', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconAlertCircle size={20} color="#f59e0b" />
                      Gelişime Açık Yönleri
                    </Typography>
                    {agentStatsData.weaknesses && agentStatsData.weaknesses.length > 0 ? (
                      <List sx={{ p: 0, m: 0 }}>
                        {agentStatsData.weaknesses.map((weak: string, i: number) => (
                          <ListItem key={i} sx={{ p: 0, pb: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ minWidth: 20, color: '#f59e0b', display: 'inline-flex', mt: 0.25 }}>•</Box>
                            <ListItemText
                              primary={weak}
                              primaryTypographyProps={{ variant: 'body2', sx: { color: '#78350f', fontWeight: '500' } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Öne çıkan gelişim alanı henüz analiz edilmedi.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Duygu Analizi Dağılımı */}
              <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: 'none' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconTrendingUp size={20} color="#10b981" />
                    Çağrı Duygu Analiz Dağılımı
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #d1fae5', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#065f46', fontWeight: 600, display: 'block', mb: 0.5 }}>Pozitif Görüşmeler</Typography>
                        <Typography variant="h5" sx={{ color: '#10b981', fontWeight: 800 }}>{agentStatsData.sentiment?.positive || 0} Adet</Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, display: 'block', mb: 0.5 }}>Nötr Görüşmeler</Typography>
                        <Typography variant="h5" sx={{ color: '#64748b', fontWeight: 800 }}>{agentStatsData.sentiment?.neutral || 0} Adet</Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ p: 2, bgcolor: '#fdf2f2', borderRadius: 2, border: '1px solid #fde2e2', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#9b1c1c', fontWeight: 600, display: 'block', mb: 0.5 }}>Negatif Görüşmeler</Typography>
                        <Typography variant="h5" sx={{ color: '#ef4444', fontWeight: 800 }}>{agentStatsData.sentiment?.negative || 0} Adet</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Kritik Hata İstatistikleri */}
              <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: 'none' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#9b1c1c', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconAlertCircle size={20} color="#ef4444" />
                    Kritik Hatalar ve Cezalar
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Kritik Kural İhlali</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }} align="center">Hatalı Çağrı Sayısı</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Yaptırım / Ceza</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 'medium' }}>Güvenlik Teyidi Yapılmaması</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${agentStatsData.criticalErrors?.securityMissCount || 0} Görüşme`} 
                              size="small" 
                              color={agentStatsData.criticalErrors?.securityMissCount > 0 ? 'error' : 'default'} 
                              variant={agentStatsData.criticalErrors?.securityMissCount > 0 ? 'filled' : 'outlined'} 
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#ef4444', fontWeight: 'bold' }}>Çağrı Skoru 0 Olarak Güncellenir</TableCell>
                        </TableRow>
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 'medium' }}>Kayıt Altında Bilgisi Verilmemesi</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${agentStatsData.criticalErrors?.recordingWarningMissCount || 0} Görüşme`} 
                              size="small" 
                              color={agentStatsData.criticalErrors?.recordingWarningMissCount > 0 ? 'error' : 'default'} 
                              variant={agentStatsData.criticalErrors?.recordingWarningMissCount > 0 ? 'filled' : 'outlined'} 
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#ef4444', fontWeight: 'bold' }}>Çağrı Skoru 0 Olarak Güncellenir</TableCell>
                        </TableRow>
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 'medium' }}>Doğru Müzakere İhlali (Onaysız İndirim)</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${agentStatsData.criticalErrors?.negotiationMissCount || 0} Görüşme`} 
                              size="small" 
                              color={agentStatsData.criticalErrors?.negotiationMissCount > 0 ? 'error' : 'default'} 
                              variant={agentStatsData.criticalErrors?.negotiationMissCount > 0 ? 'filled' : 'outlined'} 
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#ef4444' }}>Görüşme Skoru -20 Ceza Puanı Alır</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Kriter Bazlı Başarı Analizi */}
              <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: 'none' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconFileAnalytics size={20} color="#2563eb" />
                    Kriter Bazlı Başarı Analizi
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e2e8f0', maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569', bgcolor: '#f8fafc' }} align="center">#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569', bgcolor: '#f8fafc' }}>Kalite Kriter Adı</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569', bgcolor: '#f8fafc' }}>Ortalama Başarı</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569', bgcolor: '#f8fafc' }} align="center">Maks Puan</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569', bgcolor: '#f8fafc' }}>Durum</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agentStatsData.criteriaBreakdown?.map((crit: any, index: number) => {
                          const percentage = crit.max > 0 ? Math.round((crit.avgEarned / crit.max) * 100) : 0;
                          let statusLabel = 'Zayıf';
                          let statusColor: 'error' | 'warning' | 'success' = 'error';
                          if (percentage >= 85) {
                            statusLabel = 'Mükemmel';
                            statusColor = 'success';
                          } else if (percentage >= 70) {
                            statusLabel = 'Yeterli';
                            statusColor = 'success';
                          } else if (percentage >= 50) {
                            statusLabel = 'Geliştirilmeli';
                            statusColor = 'warning';
                          }
                          return (
                            <TableRow key={crit.id} hover>
                              <TableCell align="center">{index + 1}</TableCell>
                              <TableCell sx={{ fontWeight: 'semibold' }}>{crit.name}</TableCell>
                              <TableCell>{`%${percentage} (${crit.avgEarned.toFixed(1)} Puan)`}</TableCell>
                              <TableCell align="center">{crit.max}</TableCell>
                              <TableCell>
                                <Chip label={statusLabel} color={statusColor} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Koçluk ve Gelişim Eylem Planı */}
              <Card sx={{ border: '1px solid #cbd5e1', borderRadius: 3, boxShadow: 'none', bgcolor: '#f8fafc' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a8a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconTarget size={20} color="#2563eb" />
                    4. Koçluk ve Gelişim Eylem Planı
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                    Aşağıdaki aksiyon planı, temsilcinin rapor dönemindeki zayıf kaldığı kriterler analiz edilerek otomatik olarak üretilmiştir. Temsilcinin kalite hedefine (80+) ulaşması için bu adımları uygulaması kritik önem arz etmektedir.
                  </Typography>
                  
                  <List sx={{ p: 0, m: 0 }}>
                    {(() => {
                      const recs = getCoachingActionPlan();
                      if (recs.length === 0) {
                        return (
                          <ListItem sx={{ p: 0, pb: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ minWidth: 20, color: '#10b981', mt: 0.25 }}>✓</Box>
                            <ListItemText
                              primary="Temsilci tüm kriterlerde oldukça başarılı bir grafik çizmektedir. Bu yüksek standartları koruması ve diğer ekip arkadaşlarına mentorluk yapması önerilir."
                              primaryTypographyProps={{ variant: 'body2', sx: { color: '#0f766e', fontWeight: '500' } }}
                            />
                          </ListItem>
                        );
                      }
                      return recs.map((rec: any, i: number) => (
                        <ListItem key={i} sx={{ p: 0, pb: 1.5, alignItems: 'flex-start' }}>
                          <Box sx={{ minWidth: 20, color: '#2563eb', fontWeight: 'bold', mt: 0.25 }}>•</Box>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: '500' }}>
                                <Box component="span" sx={{ color: '#d97706', fontWeight: 'bold', mr: 1 }}>
                                  [{rec.name}]
                                </Box>
                                {rec.text}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ));
                    })()}
                  </List>
                </CardContent>
              </Card>

              {/* Son Değerlendirilen Çağrılar */}
              <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: 'none' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconPhone size={20} color="#2563eb" />
                    Son Değerlendirilen Çağrılar (Son 10)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Tarih</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Müşteri No</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Süre</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Duygu</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Öne Çıkan Hata / Geri Bildirim</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }} align="center">Skor</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Durum</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agentStatsData.recentCalls?.map((call: any) => {
                          const callDate = call.createdAt ? new Date(call.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
                          const pass = call.score >= 80;
                          return (
                            <TableRow key={call._id} hover>
                              <TableCell>{callDate}</TableCell>
                              <TableCell>{call.callerNumber || '-'}</TableCell>
                              <TableCell>{`${call.callDuration || 0} sn`}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={call.sentiment === 'positive' ? 'Pozitif' : (call.sentiment === 'negative' ? 'Negatif' : 'Nötr')} 
                                  size="small"
                                  color={call.sentiment === 'positive' ? 'success' : (call.sentiment === 'negative' ? 'error' : 'default')}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {call.briefFeedback}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', color: pass ? '#10b981' : '#ef4444' }}>
                                {`${call.score} / 100`}
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={pass ? 'GEÇTİ' : 'KALDI'} 
                                  size="small"
                                  color={pass ? 'success' : 'error'}
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', justifyContent: 'flex-end' }}>
          <Button onClick={handleCloseAgentStatsDialog} variant="outlined" sx={{ color: '#1e293b', borderColor: '#cbd5e1', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' } }}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Çalışan Kartı Düzenleme Modalı */}
      <Dialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', p: 2, fontWeight: 'bold', color: '#1e3a8a' }}>
          Çalışan Kartı Düzenle
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>

          {/* Profil Resmi Yükleme */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Avatar
              key={profilePic || userData?.profilePicture || 'avatar'}
              src={profilePic || userData?.profilePicture || undefined}
              sx={{ width: 100, height: 100, border: '3px solid #1e3a8a', mb: 1 }}
            >
              <span>{userData?.firstName?.[0]}{userData?.lastName?.[0]}</span>
            </Avatar>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                <span>Profil Resmi Seç</span>
                <input
                  type="file"
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleProfilePicChange}
                />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setBadgeCardOpen(true)}
                sx={{ textTransform: 'none', color: '#1e3a8a', borderColor: '#1e3a8a' }}
              >
                Görüntüle
              </Button>
            </Box>
          </Box>

          {/* İsim Soyisim (Salt Okunur) */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Ad"
                value={userData?.firstName || ''}
                disabled
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Soyad"
                value={userData?.lastName || ''}
                disabled
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>

          {/* Kan Grubu */}
          <FormControl fullWidth>
            <InputLabel id="blood-type-label">Kan Grubu</InputLabel>
            <Select
              labelId="blood-type-label"
              value={bloodType}
              label="Kan Grubu"
              onChange={(e) => setBloodType(e.target.value)}
            >
              <MenuItem value="">Seçiniz</MenuItem>
              <MenuItem value="A Rh+">A Rh+</MenuItem>
              <MenuItem value="A Rh-">A Rh-</MenuItem>
              <MenuItem value="B Rh+">B Rh+</MenuItem>
              <MenuItem value="B Rh-">B Rh-</MenuItem>
              <MenuItem value="AB Rh+">AB Rh+</MenuItem>
              <MenuItem value="AB Rh-">AB Rh-</MenuItem>
              <MenuItem value="0 Rh+">0 Rh+</MenuItem>
              <MenuItem value="0 Rh-">0 Rh-</MenuItem>
            </Select>
          </FormControl>

          {/* Acil Durum Kişisi */}
          <TextField
            label="Acil Durum Kişisi (Ad Soyad)"
            value={emergencyName}
            onChange={(e) => setEmergencyName(e.target.value)}
            fullWidth
          />

          {/* Acil Durum İrtibat Numarası */}
          <TextField
            label="Acil Durum İrtibat Numarası"
            value={emergencyPhone}
            onChange={(e) => setEmergencyPhone(e.target.value)}
            fullWidth
          />

          {/* Adres */}
          <TextField
            label="Adres"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setProfileDialogOpen(false)} variant="outlined">
            Vazgeç
          </Button>
          <Button onClick={handleSaveProfile} variant="contained" sx={{ bgcolor: '#1e3a8a', '&:hover': { bgcolor: '#152b69' } }}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Çalışan Kartı Önizleme Modalı */}
      <Dialog
        open={badgeCardOpen}
        onClose={() => setBadgeCardOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <EmployeeBadgeCard user={{
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            role: userData?.role,
            profilePicture: profilePic || userData?.profilePicture,
            bloodType: bloodType,
            address: address,
            emergencyContactName: emergencyName,
            emergencyContactPhone: emergencyPhone
          }} />
          <Button
            onClick={() => setBadgeCardOpen(false)}
            variant="contained"
            size="small"
            sx={{ mt: 2, bgcolor: '#ffffff', color: '#1e3a8a', '&:hover': { bgcolor: '#f1f5f9' }, fontWeight: 'bold' }}
          >
            Kapat
          </Button>
        </DialogContent>
      </Dialog>

      {/* MT Performansı Modal */}
      {isPerformanceModalOpen && selectedAgent && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease-in-out',
          }}
        >
          <Fade in={isPerformanceModalOpen} timeout={300}>
            <Box
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                borderRadius: 4,
                width: "95%",
                maxWidth: "1400px",
                maxHeight: "95vh",
                overflow: "auto",
                boxShadow: "0 50px 100px -20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                animation: `${float} 6s ease-in-out infinite`,
              }}
            >
              {/* Modal Header */}
              <Box sx={{ p: 4, borderBottom: "1px solid #e5e7eb", background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)", backdropFilter: "blur(10px)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "white", textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)", letterSpacing: "0.5px" }}>
                    MT Performansı
                  </Typography>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      color: "white",
                      transition: "all 0.3s ease",
                      borderRadius: 2,
                      p: 1,
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <IconX size={20} />
                  </IconButton>
                </Box>


              </Box>

              {/* Modal Content */}
              <Box sx={{ p: 4 }}>
                {/* Agent Info */}
                <Box sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 3,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  backdropFilter: "blur(10px)"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
                    <Avatar
                      src={selectedAgent?.profilePicture || selectedAgent?.agent?.profilePicture || undefined}
                      sx={{
                        width: 80,
                        height: 80,
                        background: `linear-gradient(135deg, ${getStatusColor(selectedAgent?.status || 'Not Answering')} 0%, ${getStatusColor(selectedAgent?.status || 'Not Answering')}dd 100%)`,
                        boxShadow: `0 8px 25px ${getStatusColor(selectedAgent?.status || 'Not Answering')}50`,
                        border: "3px solid white",
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          inset: "-3px",
                          borderRadius: "50%",
                          background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8), ${getStatusColor(selectedAgent?.status || 'Not Answering')}80)`,
                          zIndex: -1,
                        }
                      }}>
                      {!(selectedAgent?.profilePicture || selectedAgent?.agent?.profilePicture) && (
                        <Typography variant="h3" sx={{ fontWeight: "bold", color: "white" }}>
                          {selectedAgent?.avatar || "AG"}
                        </Typography>
                      )}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)", letterSpacing: "0.3px" }}>
                        {selectedAgent?.name}
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#6b7280", fontWeight: 500 }}>
                        {selectedAgent?.email || selectedAgent?.agent?.email}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2, alignItems: "center" }}>
                        <Chip
                          label={selectedAgent?.status || 'Not Answering'}
                          sx={{
                            bgcolor: getStatusColor(selectedAgent?.status || 'Not Answering'),
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            height: 28,
                            boxShadow: `0 2px 8px ${getStatusColor(selectedAgent?.status || 'Not Answering')}50`,
                            "& .MuiChip-label": {
                              px: 1.5
                            }
                          }}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{
                            width: 8,
                            height: 8,
                            bgcolor: getStatusColor(selectedAgent?.status || 'Not Answering'),
                            borderRadius: "50%",
                            boxShadow: `0 0 10px ${getStatusColor(selectedAgent?.status || 'Not Answering')}50`
                          }} />
                          <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500 }}>
                            Statü Süresi: <CallDurationTimer startTime={selectedAgent?.statusStartTime} />
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{
                            width: 8,
                            height: 8,
                            bgcolor: "#3b82f6",
                            borderRadius: "50%",
                            boxShadow: "0 0 10px rgba(59, 130, 246, 0.5)"
                          }} />
                          <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500 }}>
                            Statü Değişikliği: {selectedAgent?.statusChangeTime || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Günlük Kalite', value: agentQualityData.dailyAverage },
                      { label: 'Haftalık Kalite', value: agentQualityData.weeklyAverage },
                      { label: 'Aylık Kalite', value: agentQualityData.monthlyAverage }
                    ].map((item) => {
                      const score = Number(item.value || 0);
                      const color = score >= 85 ? '#10b981' : (score >= 70 ? '#f59e0b' : '#ef4444');

                      return (
                        <Grid size={{ xs: 12, sm: 4 }} key={item.label}>
                          <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)'
                          }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                              {item.label}
                            </Typography>
                            <Typography variant="h5" sx={{ color, fontWeight: 900, mt: 0.5 }}>
                              {score > 0 ? `${score} / 100` : '-'}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                        Analiz edilen çağrı: {agentQualityData.totalAnalyzed || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {loadingAgentData ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                  </Box>
                ) : agentPerformanceData ? (
                  <>
                    {/* Performance Stats Grid */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      {/* Toplam Çağrı */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconPhone size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.totalCalls}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Toplam Çağrı Adedi
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Gelen Çağrı */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconPhone size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.incomingCalls}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Gelen Çağrı Adedi
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Giden Çağrı */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconPhone size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.outgoingCalls}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Giden Çağrı Adedi
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Toplam Konuşma Süresi */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconClock size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.totalCallDuration}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Toplam Konuşma Süresi
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Gelen Çağrı Süresi */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconClock size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.incomingCallDuration}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Gelen Çağrı Süresi
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Giden Çağrı Süresi */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconClock size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.outgoingCallDuration}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Giden Çağrı Süresi
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Ortalama Toplam Çağrı */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconTarget size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.avgTotalCallDuration}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Toplam Çağrı Ortalaması
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Ortalama Gelen Çağrı */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconTarget size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.avgIncomingCallDuration}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Gelen Çağrı Ortalaması
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Ortalama Giden Çağrı */}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card sx={{
                          height: '100%',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }
                        }}>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconTarget size={32} style={{ marginBottom: 12, color: '#64748b' }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                              {agentPerformanceData.avgOutgoingCallDuration}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Giden Çağrı Ortalaması
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Statü Süreleri (Bugün) — AgentPanel ile aynı mantık, tablo olarak */}
                    <Card sx={{ borderRadius: 3, mt: 3 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
                          Statü Süreleri (Bugün)
                        </Typography>
                        <TableContainer sx={{ maxHeight: 340 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Statü</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Toplam Süre</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Değişiklik</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Oran</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Aktif mi</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(!agentStatusDurations || agentStatusDurations.length === 0) ? (
                                <TableRow>
                                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Bugün için statü süresi verisi bulunamadı
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                agentStatusDurations.map((s: any, idx: number) => {
                                  const name = s.statusName || s.name || '-';
                                  const durationSec = typeof s.durationSeconds === 'number' ? s.durationSeconds : (typeof s.totalDuration === 'number' ? s.totalDuration : 0);
                                  const formatted = s.formattedDuration || (() => {
                                    const hours = Math.floor(durationSec / 3600);
                                    const minutes = Math.floor((durationSec % 3600) / 60);
                                    const secs = durationSec % 60;
                                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                                  })();
                                  const count = typeof s.count === 'number' ? s.count : (typeof s.changeCount === 'number' ? s.changeCount : 0);
                                  const ratio = typeof s.ratio === 'number' ? s.ratio : (typeof s.percentage === 'number' ? s.percentage : null);
                                  const active = !!s.isCurrent;
                                  return (
                                    <TableRow key={idx}>
                                      <TableCell sx={{ fontWeight: 500 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(name) }} />
                                          {name}
                                        </Box>
                                      </TableCell>
                                      <TableCell>{formatted}</TableCell>
                                      <TableCell>{count}</TableCell>
                                      <TableCell>{ratio !== null ? `${Math.round(ratio)}%` : '-'}</TableCell>
                                      <TableCell>
                                        {active ? (
                                          <Chip label="Aktif" size="small" sx={{ bgcolor: '#10b981', color: 'white', fontWeight: 600 }} />
                                        ) : (
                                          <Chip label="Pasif" size="small" sx={{ bgcolor: '#6b7280', color: 'white' }} />
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>

                    {/* Call History */}
                    <Card sx={{ borderRadius: 3, mt: 3 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
                          Arama Geçmişi
                        </Typography>
                        <TableContainer sx={{ maxHeight: 400 }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Tarih</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Müşteri</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Telefon</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Yön</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Süre</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Durum</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Sonuç Kodu</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {agentCallHistory.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Henüz arama geçmişi bulunmuyor
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                agentCallHistory.map((call: any, index: number) => {
                                  // Telefon ve müşteri adı alanlarını çağrı yönüne göre doğru eşle
                                  const phone = call.direction === 'incoming'
                                    ? (call.callerNumber || call.calledNumber)
                                    : (call.calledNumber || call.callerNumber);

                                  const customerName = call.callerName
                                    || (call.direction === 'incoming' ? call.callerNumber : call.calledNumber)
                                    || '-';

                                  // Süre formatlama (ss → HH:MM:SS)
                                  const formatSeconds = (seconds: any) => {
                                    const s = Number(seconds);
                                    if (!isFinite(s) || isNaN(s)) return '00:00:00';
                                    const hours = Math.floor(s / 3600);
                                    const minutes = Math.floor((s % 3600) / 60);
                                    const secs = Math.floor(s % 60);
                                    return `${hours.toString().padStart(2, '0')}:${minutes
                                      .toString()
                                      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                                  };

                                  const statusLabel = (() => {
                                    switch (call.status) {
                                      case 'ended':
                                      case 'completed':
                                        return 'Tamamlandı';
                                      case 'failed':
                                        return 'Başarısız';
                                      case 'ringing':
                                        return 'Çalıyor';
                                      case 'answered':
                                        return 'Yanıtlandı';
                                      case 'waiting':
                                        return 'Bekliyor';
                                      default:
                                        return call.status || '-';
                                    }
                                  })();

                                  const statusColor: any = (call.status === 'ended' || call.status === 'completed')
                                    ? 'success'
                                    : (call.status === 'failed' ? 'error' : 'default');

                                  return (
                                    <TableRow key={index} hover>
                                      <TableCell>
                                        {new Date(call.startTime || call.createdAt).toLocaleString('tr-TR')}
                                      </TableCell>
                                      <TableCell>{customerName}</TableCell>
                                      <TableCell>{phone || '-'}</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={call.direction === 'incoming' ? 'Gelen' : 'Giden'}
                                          size="small"
                                          color={call.direction === 'incoming' ? 'primary' : 'secondary'}
                                        />
                                      </TableCell>
                                      <TableCell>{formatSeconds(call.duration)}</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={statusLabel}
                                          size="small"
                                          color={statusColor}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {call.finishCode ? (
                                          <Chip
                                            label={typeof call.finishCode === 'object' ? call.finishCode.name : call.finishCode}
                                            size="small"
                                            sx={{
                                              bgcolor: typeof call.finishCode === 'object' && call.finishCode.color ? call.finishCode.color : '#6b7280',
                                              color: 'white',
                                              fontSize: '0.7rem'
                                            }}
                                          />
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body1" color="text.secondary">
                      Performans verileri yüklenemedi
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Fade>
        </Box>
      )}

      {/* Manuel Ses Dosyası Yükleme Modalı */}
      <Dialog
        open={manualUploadDialogOpen}
        onClose={() => !manualUploadLoading && setManualUploadDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', p: 2, fontWeight: 'bold', color: '#1e3a8a' }}>
          Yapay Zeka Analizi İçin Ses Dosyası Yükle
        </DialogTitle>
        <form onSubmit={handleManualUpload}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                Ses Dosyası (MP3, WAV, M4A, OGG) *
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<IconUpload size={18} />}
                sx={{
                  py: 1.5,
                  borderStyle: 'dashed',
                  borderColor: manualUploadFile ? '#10b981' : '#cbd5e1',
                  color: manualUploadFile ? '#10b981' : '#64748b',
                  '&:hover': {
                    borderColor: manualUploadFile ? '#059669' : '#94a3b8',
                    bgcolor: 'rgba(248, 250, 252, 0.5)'
                  }
                }}
              >
                {manualUploadFile ? (manualUploadFile as any).name : 'Dosya Seç veya Sürükle'}
                <input
                  type="file"
                  accept="audio/*"
                  hidden
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setManualUploadFile(e.target.files[0]);
                    }
                  }}
                  required
                />
              </Button>
            </Box>

            <FormControl fullWidth size="small">
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                Müşteri Temsilcisi (Ajan)
              </Typography>
              <Select
                value={manualUploadAgentId}
                onChange={(e) => setManualUploadAgentId(e.target.value)}
                displayEmpty
                renderValue={(value) => {
                  if (!value) return <span style={{ color: '#94a3b8' }}>Seçilmedi (Bilinmeyen)</span>;
                  const agent = agentsData.find(a => a.id === value);
                  return agent ? agent.name : value;
                }}
              >
                <MenuItem value="">
                  <em>Seçilmedi (Bilinmeyen)</em>
                </MenuItem>
                {agentsData.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                Müşteri Telefon Numarası
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Örn: 905XXXXXXXXX"
                value={manualUploadCustomerPhone}
                onChange={(e) => setManualUploadCustomerPhone(e.target.value)}
              />
            </Box>

            <FormControl fullWidth size="small">
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                Çağrı Yönü
              </Typography>
              <Select
                value={manualUploadDirection}
                onChange={(e) => setManualUploadDirection(e.target.value as 'inbound' | 'outbound')}
              >
                <MenuItem value="inbound">Gelen Çağrı (Inbound)</MenuItem>
                <MenuItem value="outbound">Giden Çağrı (Outbound)</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
            <Button
              onClick={() => setManualUploadDialogOpen(false)}
              disabled={manualUploadLoading}
              sx={{ color: '#64748b' }}
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={manualUploadLoading}
              startIcon={manualUploadLoading ? <CircularProgress size={16} color="inherit" /> : <IconUpload size={18} />}
              sx={{ bgcolor: '#1e3a8a', '&:hover': { bgcolor: '#152b69' } }}
            >
              {manualUploadLoading ? 'Yükleniyor...' : 'Yükle & Analiz Et'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for messages (Rendered at root to overlay all content layers) */}
      <Snackbar
        open={snackbar.open}
        onClose={closeSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%', boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  </Box>
  );
};

export default SupervisorDashboard;
